import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type {
  ClaimReadinessControlSummary,
  ProofReadinessAction,
  ProofReadinessModel,
  ProofReadinessSummary,
  ProofReadinessVerdict,
  SafetyCaseRecord,
  WorkflowCorrectiveAction,
  WorkflowCorrectiveActionEvidenceSummary,
  WorkflowClaimReadinessResponse,
  WorkflowImpactSummary,
} from '../services/reportsApi';
import { reportsApi } from '../services/reportsApi';
import { useAuth } from '../contexts/AuthContext';
import { getWarehouseCapabilityProfile, hasCapability } from '../utils/warehouseNavigation';
import { buildSafetyReportArtifactPath, resolveArtifactFlowPreset } from '../utils/reportFlow';
import { buildLiveSafetyCasePath } from '../utils/acceptanceHierarchyNavigation';
import MeasurementAssurancePanel from '../components/safetyReports/MeasurementAssurancePanel';
import {
  buildClaimReadinessBlockerReviewPath,
  claimReadinessBlockerActionLabel,
  resolveClaimReadinessBlockerReviewFocus,
  resolveClaimReadinessSafetyCaseFocus,
} from '../domain/incidents/riskPatternWorkflowReviewContext';
import ReportVisuals from '../components/reports/ReportVisuals';
import { PlatformWorkflowStrip } from '../components/platform/PlatformWorkflowStrip';
import {
  dominantPatternLabel,
  evidenceConfidenceLabel,
  evidenceConfidenceTier,
  hasUsableSafetyPattern,
} from '../components/proof/proofSafetyNarrative';
import { humanizeAreaLabel, humanizeZoneLabel } from '../components/proof/proofDisplayUtils';
import {
  normalizeScopeWindowValue,
  resolveReportWindowIso,
  toIsoFromLocalInput,
  toLocalInputValue,
  useIncidentsTimeRange,
} from './incidents/useIncidentsTimeRange';
import { useIncidentsFilters, type OperatorPreset } from './incidents/useIncidentsFilters';
import {
  describeTrustPackStartError,
  hasBuiltProofPackPreview,
  useIncidentsReports,
} from './incidents/useIncidentsReports';
import { useIncidentsReferenceData } from './incidents/useIncidentsReferenceData';
import { incidentTypeLabel } from './incidents/incidentTypeLabels';
import { useIncidentsUiMode } from './incidents/useIncidentsUiMode';
import { canAccessWarehouseSurface, canManageSafetyCaseArtifacts, canUseAdvancedIncidentsUi } from '../auth/access';
import { useSiteLocationLookups } from '../hooks/useSiteLocationLookups';
import { resolveSafetyLocationDisplay } from '../utils/locationDisplay';
import {
  buildOperationalResolutionProofCase,
  buildWebSummitDemoProofCase,
  isWebSummitDemoProofCaseId,
} from './demo/webSummitDemoProofCase';
import {
  emitSafetyPatternActionCtaEvent,
  routeProofAction,
  type ProofActionRoutingCapabilities,
  type ProofActionRoutingContext,
  type ProofActionType,
  type RoutedActionResult,
  type RoutedCta,
} from '../components/proof/proofActionRouting';
import {
  buildOperationalFollowThrough,
  type OperationalFollowThroughModel,
} from '../components/proof/operationalFollowThrough';
import ProofSummarySurface from './acceptanceProof/ProofSummarySurface';
import { useAcceptanceProofRollups } from './acceptanceProof/useAcceptanceProofRollups';
import ProofScopePanel from './acceptanceProof/ProofScopePanel';
import {
  resolveHazardClassOption,
  resolveCustomerReportSubtitle,
  resolveReportBuildingBannerText,
  resolveReportBuildingNotice,
  resolveReportDownloadButtonLabel,
  resolveReportProductName,
} from './acceptanceProof/hazardClassReportOptions';
import { parseSafetyReportTypeFromSearchParams } from './incidents/safetyReportEvidencePackParams';
import {
  buildRepresentativeCaseFromParts,
  pickDefaultRepresentativeCaseId,
  type RepresentativeSafetyCaseOption,
} from './acceptanceProof/representativeSafetyCaseSelection';
import ProofTechnicalAppendix from './acceptanceProof/ProofTechnicalAppendix';
import LinkedSafetyCasesPanel from './acceptanceProof/LinkedSafetyCasesPanel';
import SafetyScanLinkedCaseContextBlock from './acceptanceProof/SafetyScanLinkedCaseContextBlock';
import UpdatedSafetyScanScopeConfirmationPanel from './acceptanceProof/UpdatedSafetyScanScopeConfirmationPanel';
import SafetyCaseLinkDisambiguationDialog from './acceptanceProof/SafetyCaseLinkDisambiguationDialog';
import SafetyCaseAttestationPanel, {
  type SafetyCaseAttestationPanelHandle,
} from '../components/safetyCases/SafetyCaseAttestationPanel';
import {
  buildRepeatSafetyScanPath,
  deriveInheritedWorkspaceReadiness,
  loadLinkedSafetyCasesForHandoff,
  parseSafetyScanHandoffFromSearchParams,
} from '../domain/safetyCases/safetyScanWorkspaceHandoff';
import { usesCompactBuyerFace } from '../domain/reportContext/compactBuyerFace';
import {
  buildWorkflowCorrectiveActionTrackingLabel,
  syncOpenValidationTasksFromLiveWorkflow,
} from '../domain/incidents/groupedValidationTaskSync';
import { incidentsApi } from '../services/incidentsApi';
import { buildReportLanguagePreview } from '../domain/reportLanguage/reportLanguageGuardrails';
import {
  hasWorkplaceConditionContent,
  readWorkplaceCondition,
  type WorkplaceConditionFields,
} from '../domain/workplaceCondition/workplaceCondition';
import { resolveSiteFlowConditionTypeLabel } from '../domain/condition_families/siteFlowConditionFamily';
import { buildSafetyCaseReviewNewActivityPath, formatSafetyCaseActionOwnerLabel, hasCanonicalFollowupOwner } from '../domain/safetyCases/safetyCaseWorklist';
import { buildScanVerifyPath, parsePendingCountFromBlockers } from '../domain/acceptance/scanVerifyNavigation';
import { parseOptionalCount } from '../domain/acceptance/optionalCount';
import {
  DRAFT_SAFETY_SCAN_LABEL,
  SIGNED_SAFETY_SCAN_LABEL,
  buildSafetyScanHeader,
} from '../domain/acceptance/safetyScanPresentationCopy';
import { hasCanonicalPersistedWindow, parseWorkflowScope, resolveScanCameraId, resolveWorkspaceSafetyScanReportContext } from '../domain/acceptance/safetyScanReportContext';
import {
  buildSafetyScanProductPresentation,
  buildSafetyScanWindowLabel,
  customerScanZoneLabel,
  detectionCheckWrapperSubtitle,
  SIGN_SAFETY_SCAN_COPY,
} from '../domain/acceptance/safetyScanProductPresentation';
import { resolveCanonicalProofWindow } from './acceptanceProof/canonicalProofWindow';
import { resolvePostFinalizeNavigation, resolvePostFinalizeRevision } from '../domain/acceptance/postFinalizeNavigation';
import { buildSafetyScanWorkflowViewModel } from '../domain/acceptance/safetyScanWorkflowViewModel';
import { presentMeasurementReferences } from '../domain/safetyCases/measurementReferencesPresenter';
import SafetyScanWorkflowPage from './acceptanceProof/safetyScan/SafetyScanWorkflowPage';
import { resolveSafetyScanMeasurementContext } from './acceptanceProof/safetyScan/resolveSafetyScanMeasurementContext';
import { useSiteTimeZone } from '../hooks/useSiteTimeZone';
import { formatSiteDateTime, formatSiteDateTimeLocalInput } from '../domain/siteTime/siteTime';
import SafetyScanProductStory from './acceptanceProof/safetyScan/SafetyScanProductStory';
import { formatUpdatedScanScopeLabels, planCanonicalProofScopeApply, resolveCanonicalCameraDraft, resolveCanonicalZoneDraft } from './acceptanceProof/canonicalProofScopeApply';
import { useObservationFunnelQueue, useOperatorLifecycleSnapshot, useSafetyScanProductStoryModel } from './acceptanceProof/useSafetyScanProductStory';
import { buildEffectivenessClaimLabelFromBoundary, isEffectivenessClaimAllowedFromBoundary } from '../domain/claimReadinessPresentation';
import { useSupportingPacks } from './acceptanceProof/useSupportingPacks';
import { useAcceptanceProofViewModel } from './acceptanceProof/useAcceptanceProofViewModel';
import {
  buildReportScopeHierarchySummary,
  buildPolicyTypeOptionsForCamera,
  buildHazardClassReportScopeDisplayLines,
  buildReportScopeDisplayLines,
  buildZoneOptionsForCamera,
  mergeCameraOptionsForScope,
  normalizeProofScopePolicyType,
  REPORT_SCOPE_RESOLUTION_HELPER,
  reportScopeLinesAreComplete,
  reportScopeValueNeedsSelection,
  resolveReportScopeAreaLabel,
  resolveCameraFilterSelectValue,
  resolveProofScopePolicySelectValue,
  resolveReportScopeCameraLabel,
  resolveReportScopePatternLabel,
} from './acceptanceProof/safetyReportFilterCopy';
import {
  formatSafetyReportSelectionSummary,
  normalizeSafetyReportAreaLabel,
  SAFETY_REPORT_BUILD_FAILED_HELPER,
  SAFETY_REPORT_BUILD_SCOPE_HELPER,
  SAFETY_REPORT_DOWNLOAD_FAILED_HELPER,
  SAFETY_REPORT_SIGNOFF_PENDING_HELPER,
} from './acceptanceProof/safetyReportMvpPresentation';
import {
  _deviceFilterLabel,
  _isSelectableDeviceForIncidentsFilter,
  _scopedOrgId,
  _scopedSiteId,
  _titleCase,
  actionPriorityRank,
  actionTextBlob,
  buildConfigurationHandoffHref,
  buildIncidentReviewReturnHref,
  buildOperatorBlockingVerdictSentence,
  buildOperatorVerdictFactors,
  buildVerdictReason,
  cameraAvailabilityAvailable,
  clipCaptureDisabled,
  customerFacingAreaLabel,
  customerFacingSafetyRuleLabel,
  resolveCustomerFacingPatternLabel,
  dedupeActions,
  evidenceMetricsAvailable,
  firstDefinedString,
  firstIncidentHref,
  firstIssueHref,
  firstStringFromPaths,
  formatCount,
  formatNumber,
  formatTimestamp,
  hasFiniteNumber,
  humanizeJobState,
  incidentGroupingKey,
  inferenceTrustDegraded,
  isEvidenceConfidenceAction,
  isOperatorFollowupAction,
  isSafetyInterventionAction,
  isZoneViolationIssue,
  lookupLabeledValue,
  parseInternalPath,
  pipelineDegraded,
  pluralize,
  proofPackGeneratedAt,
  proofSurfaceFromPreset,
  readEvidenceBundleReviewed,
  reliabilityMetricsAvailable,
  sanitizeVerdictFactors,
  sortEvidenceActionsForDisplay,
  sortOperatorActionsForDisplay,
  sortProofActionsForDisplay,
  sortSafetyActionsForDisplay,
  supportingVisuals,
  trimmedString,
  withPreferredActionHref,
  writeEvidenceBundleReviewed,
} from './acceptanceProof/workspaceUtils';

type ProofPageState = 'EMPTY' | 'GENERATING' | 'READY' | 'GENERATED_WITHOUT_READINESS' | 'ERROR';

type ProofJourneyPhaseProps = {
  phase: string;
  title: string;
  detail: string;
  children: React.ReactNode;
};

type ProofSurfaceCardStatus = 'Ready' | 'Needs review' | 'Waiting' | 'In progress' | 'Done';
type ProofSurfaceKey = 'corrective' | 'package' | 'reliability';

function isSafetyReportBuildComplete(state: ProofPageState): boolean {
  return state === 'READY' || state === 'GENERATED_WITHOUT_READINESS';
}

function isSafetyReportBuilding(state: ProofPageState): boolean {
  return state === 'GENERATING';
}

type ProofSurfaceCardProps = {
  title: string;
  question: string;
  status: ProofSurfaceCardStatus;
  summary?: string;
  blockingReason: string;
  primaryCtaLabel: string;
  completionCriteria: string[];
  onPrimaryCta: () => void;
  active?: boolean;
  recommended?: boolean;
};

type CaseStatusCardProps = {
  title: string;
  value: string;
  description: string;
  color?: 'success' | 'warning' | 'error' | 'info' | 'default';
};

type ProofJourneyStepProps = {
  title: string;
  statusTone: ProofSurfaceCardStatus;
  statusLabel: string;
  detail: string;
};

type ProofOutputCardProps = {
  title: string;
  status: string;
  detail: string;
  primaryActionLabel: string;
  onPrimaryAction: () => void;
  primaryActionDisabled?: boolean;
  primaryActionVariant?: 'contained' | 'outlined';
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
};

type NextDecisionCta = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
};

const EVIDENCE_BUNDLE_REVIEWED_PREFIX = 'acceptance.proof.evidenceReviewed.v1';

const _ProofJourneyPhase: React.FC<ProofJourneyPhaseProps> = ({ phase, title, detail, children }) => (
  <Box
    component="section"
    sx={{
      borderLeft: 3,
      borderColor: 'primary.light',
      pl: { xs: 1.25, sm: 1.5 },
    }}
  >
    <Typography
      variant="overline"
      color="primary"
      sx={{ fontWeight: 800, letterSpacing: '0.08em', display: 'block', mb: 0.35 }}
    >
      {phase}
    </Typography>
    <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25, letterSpacing: -0.02 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.25, lineHeight: 1.5 }}>
      {detail}
    </Typography>
    <Stack spacing={1.5}>{children}</Stack>
  </Box>
);

function proofSurfaceStatusColor(status: ProofSurfaceCardStatus): 'success' | 'warning' | 'error' | 'info' | 'default' {
  if (status === 'Done' || status === 'Ready') return 'success';
  if (status === 'Needs review') return 'warning';
  if (status === 'Waiting') return 'default';
  if (status === 'In progress') return 'info';
  return 'default';
}

const _ProofSurfaceCard: React.FC<ProofSurfaceCardProps> = ({
  title,
  question,
  status,
  summary,
  blockingReason,
  primaryCtaLabel,
  completionCriteria,
  onPrimaryCta,
  active = false,
  recommended = false,
}) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.5,
      borderRadius: 2,
      height: '100%',
      borderColor: active ? 'primary.main' : 'divider',
      boxShadow: active ? '0 0 0 1px rgba(25, 118, 210, 0.18)' : 'none',
      bgcolor: active ? 'rgba(25, 118, 210, 0.08)' : 'background.paper',
    }}
  >
    <Stack spacing={1.25} sx={{ height: '100%' }}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
        <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: -0.02 }}>
          {title}
        </Typography>
        <Chip size="small" label={status} color={proofSurfaceStatusColor(status)} variant="outlined" />
        {active ? <Chip size="small" label="Current lane" color="primary" variant="filled" /> : null}
        {recommended && !active ? (
          <Chip
            size="small"
            label="Recommended next step"
            variant="outlined"
            sx={{
              height: 20,
              fontSize: '0.6875rem',
              borderColor: 'divider',
              color: 'text.secondary',
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        ) : null}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.02 }}>
        {question}
      </Typography>
      {summary ? (
        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.5 }}>
          {summary}
        </Typography>
      ) : null}
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45, display: 'block' }}>
        {blockingReason}
      </Typography>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
          Completion criteria
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          {completionCriteria.map((item) => (
            <Typography key={item} component="li" variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
              {item}
            </Typography>
          ))}
        </Box>
      </Box>
      <Box sx={{ pt: 0.25 }}>
        <Button variant="outlined" onClick={onPrimaryCta} disabled={active}>
          {primaryCtaLabel}
        </Button>
      </Box>
    </Stack>
  </Paper>
);

const _ProofJourneyStepCard: React.FC<ProofJourneyStepProps> = ({ title, statusTone, statusLabel, detail }) => (
  <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, height: '100%' }}>
    <Stack spacing={0.75}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Chip size="small" label={statusLabel} color={proofSurfaceStatusColor(statusTone)} variant="outlined" />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
        {detail}
      </Typography>
    </Stack>
  </Paper>
);

const _ProofOutputCard: React.FC<ProofOutputCardProps> = ({
  title,
  status,
  detail,
  primaryActionLabel,
  onPrimaryAction,
  primaryActionDisabled = false,
  primaryActionVariant = 'contained',
  secondaryActionLabel,
  onSecondaryAction,
}) => (
  <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, height: '100%' }}>
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Chip size="small" label={status} variant="outlined" />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
        {detail}
      </Typography>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        <Button variant={primaryActionVariant} onClick={onPrimaryAction} disabled={primaryActionDisabled}>
          {primaryActionLabel}
        </Button>
        {secondaryActionLabel && onSecondaryAction ? (
          <Button variant="outlined" onClick={onSecondaryAction}>
            {secondaryActionLabel}
          </Button>
        ) : null}
      </Stack>
    </Stack>
  </Paper>
);

const _CaseStatusCard: React.FC<CaseStatusCardProps> = ({ title, value, description, color = 'default' }) => (
  <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, height: '100%' }}>
    <Stack spacing={0.75}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
        {title}
      </Typography>
      <Chip size="small" label={value} color={color} variant="outlined" sx={{ alignSelf: 'flex-start' }} />
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
        {description}
      </Typography>
    </Stack>
  </Paper>
);

type _ReportDetailFact = {
  label: string;
  value: string;
};

const ReportDetailFactsList: React.FC<{ facts: _ReportDetailFact[] }> = ({ facts }) => {
  if (facts.length === 0) return null;
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
      {facts.map((fact) => (
        <Typography key={fact.label} component="li" variant="body2" sx={{ lineHeight: 1.5, mb: 0.35 }}>
          <Box component="span" sx={{ fontWeight: 700 }}>{fact.label}: </Box>
          {fact.value}
        </Typography>
      ))}
    </Box>
  );
};

const ReportDetailSection: React.FC<{
  title: string;
  summary?: string;
  facts?: _ReportDetailFact[];
  bullets?: string[];
  children?: React.ReactNode;
  sectionRef?: React.MutableRefObject<HTMLDivElement | null>;
  highlighted?: boolean;
}> = ({ title, summary, facts = [], bullets = [], children, sectionRef, highlighted = false }) => (
  <Paper
    ref={sectionRef ? (node: HTMLDivElement | null) => { sectionRef.current = node; } : undefined}
    tabIndex={sectionRef ? -1 : undefined}
    variant="outlined"
    sx={{
      p: 1.5,
      borderRadius: 2.5,
      height: '100%',
      outline: highlighted ? '2px solid' : 'none',
      outlineColor: highlighted ? 'primary.main' : undefined,
      boxShadow: highlighted ? 8 : undefined,
      transition: 'outline-color 0.2s ease, box-shadow 0.2s ease',
    }}
  >
    <Stack spacing={1}>
      <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: -0.01 }}>
        {title}
      </Typography>
      {summary ? (
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {summary}
        </Typography>
      ) : null}
      <ReportDetailFactsList facts={facts} />
      {bullets.length > 0 ? (
        <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
          {bullets.map((item) => (
            <Typography key={item} component="li" variant="body2" sx={{ lineHeight: 1.5, mb: 0.35 }}>
              {item}
            </Typography>
          ))}
        </Box>
      ) : null}
      {children}
    </Stack>
  </Paper>
);

const DetailStateCard: React.FC<{
  title: string;
  value: string;
  helper: string;
  tone?: 'success' | 'warning' | 'error' | 'info' | 'default';
}> = ({ title, value, helper, tone = 'default' }) => (
  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, height: '100%' }}>
    <Stack spacing={0.9}>
      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 800, letterSpacing: '0.08em' }}>
        {title}
      </Typography>
      <Chip
        size="small"
        label={value}
        color={tone === 'default' ? undefined : tone}
        variant={tone === 'default' ? 'outlined' : 'filled'}
        sx={{ alignSelf: 'flex-start' }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        {helper}
      </Typography>
    </Stack>
  </Paper>
);

type _ReadinessBlocker = {
  id: string;
  code: string;
  workflowStepLabel: string;
  label: string;
  nextActionText: string;
  ctaLabel: string;
  disabled?: boolean;
  onClick: () => void;
  secondaryCtaLabel?: string;
  secondaryOnClick?: () => void;
  secondaryDisabled?: boolean;
};

type ReportLanguagePreviewSourceFields = {
  condition_summary: string;
  why_this_matters_text: string;
  likely_contributing_condition: string;
  contributing_condition_status: string;
  worker_support_note_enabled: boolean;
};

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? value as Record<string, unknown> : {};
}

function buildLanguagePreviewSourceFields(
  workplaceCondition: WorkplaceConditionFields,
): ReportLanguagePreviewSourceFields {
  return {
    condition_summary: String(workplaceCondition.condition_summary || '').trim(),
    why_this_matters_text: String(workplaceCondition.why_this_matters_text || '').trim(),
    likely_contributing_condition: String(workplaceCondition.likely_contributing_condition || '').trim(),
    contributing_condition_status: String(workplaceCondition.contributing_condition_status || '').trim().toLowerCase(),
    worker_support_note_enabled: workplaceCondition.worker_support_note_enabled !== false,
  };
}

function readReportLanguagePreviewSnapshot(
  payload: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  const preview = asRecord(payload).report_language_preview;
  return preview && typeof preview === 'object' ? asRecord(preview) : null;
}

function hasConfirmedLanguagePreviewSnapshot(
  preview: Record<string, unknown> | null,
): boolean {
  if (!preview) return false;
  const confirmedAt = String(preview.confirmed_at || '').trim();
  const combinedText = String(preview.combined_text || '').trim();
  return Boolean(confirmedAt && (combinedText || Array.isArray(preview.sections)));
}

function languagePreviewMatchesWorkplaceCondition(
  preview: Record<string, unknown> | null,
  workplaceCondition: WorkplaceConditionFields,
): boolean {
  if (!preview) return false;
  const sourceFields = buildLanguagePreviewSourceFields(
    asRecord(preview.source_fields) as WorkplaceConditionFields,
  );
  const currentFields = buildLanguagePreviewSourceFields(workplaceCondition);
  return JSON.stringify(sourceFields) === JSON.stringify(currentFields);
}

const _CLAIM_READINESS_WORKFLOW_STEP_LABELS: Record<string, string> = {
  verification_method: 'Response Readiness',
  field_confirmation_of_zone_controls: 'Response Readiness',
  observation_coverage_reliability: 'Measurement Readiness',
  matched_window_followup_validation: 'Measurement Readiness',
  contributing_condition_status_required: 'Contributing Condition Review',
  closure_summary_required: 'Closure Summary',
};

function _claimReadinessControlStatusLabel(control: ClaimReadinessControlSummary | null | undefined): string {
  const label = String(control?.label || 'Claim readiness control').trim();
  const status = String(control?.status || '').trim().toLowerCase();
  if (status === 'needs_attestation') return `${label} needs attestation.`;
  if (status === 'needs_confirmation') return `${label} needs confirmation.`;
  if (status === 'needs_evidence') return `${label} needs evidence.`;
  if (status === 'blocked') return `${label} is blocking claim readiness.`;
  if (status === 'resolved') return `${label} is resolved.`;
  return `${label} needs review.`;
}

function _claimReadinessControlNextStep(control: ClaimReadinessControlSummary | null | undefined): string {
  const actionLabel = String(control?.next_action?.label || '').trim();
  if (actionLabel) {
    return `${actionLabel} in the Safety Case workflow.`;
  }
  const autoReason = String(control?.auto_resolution_reason || '').trim();
  if (autoReason) return autoReason;
  return 'Review this claim-readiness control in the Safety Case workflow.';
}

function _claimReadinessStageStatus(
  controlsById: Map<string, ClaimReadinessControlSummary>,
  controlIds: string[],
): 'Complete' | 'Pending' | 'Blocked' | 'Not started' {
  const controls = controlIds
    .map((controlId) => controlsById.get(controlId))
    .filter((control): control is ClaimReadinessControlSummary => Boolean(control));
  if (!controls.length) return 'Not started';
  if (controls.every((control) => {
    const status = String(control.status || '').trim().toLowerCase();
    return status === 'resolved' || status === 'not_applicable';
  })) {
    return 'Complete';
  }
  if (controls.some((control) => control.blocking === true || String(control.status || '').trim().toLowerCase() === 'blocked')) {
    return 'Blocked';
  }
  if (controls.some((control) => {
    const status = String(control.status || '').trim().toLowerCase();
    return status === 'needs_attestation' || status === 'needs_confirmation' || status === 'needs_evidence';
  })) {
    return 'Pending';
  }
  return 'Not started';
}

const _WORKFLOW_STEP_LABELS: Record<string, string> = {
  detected: 'Observed',
  review: 'Representative Evidence',
  assign_action: 'Response Readiness',
  track_impact: 'Measurement Readiness',
  report: 'Scan Package',
};

const ReadinessStatusPanel: React.FC<{
  title: string;
  reportTypeLine: string;
  scopeLine: string;
  summaryIntro?: string;
  facts?: _ReportDetailFact[];
  statusLabel: string;
  primaryBlocker: string;
  nextStep: string;
  primaryBlockerLabel?: string;
  nextStepLabel?: string;
  primaryActionLabel: string;
  primaryActionDisabled?: boolean;
  onPrimaryAction: () => void;
  secondaryActions?: React.ReactNode;
  chips: string[];
  hideActions?: boolean;
  essentialActions?: React.ReactNode;
}> = ({
  title,
  reportTypeLine,
  scopeLine,
  summaryIntro,
  facts = [],
  statusLabel,
  primaryBlocker,
  nextStep,
  primaryBlockerLabel,
  nextStepLabel,
  primaryActionLabel,
  primaryActionDisabled = false,
  onPrimaryAction,
  secondaryActions,
  chips,
  hideActions = false,
  essentialActions,
}) => (
  <Paper variant="outlined" data-testid="safety-report-summary-card" sx={{ p: 2, borderRadius: 2.5 }}>
    <Stack spacing={1.5}>
      <Stack spacing={0.5}>
        <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: -0.01 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
          {reportTypeLine}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
          {scopeLine}
        </Typography>
        {summaryIntro ? (
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
            {summaryIntro}
          </Typography>
        ) : null}
      </Stack>
      <ReportDetailFactsList facts={facts} />
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
        <Chip
          size="small"
          label={`Status: ${statusLabel}`}
          color={/blocked/i.test(statusLabel) ? 'error' : /attention/i.test(statusLabel) ? 'warning' : 'success'}
        />
        {chips.map((chip) => (
          <Chip key={chip} size="small" label={chip} variant="outlined" />
        ))}
      </Stack>
      <Stack spacing={0.4}>
        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.5 }}>
          {primaryBlockerLabel || (/signed off/i.test(statusLabel) ? 'Artifact context' : 'Primary blocker')}: {primaryBlocker}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {nextStepLabel || (/signed off/i.test(statusLabel) ? 'Suggested next step' : 'Primary next step')}: {nextStep}
        </Typography>
      </Stack>
      {!hideActions ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
          <Button variant="contained" onClick={onPrimaryAction} disabled={primaryActionDisabled}>
            {primaryActionLabel}
          </Button>
          {secondaryActions}
        </Stack>
      ) : essentialActions ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
          {essentialActions}
        </Stack>
      ) : null}
    </Stack>
  </Paper>
);

const OptionalCollapsedAdvanced: React.FC<{
  enabled: boolean;
  children: React.ReactNode;
}> = ({ enabled, children }) => {
  if (!enabled) return <>{children}</>;
  return (
    <Accordion
      defaultExpanded={false}
      disableGutters
      data-testid="safety-scan-workflow-advanced"
      sx={{ border: 1, borderColor: 'divider', borderRadius: 2.5, boxShadow: 'none', '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ px: 2, minHeight: 52 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          Advanced
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

const DisclosureSection: React.FC<{
  title: string;
  subtitle?: string;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}> = ({ title, subtitle, defaultExpanded = false, children }) => (
  <Accordion
    defaultExpanded={defaultExpanded}
    disableGutters
    sx={{ border: 1, borderColor: 'divider', borderRadius: 2.5, boxShadow: 'none', '&:before': { display: 'none' } }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ px: 2, minHeight: 52 }}>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: -0.01 }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
    </AccordionSummary>
    <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
      {children}
    </AccordionDetails>
  </Accordion>
);

const ReadinessBlockersPanel: React.FC<{
  blockers: _ReadinessBlocker[];
  title?: string;
  emptyStateMessage?: string;
  hideActions?: boolean;
}> = ({
  blockers,
  title = 'Why this needs attention',
  emptyStateMessage = 'No blockers remain. This draft report is ready for final human sign-off.',
  hideActions = false,
}) => {
  const nextBlocker = blockers[0] || null;
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
      <Stack spacing={1.25}>
        <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: -0.01 }}>
          {title}
        </Typography>
        {blockers.length > 0 ? (
          <>
            <Stack spacing={1}>
              {blockers.map((blocker) => (
                <Box
                  key={blocker.id}
                  sx={{ p: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}
                >
                  <Stack spacing={0.75}>
                    <Chip size="small" label={blocker.workflowStepLabel} variant="outlined" sx={{ alignSelf: 'flex-start' }} />
                    <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                      {blocker.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      {blocker.nextActionText}
                    </Typography>
                    {!hideActions ? (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                        <Button variant="outlined" onClick={blocker.onClick} disabled={Boolean(blocker.disabled)} sx={{ alignSelf: 'flex-start' }}>
                          {blocker.ctaLabel}
                        </Button>
                        {blocker.secondaryCtaLabel && blocker.secondaryOnClick ? (
                          <Button
                            variant="contained"
                            onClick={blocker.secondaryOnClick}
                            disabled={Boolean(blocker.secondaryDisabled)}
                            sx={{ alignSelf: 'flex-start' }}
                          >
                            {blocker.secondaryCtaLabel}
                          </Button>
                        ) : null}
                      </Stack>
                    ) : null}
                  </Stack>
                </Box>
              ))}
            </Stack>
            {nextBlocker ? (
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    Next best action:
                  </Box>{' '}
                  {nextBlocker.nextActionText}
                </Typography>
                {!hideActions ? (
                  <Button
                    variant="contained"
                    onClick={
                      nextBlocker.secondaryOnClick && !nextBlocker.secondaryDisabled
                        ? nextBlocker.secondaryOnClick
                        : nextBlocker.onClick
                    }
                    disabled={Boolean(
                      nextBlocker.secondaryOnClick
                        && !nextBlocker.secondaryDisabled
                        ? nextBlocker.secondaryDisabled
                        : nextBlocker.disabled,
                    )}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    {nextBlocker.secondaryOnClick && !nextBlocker.secondaryDisabled
                      ? nextBlocker.secondaryCtaLabel || nextBlocker.ctaLabel
                      : nextBlocker.ctaLabel}
                  </Button>
                ) : null}
              </Stack>
            ) : null}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {emptyStateMessage}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

type _WorkflowStepStatus = 'Complete' | 'Ready' | 'Pending' | 'Blocked' | 'Not started';

const CompactWorkflowPanel: React.FC<{
  title?: string;
  steps: Array<{ label: string; status: _WorkflowStepStatus }>;
}> = ({ title = 'Progress', steps }) => (
  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5 }} data-testid="imported-workflow-state">
    <Stack spacing={1}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: -0.01 }}>
        {title}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(5, minmax(0, 1fr))' },
          gap: 1,
        }}
      >
        {steps.map((step, index) => (
          <Paper key={step.label} variant="outlined" sx={{ p: 1.1, borderRadius: 2 }}>
            <Stack spacing={0.4}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                {index < steps.length - 1 ? `${step.label} ->` : step.label}
              </Typography>
              <Chip
                size="small"
                label={step.status}
                color={
                  step.status === 'Complete'
                    ? 'success'
                    : step.status === 'Ready'
                      ? 'info'
                      : step.status === 'Blocked'
                        ? 'error'
                        : step.status === 'Pending'
                          ? 'warning'
                          : undefined
                }
                variant={step.status === 'Not started' ? 'outlined' : 'filled'}
                sx={{ alignSelf: 'flex-start' }}
              />
            </Stack>
          </Paper>
        ))}
      </Box>
    </Stack>
  </Paper>
);

const _renderAllLabel = (kind: string, value: any, labelForValue?: (id: string) => string): React.ReactNode => {
  const v = String(value || '').trim();
  if (!v) return <em title={`All ${kind}`}>All</em>;
  try {
    const lbl = labelForValue ? String(labelForValue(v) || '').trim() : '';
    return lbl || v;
  } catch {
    return v;
  }
};

function DraftSafetyScanLinkLoadingPanel() {
  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3 }}
      data-testid="draft-safety-scan-link-loading"
    >
      <Typography variant="body2" color="text.secondary">
        Loading linked Safety Case...
      </Typography>
    </Paper>
  );
}

const _SupportingReportDetails: React.FC<{
  proofPackPreviewData: any;
  onOpenLink?: (href?: string) => void;
  canOpenReportIncidentLink?: boolean;
  canOpenReportDiagnosticsLink?: boolean;
  canOpenReportLiveViewLink?: boolean;
  canOpenReportDeviceControlLink?: boolean;
}> = ({
  proofPackPreviewData,
  onOpenLink,
  canOpenReportIncidentLink,
  canOpenReportDiagnosticsLink,
  canOpenReportLiveViewLink,
  canOpenReportDeviceControlLink,
}) => {
  if (!proofPackPreviewData?.ok) return null;
  const summary = proofPackPreviewData?.case_file?.summary || {};
  const topActionsRaw = proofPackPreviewData?.case_file?.top_actions;
  const actions = Array.isArray(topActionsRaw) ? topActionsRaw.slice(0, 8) : [];
  const timelineRaw = proofPackPreviewData?.case_file?.timeline;
  const timeline = Array.isArray(timelineRaw) ? timelineRaw.slice(-8).reverse() : [];
  const visuals = supportingVisuals(proofPackPreviewData?.report_visuals);

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Supporting case data
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Chip size="small" label={`Incidents: ${formatNumber(Number(summary?.incidents_total || 0))}`} variant="outlined" />
          <Chip size="small" label={`Open: ${Number(summary?.open_total || 0)}`} variant="outlined" />
          <Chip size="small" label={`Acknowledged: ${Number(summary?.acknowledged_total || 0)}`} variant="outlined" />
          <Chip size="small" label={`Resolved: ${Number(summary?.resolved_total || 0)}`} variant="outlined" />
          <Chip size="small" label={`High/Critical: ${Number(summary?.high_or_critical_total || 0)}`} variant="outlined" />
        </Stack>
      </Box>

      {visuals.length ? (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Raw report details
          </Typography>
          <ReportVisuals visuals={visuals} compact onOpenLink={onOpenLink} />
        </Box>
      ) : null}

      {actions.length ? (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.35 }}>
            Follow-up routes
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1.35 }}>
            Same operational shortcuts as the proof-pack dialog: open incidents, diagnostics, live view, or device control
            for each suggested follow-up.
          </Typography>
          <Stack spacing={1}>
            {actions.map((action: any, index: number) => (
              <Box key={String(action?.title || index)} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {String(action?.title || 'Follow-up')}
                </Typography>
                {action?.detail ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    {String(action.detail)}
                  </Typography>
                ) : null}
                {canOpenReportIncidentLink ||
                canOpenReportDiagnosticsLink ||
                canOpenReportLiveViewLink ||
                canOpenReportDeviceControlLink ? (
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap' }}>
                    {canOpenReportIncidentLink ? (
                      <Button size="small" variant="outlined" onClick={() => onOpenLink?.(action?.links?.incident)}>
                        Incident
                      </Button>
                    ) : null}
                    {canOpenReportDiagnosticsLink ? (
                      <Button size="small" variant="outlined" onClick={() => onOpenLink?.(action?.links?.diagnostics)}>
                        Diagnostics
                      </Button>
                    ) : null}
                    {canOpenReportLiveViewLink ? (
                      <Button size="small" variant="outlined" onClick={() => onOpenLink?.(action?.links?.live_view)}>
                        Live View
                      </Button>
                    ) : null}
                    {canOpenReportDeviceControlLink ? (
                      <Button size="small" variant="outlined" onClick={() => onOpenLink?.(action?.links?.device_control)}>
                        Device Control
                      </Button>
                    ) : null}
                  </Box>
                ) : null}
              </Box>
            ))}
          </Stack>
        </Box>
      ) : null}

      {timeline.length ? (
        <Accordion disableGutters sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, boxShadow: 'none', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ px: 1.25, minHeight: 40 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Technical audit trail
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
                Raw generation and incident-event entries for audit and troubleshooting.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={0.75}>
              {timeline.map((event: any, index: number) => (
                <Typography key={`${String(event?.incident_id || '')}-${index}`} variant="caption" color="text.secondary">
                  {String(event?.ts || '')} · {String(event?.event || 'event')}
                </Typography>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ) : null}
    </Stack>
  );
};

type ProofWorkspaceHeaderProps = {
  canReadIncidents: boolean;
  canReadPolicies: boolean;
  configurationHandoffHref: string;
  onOpenIncidents: () => void;
  onOpenConfiguration: () => void;
  summaryLine: string;
  reportTitle: string;
  reportSubtitle: string;
  secondaryCopy?: string;
  overline?: string;
  stateLabel?: string;
};

const ProofWorkspaceHeader: React.FC<ProofWorkspaceHeaderProps> = ({
  canReadIncidents: _canReadIncidents,
  canReadPolicies: _canReadPolicies,
  configurationHandoffHref: _configurationHandoffHref,
  onOpenIncidents: _onOpenIncidents,
  onOpenConfiguration: _onOpenConfiguration,
  summaryLine: _summaryLine,
  reportTitle,
  reportSubtitle,
  secondaryCopy,
  overline = 'Safety Scan',
  stateLabel,
}) => (
  <Box>
    <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em' }}>
      {overline}
    </Typography>
    {stateLabel ? (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700 }}>
        {stateLabel}
      </Typography>
    ) : null}
    <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: -0.02 }}>
      {reportTitle}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.35 }}>
      {reportSubtitle}
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.45 }}>
      {secondaryCopy || 'This Safety Scan summarizes verified evidence, the recorded Control, follow-up measurement, and the conclusion for this measurement window.'}
    </Typography>
  </Box>
);

const AcceptanceProofWorkspace: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [technicalDetailsExpanded, setTechnicalDetailsExpanded] = useState(false);
  const { effectiveUser: user } = useAuth();
  const canManageSafetyCases = canManageSafetyCaseArtifacts(user);
  const capabilityProfile = useMemo(() => getWarehouseCapabilityProfile(user), [user]);
  const canConfigureScheduledScans = useMemo(
    () => capabilityProfile.capabilities.includes('scan:configure'),
    [capabilityProfile.capabilities],
  );
  const incidentsAdvancedEligible = useMemo(
    () => canUseAdvancedIncidentsUi(capabilityProfile.capabilities),
    [capabilityProfile.capabilities],
  );
  const { incidentsUiMode: _incidentsUiMode } = useIncidentsUiMode(user?.id, incidentsAdvancedEligible);

  const _canOpenReportIncidentLink = capabilityProfile.canReadIncidents;
  const _canOpenReportDiagnosticsLink = capabilityProfile.hasHealthAccess;
  const _canOpenReportLiveViewLink = capabilityProfile.hasLiveOpsAccess;
  const canOpenReportDeviceControlLink = hasCapability(capabilityProfile.capabilities, 'devices:write');

  const _initialPreset: OperatorPreset = '';
  const {
    filterZoneId,
    filterDeviceId,
    filterCameraId,
  } = useIncidentsFilters(_initialPreset);

  const {
    timePreset: _timePreset,
    setTimePreset: _setTimePreset,
    customFrom: _customFrom,
    setCustomFrom: _setCustomFrom,
    customTo: _customTo,
    setCustomTo: _setCustomTo,
    timeRange: workspaceTimeRange,
  } = useIncidentsTimeRange('24h');

  const scopedSiteId = _scopedSiteId();
  const locationLookups = useSiteLocationLookups(String(scopedSiteId || '').trim(), Boolean(scopedSiteId));
  const scopedOrgId = _scopedOrgId();
  const hasScope = Boolean(scopedOrgId && scopedSiteId);

  const {
    allDevices: _allDevices,
    allCameras: _allCameras,
    allZones: _allZones,
    allPolicies: _allPolicies,
    zoneNameById,
    cameraNameById: _cameraNameById,
    deviceNameById,
    deviceOptions: _deviceOptions,
    cameraOptions: _cameraOptions,
    zoneOptions: _zoneOptions,
    policyOptions: _policyOptions,
    policyTypeOptions: _policyTypeOptions,
  } = useIncidentsReferenceData({
    scopedSiteId,
    isSelectableDeviceForIncidentsFilter: _isSelectableDeviceForIncidentsFilter,
    deviceFilterLabel: _deviceFilterLabel,
    titleCase: _titleCase,
  });

  const downloadBlob = useCallback((blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }, []);

  const {
    reportDialogOpen: _reportDialogOpen,
    setReportDialogOpen: _setReportDialogOpen,
    reportTask: _reportTask,
    setReportTask,
    reportKind,
    setReportKind,
    reportAdvancedArtifactMode: _reportAdvancedArtifactMode,
    setReportAdvancedArtifactMode: _setReportAdvancedArtifactMode,
    reportFormMoreOptions: _reportFormMoreOptions,
    setReportFormMoreOptions: _setReportFormMoreOptions,
    reportTaskFirstMoreOpen: _reportTaskFirstMoreOpen,
    setReportTaskFirstMoreOpen: _setReportTaskFirstMoreOpen,
    reportFrom,
    setReportFrom: _setReportFrom,
    reportTo,
    setReportTo: _setReportTo,
    reportSiteId,
    setReportSiteId: _setReportSiteId,
    reportDeviceId,
    setReportDeviceId: _setReportDeviceId,
    reportZoneId,
    setReportZoneId: _setReportZoneId,
    reportCameraIds,
    setReportCameraIds: _setReportCameraIds,
    reportPolicyType,
    setReportPolicyType: _setReportPolicyType,
    reportPolicyIds,
    setReportPolicyIds: _setReportPolicyIds,
    reportScope: _reportScope,
    setReportScope: _setReportScope,
    reportScopeId: _reportScopeId,
    setReportScopeId: _setReportScopeId,
    reportReliabilityFormat: _reportReliabilityFormat,
    setReportReliabilityFormat: _setReportReliabilityFormat,
    reportLoading,
    reportError,
    setReportError: _setReportError,
    reportNotice: _reportNotice,
    setReportNotice: _setReportNotice,
    proofPackJobId,
    proofPackJobState,
    proofPackJobLongRunning: _proofPackJobLongRunning,
    reportData,
    reliabilityPreviewData: _reliabilityPreviewData,
    proofPackPreviewData,
    craPreviewData: _craPreviewData,
    siteReadinessPreviewData: _siteReadinessPreviewData,
    craFormat: _craFormat,
    setCraFormat: _setCraFormat,
    craTask: _craTask,
    setCraTask: _setCraTask,
    craCameraViewId: _craCameraViewId,
    setCraCameraViewId: _setCraCameraViewId,
    craAcceptanceMode: _craAcceptanceMode,
    setCraAcceptanceMode: _setCraAcceptanceMode,
    craCaptureProtocolFile: _craCaptureProtocolFile,
    setCraCaptureProtocolFile: _setCraCaptureProtocolFile,
    craCaptureScenario: _craCaptureScenario,
    setCraCaptureScenario: _setCraCaptureScenario,
    craUseEdgeModels: _craUseEdgeModels,
    setCraUseEdgeModels: _setCraUseEdgeModels,
    craEdgeDeviceId: _craEdgeDeviceId,
    setCraEdgeDeviceId: _setCraEdgeDeviceId,
    craEdgeCameraId: _craEdgeCameraId,
    setCraEdgeCameraId: _setCraEdgeCameraId,
    craEdgeMode: _craEdgeMode,
    setCraEdgeMode: _setCraEdgeMode,
    craIncludeEdgeFramesZip: _craIncludeEdgeFramesZip,
    setCraIncludeEdgeFramesZip: _setCraIncludeEdgeFramesZip,
    craLiveCameraId: _craLiveCameraId,
    setCraLiveCameraId: _setCraLiveCameraId,
    craCaptureSec: _craCaptureSec,
    setCraCaptureSec: _setCraCaptureSec,
    craClipFile: _craClipFile,
    setCraClipFile: _setCraClipFile,
    craPrecomputedFile: _craPrecomputedFile,
    setCraPrecomputedFile: _setCraPrecomputedFile,
    craRoiFile: _craRoiFile,
    setCraRoiFile: _setCraRoiFile,
    craDeviceMetadataFile: _craDeviceMetadataFile,
    setCraDeviceMetadataFile: _setCraDeviceMetadataFile,
    craAnnotateFrames: _craAnnotateFrames,
    setCraAnnotateFrames: _setCraAnnotateFrames,
    craIncludeRawFrames: _craIncludeRawFrames,
    setCraIncludeRawFrames: _setCraIncludeRawFrames,
    craAnalysisFps: _craAnalysisFps,
    setCraAnalysisFps: _setCraAnalysisFps,
    craMaxDurationSec: _craMaxDurationSec,
    setCraMaxDurationSec: _setCraMaxDurationSec,
    craMaxFrames: _craMaxFrames,
    setCraMaxFrames: _setCraMaxFrames,
    siteReadinessFormat: _siteReadinessFormat,
    setSiteReadinessFormat: _setSiteReadinessFormat,
    siteReadinessMode: _siteReadinessMode,
    setSiteReadinessMode: _setSiteReadinessMode,
    siteReadinessTaskProfile: _siteReadinessTaskProfile,
    setSiteReadinessTaskProfile: _setSiteReadinessTaskProfile,
    siteReadinessSiteProfileFile: _siteReadinessSiteProfileFile,
    setSiteReadinessSiteProfileFile: _setSiteReadinessSiteProfileFile,
    siteReadinessCamerasFile: _siteReadinessCamerasFile,
    setSiteReadinessCamerasFile: _setSiteReadinessCamerasFile,
    siteReadinessRtspStabilitySec: _siteReadinessRtspStabilitySec,
    setSiteReadinessRtspStabilitySec: _setSiteReadinessRtspStabilitySec,
    siteReadinessRtspJitterFailMs: _siteReadinessRtspJitterFailMs,
    setSiteReadinessRtspJitterFailMs: _setSiteReadinessRtspJitterFailMs,
    siteReadinessSwitchModel: _siteReadinessSwitchModel,
    setSiteReadinessSwitchModel: _setSiteReadinessSwitchModel,
    siteReadinessPoeTotalWatts: _siteReadinessPoeTotalWatts,
    setSiteReadinessPoeTotalWatts: _setSiteReadinessPoeTotalWatts,
    siteReadinessPoePortClass: _siteReadinessPoePortClass,
    setSiteReadinessPoePortClass: _setSiteReadinessPoePortClass,
    siteReadinessPoePorts: _siteReadinessPoePorts,
    setSiteReadinessPoePorts: _setSiteReadinessPoePorts,
    siteReadinessPoweredCameras: _siteReadinessPoweredCameras,
    setSiteReadinessPoweredCameras: _setSiteReadinessPoweredCameras,
    siteReadinessCamerasTransport: _siteReadinessCamerasTransport,
    setSiteReadinessCamerasTransport: _setSiteReadinessCamerasTransport,
    siteReadinessRfRssiDb: _siteReadinessRfRssiDb,
    setSiteReadinessRfRssiDb: _setSiteReadinessRfRssiDb,
    siteReadinessRfChannel: _siteReadinessRfChannel,
    setSiteReadinessRfChannel: _setSiteReadinessRfChannel,
    siteReadinessRfPacketLossPct: _siteReadinessRfPacketLossPct,
    setSiteReadinessRfPacketLossPct: _setSiteReadinessRfPacketLossPct,
    siteReadinessRfMeasurementMethod: _siteReadinessRfMeasurementMethod,
    setSiteReadinessRfMeasurementMethod: _setSiteReadinessRfMeasurementMethod,
    reportSignalView: _reportSignalView,
    setReportSignalView: _setReportSignalView,
    reportSuppressionView: _reportSuppressionView,
    setReportSuppressionView: _setReportSuppressionView,
    rulePolicyLoading: _rulePolicyLoading,
    rulePolicySaving: _rulePolicySaving,
    rulePolicyError: _rulePolicyError,
    setRulePolicyError: _setRulePolicyError,
    rulePolicyVersion: _rulePolicyVersion,
    rulePolicyFromDb: _rulePolicyFromDb,
    rulePolicyVersions: _rulePolicyVersions,
    rulePolicyVersionsLoading: _rulePolicyVersionsLoading,
    rulePolicyPreview: _rulePolicyPreview,
    rulePolicyPreviewLoading: _rulePolicyPreviewLoading,
    ruleTopAreasLimit: _ruleTopAreasLimit,
    setRuleTopAreasLimit: _setRuleTopAreasLimit,
    ruleTopTypesLimit: _ruleTopTypesLimit,
    setRuleTopTypesLimit: _setRuleTopTypesLimit,
    ruleTopActionsLimit: _ruleTopActionsLimit,
    setRuleTopActionsLimit: _setRuleTopActionsLimit,
    rulePpePerHourRed: _rulePpePerHourRed,
    setRulePpePerHourRed: _setRulePpePerHourRed,
    ruleDwellTrendAmber: _ruleDwellTrendAmber,
    setRuleDwellTrendAmber: _setRuleDwellTrendAmber,
    ruleHighCriticalRed: _ruleHighCriticalRed,
    setRuleHighCriticalRed: _setRuleHighCriticalRed,
    ruleNoIncidentsGrey: _ruleNoIncidentsGrey,
    setRuleNoIncidentsGrey: _setRuleNoIncidentsGrey,
    ruleMustActTypes: _ruleMustActTypes,
    setRuleMustActTypes: _setRuleMustActTypes,
    ruleSafetyCriticalTypes: _ruleSafetyCriticalTypes,
    setRuleSafetyCriticalTypes: _setRuleSafetyCriticalTypes,
    ruleOperationalTypes: _ruleOperationalTypes,
    setRuleOperationalTypes: _setRuleOperationalTypes,
    reportFieldVisibility: _reportFieldVisibility,
    taskRequiresAdvancedArtifactMode: _taskRequiresAdvancedArtifactMode,
    showTaskFirstMoreOptions: _showTaskFirstMoreOptions,
    _openReportLink,
    _riskFlagSeverity,
    _loadRulePolicy,
    _buildRulePolicyFromUi,
    _saveRulePolicy,
    _activateRulePolicyVersion,
    _deactivateRulePolicy,
    _previewRulePolicyImpact,
    _openReportDialog,
    _downloadCsv,
    _printReport,
    _runReportAction,
    _downloadProofPackAction,
    downloadPersistedSafetyScanPdf,
    downloadPersistedSafetyScanPackage,
    loadPersistedSafetyReportReadOnly,
    artifactPdfStatus,
    artifactZipStatus,
    proofPackDownloading,
    safetyReportTypeMode,
    setSafetyReportTypeMode,
    hazardClassKey,
    setHazardClassKey,
  } = useIncidentsReports({
    filterCameraId,
    filterZoneId,
    filterDeviceId,
    timeRange: workspaceTimeRange,
    searchParams,
    setSearchParams,
    getScopedSiteId: _scopedSiteId,
    getScopedOrgId: _scopedOrgId,
    downloadBlob,
  });

  const _customerReportSubtitle = useMemo(
    () => resolveCustomerReportSubtitle(safetyReportTypeMode),
    [safetyReportTypeMode],
  );
  const reportBuildingBannerText = useMemo(
    () => resolveReportBuildingBannerText(safetyReportTypeMode, hazardClassKey),
    [hazardClassKey, safetyReportTypeMode],
  );
  const customerReportProductName = useMemo(
    () => resolveReportProductName(safetyReportTypeMode, hazardClassKey),
    [hazardClassKey, safetyReportTypeMode],
  );

  const artifactFlowPreset = useMemo(
    () => resolveArtifactFlowPreset(searchParams),
    [searchParams],
  );

  const didSeedDefaults = useRef(false);
  const autoOpenedPersistedReportIdRef = useRef('');

  const readiness = (proofPackPreviewData?.proof_readiness || null) as ProofReadinessModel | null;
  const proofPackExists = hasBuiltProofPackPreview(proofPackPreviewData);
  const proofPackArtifactExists = useMemo(() => {
    const persistedReportId = String(proofPackPreviewData?.persisted_report?.report_id || '').trim();
    if (persistedReportId) return true;
    const proofPack = proofPackPreviewData?.evidence_pack?.contents?.reliability_proof_pack;
    return Boolean(proofPack && typeof proofPack === 'object');
  }, [
    proofPackPreviewData?.evidence_pack?.contents?.reliability_proof_pack,
    proofPackPreviewData?.persisted_report?.report_id,
  ]);
  // Persisted draft id or built preview both mean downloads should work.
  const packageBuilt = proofPackArtifactExists || proofPackExists;
  const finalizedPersistedReport = useMemo(() => {
    const persistedStatus = String(
      proofPackPreviewData?.persisted_report?.status
      || firstStringFromPaths(proofPackPreviewData, ['evidence_pack.report_projection.report_status'])
      || '',
    ).trim().toLowerCase();
    return persistedStatus === 'finalized';
  }, [proofPackPreviewData]);
  const proofPackGenerationInProgress =
    reportKind === 'proof_pack' && (reportLoading || Boolean(proofPackJobId)) && !proofPackDownloading;
  const proofPageState = useMemo<ProofPageState>(() => {
    if (proofPackGenerationInProgress) {
      if (readiness && proofPackArtifactExists) return 'READY';
      return 'GENERATING';
    }
    if (readiness && proofPackArtifactExists) return 'READY';
    if (proofPackArtifactExists) return 'GENERATED_WITHOUT_READINESS';
    if (reportError && !proofPackDownloading) return 'ERROR';
    return 'EMPTY';
  }, [proofPackArtifactExists, proofPackDownloading, proofPackGenerationInProgress, readiness, reportError]);
  const explicitPersistedReportId = useMemo(
    () => String(searchParams.get('report_id') || '').trim(),
    [searchParams],
  );
  const persistedDraftReportId = useMemo(
    () => String(
      explicitPersistedReportId
      || proofPackPreviewData?.persisted_report?.report_id
      || '',
    ).trim(),
    [explicitPersistedReportId, proofPackPreviewData?.persisted_report?.report_id],
  );
  const persistedReportPayload = useMemo(
    () => (
      (proofPackPreviewData?.persisted_report?.report_payload as Record<string, unknown> | undefined)
      || null
    ),
    [proofPackPreviewData?.persisted_report?.report_payload],
  );
  const handoffParams = useMemo(
    () => parseSafetyScanHandoffFromSearchParams(
      searchParams,
      persistedReportPayload,
      (proofPackPreviewData?.persisted_report?.workflow_anchor as Record<string, unknown> | undefined) || null,
      (proofPackPreviewData?.persisted_report as Record<string, unknown> | undefined) || null,
    ),
    [
      persistedReportPayload,
      proofPackPreviewData?.persisted_report,
      searchParams,
    ],
  );
  const isWorkflowLinkedSafetyScan = useMemo(
    () => Boolean(String(handoffParams.safetyCaseId || searchParams.get('safety_case_id') || '').trim()),
    [handoffParams.safetyCaseId, searchParams],
  );
  const linkedScanHasPersistedDraft = Boolean(proofPackPreviewData?.persisted_report?.report_id);
  const workflowLinkedDraftModeEligible = isWorkflowLinkedSafetyScan
    && safetyReportTypeMode !== 'hazard_class'
    && (linkedScanHasPersistedDraft || proofPackArtifactExists || !readiness);
  const [draftReportPayload, setDraftReportPayload] = useState<Record<string, unknown> | null>(persistedReportPayload);
  const [safetyScanFinalizing, setSafetyScanFinalizing] = useState(false);
  const [measurementGateAdmitted, setMeasurementGateAdmitted] = useState<boolean | null>(null);
  const [fieldVerificationAllowed, setFieldVerificationAllowed] = useState<boolean | null>(null);
  const [fieldVerificationLabel, setFieldVerificationLabel] = useState<string | null>(null);
  const measurementAssuranceSectionRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    setDraftReportPayload(persistedReportPayload);
  }, [persistedReportPayload]);
  useEffect(() => {
    if (!explicitPersistedReportId || !finalizedPersistedReport) return;
    const workflowAnchor = proofPackPreviewData?.persisted_report?.workflow_anchor || null;
    const scope = workflowAnchor?.scope || null;
    const timeWindow = workflowAnchor?.time_window || null;
    const scopeZones = scope?.zones;
    const zones = Array.isArray(scopeZones) ? scopeZones : [];
    const anchorIncidentId = String(
      workflowAnchor?.representative_incident_id
      || searchParams.get('incident_id')
      || searchParams.get('case_id')
      || '',
    ).trim();
    navigate(buildSafetyReportArtifactPath({
      reportId: explicitPersistedReportId,
      orgId: searchParams.get('org_id'),
      siteId: String(scope?.site_id || searchParams.get('site_id') || '').trim() || undefined,
      workflowId: String(
        proofPackPreviewData?.persisted_report?.workflow_id
        || workflowAnchor?.workflow_id
        || searchParams.get('workflow_id')
        || '',
      ).trim() || undefined,
      deviceId: String(scope?.device_id || searchParams.get('device_id') || '').trim() || undefined,
      zoneId: String(zones[0] || searchParams.get('zone_id') || '').trim() || undefined,
      cameraId: String(scope?.camera_id || searchParams.get('camera_id') || '').trim() || undefined,
      reportFrom: String(timeWindow?.start || timeWindow?.from || searchParams.get('report_from') || '').trim() || undefined,
      reportTo: String(timeWindow?.end || timeWindow?.to || searchParams.get('report_to') || '').trim() || undefined,
      caseId: anchorIncidentId || undefined,
      incidentId: anchorIncidentId || undefined,
      policyType: String(scope?.risk_type || searchParams.get('policy_type') || '').trim() || undefined,
      policyName: String(searchParams.get('policy_name') || '').trim() || undefined,
    }), { replace: true });
  }, [explicitPersistedReportId, finalizedPersistedReport, navigate, proofPackPreviewData, searchParams]);
  useEffect(() => {
    const reopenNotice = String(searchParams.get('report_notice') || '').trim();
    if (!reopenNotice) return;
    _setReportNotice(reopenNotice);
  }, [_setReportNotice, searchParams]);
  const hasReadinessDecision = proofPageState === 'READY' || finalizedPersistedReport;
  const [activeProofSurface, setActiveProofSurface] = useState<ProofSurfaceKey>(() => proofSurfaceFromPreset(searchParams));
  const userSelectedProofSurfaceRef = useRef(false);
  const correctiveSummarySectionRef = useRef<HTMLDivElement | null>(null);
  const evidenceBundleSectionRef = useRef<HTMLDivElement | null>(null);
  const reliabilityReviewSectionRef = useRef<HTMLDivElement | null>(null);
  const correctiveActionStatusSectionRef = useRef<HTMLDivElement | null>(null);
  const impactMeasurementStatusSectionRef = useRef<HTMLDivElement | null>(null);
  const reportPackageSectionRef = useRef<HTMLDivElement | null>(null);
  const finalSignoffSectionRef = useRef<HTMLDivElement | null>(null);
  const caseSummarySectionRef = useRef<HTMLDivElement | null>(null);
  const caseActionsSectionRef = useRef<HTMLDivElement | null>(null);

  const openIncidentsRaw = proofPackPreviewData?.case_file?.summary?.open_total;
  const openIncidents = hasFiniteNumber(openIncidentsRaw) ? Number(openIncidentsRaw) : null;
  const evidenceAvailable = useMemo(() => evidenceMetricsAvailable(readiness), [readiness]);
  const reliabilityAvailable = useMemo(() => reliabilityMetricsAvailable(readiness), [readiness]);
  const camerasAvailable = useMemo(() => cameraAvailabilityAvailable(readiness), [readiness]);

  const normalizedSummary = useMemo<ProofReadinessSummary | undefined>(() => {
    if (!readiness?.summary) return readiness?.summary;
    const evOp = String(readiness.summary.evidence_operator_status || '').toLowerCase();
    const relOp = String(readiness.summary.reliability_operator_status || '').toLowerCase();
    if (evOp || relOp) {
      return { ...readiness.summary };
    }
    return {
      ...readiness.summary,
      evidence_coverage_rate: evidenceAvailable ? readiness.summary.evidence_coverage_rate : undefined,
      cameras_online: camerasAvailable ? readiness.summary.cameras_online : null,
      cameras_total: camerasAvailable ? readiness.summary.cameras_total : null,
      reliability_status: reliabilityAvailable ? readiness.summary.reliability_status : undefined,
    };
  }, [camerasAvailable, evidenceAvailable, readiness, reliabilityAvailable]);

  const normalizedVerdict = useMemo<ProofReadinessVerdict | undefined>(() => {
    if (!readiness?.verdict) return readiness?.verdict;
    return {
      ...readiness.verdict,
      reason: buildVerdictReason(readiness.verdict, readiness, proofPackPreviewData, {
        evidenceAvailable,
        reliabilityAvailable,
      }),
      factors: sanitizeVerdictFactors(readiness.verdict, {
        evidenceAvailable,
        reliabilityAvailable,
        cameraAvailabilityAvailable: camerasAvailable,
      }),
    };
  }, [camerasAvailable, evidenceAvailable, proofPackPreviewData, readiness, reliabilityAvailable]);

  const dominantPattern = useMemo(() => {
    const topIssues = readiness?.top_issues;
    return dominantPatternLabel(Array.isArray(topIssues) ? topIssues : [], normalizedSummary);
  }, [normalizedSummary, readiness?.top_issues]);

  const proofRiskPatternSummary = useMemo(
    () => normalizedSummary?.risk_pattern_summary || null,
    [normalizedSummary],
  );

  const proofTopHotspotRaw = useMemo(
    () => String(proofRiskPatternSummary?.top_hotspot || normalizedSummary?.top_hotspot || '').trim(),
    [normalizedSummary?.top_hotspot, proofRiskPatternSummary?.top_hotspot],
  );

  const proofTopHotspotLabel = useMemo(() => {
    if (!proofTopHotspotRaw) return '';
    const human = humanizeZoneLabel(proofTopHotspotRaw);
    return customerFacingAreaLabel(human) || human;
  }, [proofTopHotspotRaw]);

  const proofDominantPatternLabel = useMemo(() => {
    const raw = String(proofRiskPatternSummary?.strongest_pattern_label || dominantPattern?.label || '').trim();
    if (!raw) return '';
    return customerFacingSafetyRuleLabel(raw);
  }, [dominantPattern?.label, proofRiskPatternSummary?.strongest_pattern_label]);

  const clipCaptureDisabledUi = useMemo(
    () => clipCaptureDisabled(readiness, readiness?.verdict),
    [readiness],
  );

  const pipelineDegradedForUi = useMemo(() => pipelineDegraded(normalizedVerdict), [normalizedVerdict]);

  const { safetyActions, evidenceActions, operatorFollowupActions: _operatorFollowupActions } = useMemo(() => {
    const actionsRaw = readiness?.actions;
    const issuesRaw = readiness?.top_issues;
    const issues = Array.isArray(issuesRaw) ? issuesRaw : [];
    const zoneIssues = issues.filter(isZoneViolationIssue);
    const hasOpenIncidentBlocker = openIncidents != null && openIncidents > 0;
    const verdictPipelineDegraded = pipelineDegraded(normalizedVerdict);
    const baseActions = Array.isArray(actionsRaw)
      ? [...actionsRaw]
          .filter((action) => !(hasOpenIncidentBlocker && /review open incidents|open incidents need review/i.test(String(action?.label || ''))))
          .filter((action) => !(hasOpenIncidentBlocker && /review unresolved high-risk incidents|review open critical risks/i.test(String(action?.label || ''))))
          .filter((action) => !(!evidenceAvailable && /generate proof with evidence metrics/i.test(String(action?.label || ''))))
          .filter((action) => !(!reliabilityAvailable && /confirm reliability metrics/i.test(String(action?.label || ''))))
          .filter((action) => !(verdictPipelineDegraded && /reduce evidence delay\/failures/i.test(String(action?.label || ''))))
          .filter((action) => !/proceed with approval/i.test(String(action?.label || '')))
          .sort(
            (left, right) =>
              actionPriorityRank(left?.priority) - actionPriorityRank(right?.priority),
          )
      : [];

    const syntheticActions: ProofReadinessAction[] = [];
    const incidentHref = firstIncidentHref(readiness, proofPackPreviewData);
    const zoneHref = firstIssueHref(zoneIssues) || incidentHref;
    const status = String(normalizedVerdict?.status || '').toUpperCase();

    if (zoneIssues.length > 0) {
      const zoneCount = zoneIssues.reduce((sum, issue) => {
        const count = typeof issue?.count === 'number' && Number.isFinite(issue.count) ? issue.count : 1;
        return sum + count;
      }, 0);
      syntheticActions.push({
        id: 'review_open_zone_violations',
        label: 'Review open danger-zone entries',
        priority: status === 'NOT_READY' ? 'high' : 'medium',
        reason: `${formatNumber(zoneCount)} open danger-zone ${zoneCount === 1 ? 'entry' : 'entries'} should be reduced before relying on this window as a baseline.`,
        href: zoneHref || undefined,
      });
    } else if (openIncidents != null && openIncidents > 0) {
      syntheticActions.push({
        id: 'review_open_incidents',
        label: 'Review open incidents',
        priority: status === 'NOT_READY' ? 'high' : 'medium',
        reason: `${pluralize(openIncidents, 'incident')} remain open in this reporting period.`,
        href: incidentHref,
      });
    }
    const topHotspotRaw = proofTopHotspotRaw;
    const hasReviewHotspotAction = baseActions.some((a) => isSafetyInterventionAction(a) && /hotspot|high-risk area|risk area/i.test(actionTextBlob(a)));
    if (topHotspotRaw && !hasReviewHotspotAction) {
      syntheticActions.push({
        id: 'review_hotspot',
        label: 'Review top high-risk area',
        priority: zoneIssues.length > 0 ? 'medium' : 'high',
        reason: `Exposure is most concentrated in the ${humanizeAreaLabel(topHotspotRaw)} in this reporting period.`,
        href: zoneHref || incidentHref || undefined,
      });
    }
    const hasBackendRegenBundle = baseActions.some(
      (a) => String(a?.id || '') === 'regenerate_proof_readiness_inputs',
    );
    if ((!evidenceAvailable || !reliabilityAvailable) && !hasBackendRegenBundle) {
      syntheticActions.push({
        id: 'regenerate_proof_readiness_inputs',
        label: 'Regenerate analysis with evidence and reliability metrics',
        priority: 'medium',
        reason: 'Evidence and reliability metrics are missing from this reporting period.',
      });
    }
    if (verdictPipelineDegraded) {
      syntheticActions.push({
        id: 'investigate_degraded_pipeline',
        label: 'Inspect edge pipeline diagnostics',
        priority: 'medium',
        reason: 'The edge detection pipeline had health issues during this reporting period.',
      });
    }
    if (status === 'READY_WITH_WARNINGS') {
      syntheticActions.push({
        id: 'review_warnings_before_approval',
        label: 'Resolve outstanding warnings',
        priority: 'medium',
        reason: normalizedVerdict?.reason || 'Warnings still need operator follow-up alongside safety work.',
        href: incidentHref,
      });
    }

    const merged = sortProofActionsForDisplay(dedupeActions([...syntheticActions, ...baseActions]));
    const filtered = merged.filter((a) => {
      const id = String(a?.id || '').toLowerCase();
      const label = String(a?.label || '').toLowerCase();
      if (id === 'proceed_with_approval' || id === 'proceed') return false;
      if (/proceed with approval|document acceptance/i.test(label)) return false;
      return true;
    });
    const safety = sortSafetyActionsForDisplay(filtered.filter(isSafetyInterventionAction));
    const evidence = sortEvidenceActionsForDisplay(filtered.filter(isEvidenceConfidenceAction));
    const operatorFollowup = sortOperatorActionsForDisplay(
      filtered.filter((a) => !isSafetyInterventionAction(a) && !isEvidenceConfidenceAction(a) && isOperatorFollowupAction(a)),
    );
    return { safetyActions: safety, evidenceActions: evidence, operatorFollowupActions: operatorFollowup };
  }, [evidenceAvailable, normalizedVerdict, openIncidents, proofPackPreviewData, proofTopHotspotRaw, readiness, reliabilityAvailable]);

  const pedestrianZoneHero = useMemo(() => {
    if (!readiness) return null;
    const issues = Array.isArray(readiness.top_issues) ? readiness.top_issues : [];
    const dominant = dominantPattern;
    if (!dominant || !/zone violation|danger-zone entries/i.test(String(dominant.label || ''))) return null;
    const raw = proofTopHotspotRaw;
    if (!raw) return null;
    const humane = humanizeZoneLabel(raw);
    const t = humane.toLowerCase();
    const areaLabel = humanizeAreaLabel(humane);
    const controlLabel = /(pedestrian|crosswalk|walkway|footpath|seconddemo)/i.test(`${t} ${areaLabel.toLowerCase()}`)
      ? 'pedestrian controls'
      : 'area controls';
    const href = firstIssueHref(issues.filter(isZoneViolationIssue)) || firstIncidentHref(readiness, proofPackPreviewData) || undefined;
    return { zoneLabel: humane, areaLabel, href, controlLabel };
  }, [dominantPattern, proofPackPreviewData, proofTopHotspotRaw, readiness]);

  const safetyActionsForUi = useMemo(() => {
    if (!pedestrianZoneHero) return safetyActions;
    return safetyActions.filter((a) => !['review_open_zone_violations', 'review_hotspot'].includes(String(a?.id || '')));
  }, [pedestrianZoneHero, safetyActions]);

  const evidenceConfidenceSummary = useMemo(
    () =>
      evidenceConfidenceLabel(
        evidenceConfidenceTier({
          summary: normalizedSummary,
          evidence: readiness?.evidence,
          clipCaptureDisabled: clipCaptureDisabledUi,
        }),
      ),
    [clipCaptureDisabledUi, normalizedSummary, readiness],
  );

  const _usableSafetyPattern = useMemo(
    () =>
      hasUsableSafetyPattern({
        summary: normalizedSummary,
        issues: readiness?.top_issues,
        caseFileSummary: proofPackPreviewData?.case_file?.summary,
      }),
    [normalizedSummary, readiness?.top_issues, proofPackPreviewData?.case_file?.summary],
  );

  const _operatorPresentation = useMemo(() => {
    if (!hasReadinessDecision || !readiness?.verdict) {
      return {
        displayReason: undefined as string | undefined,
        displayFactors: undefined as string[] | undefined,
      };
    }
    const status = String(readiness.verdict.status || '').toUpperCase();
    const clip = clipCaptureDisabledUi;
    const inf = inferenceTrustDegraded(readiness, readiness.verdict);
    const displayReason =
      buildOperatorBlockingVerdictSentence({
        status,
        openIncidents,
        clipDisabled: clip,
        inferenceDegraded: inf,
      }) ?? undefined;
    const factorRows =
      status === 'NOT_READY' || status === 'NEEDS_REVIEW'
        ? buildOperatorVerdictFactors({
            openIncidents,
            highRiskIncidents: readiness.summary?.high_risk_incidents,
            clipDisabled: clip,
            inferenceDegraded: inf,
          })
        : [];
    const displayFactors = factorRows.length ? factorRows : undefined;
    return { displayReason, displayFactors };
  }, [clipCaptureDisabledUi, hasReadinessDecision, openIncidents, readiness]);

  const supportPackageGeneratedAt = useMemo(
    () => proofPackGeneratedAt(proofPackPreviewData),
    [proofPackPreviewData],
  );
  const _proofPackStartedAt = useMemo(
    () => firstDefinedString(
      firstStringFromPaths(proofPackPreviewData, [
        'job.started_at',
        'manifest.started_at',
        'manifest.created_at',
        'evidence_pack.started_at',
        'evidence_pack.created_at',
      ]),
    ),
    [proofPackPreviewData],
  );
  const _supportPackageStatus = hasReadinessDecision
    ? 'Generated'
    : proofPackExists
      ? 'Needs refresh'
      : 'Missing';

  const _proofPackPrimaryActionLabel = proofPageState === 'GENERATED_WITHOUT_READINESS'
    ? 'Regenerate Scan Package'
    : 'Build Scan Package';
  const _proofPackSupportTitle = hasReadinessDecision ? 'Bundle tools' : 'Bundle settings';
  const _proofPackSupportDescription = hasReadinessDecision
    ? 'Download the current bundle or reopen the bundle settings for this case.'
    : 'Review the current scope, time window, and bundle settings for this case.';

  const proofActionRoutingCapabilities = useMemo<ProofActionRoutingCapabilities>(() => ({
    canUseCopilot: canAccessWarehouseSurface(capabilityProfile.capabilities, 'opsCopilot'),
    canUseRunbooks: canAccessWarehouseSurface(capabilityProfile.capabilities, 'opsRunbooks'),
    canReadIncidents: capabilityProfile.canReadIncidents,
    canOpenDiagnostics: capabilityProfile.hasHealthAccess,
    canControlDevices: canOpenReportDeviceControlLink,
    canCreateFollowupReview: capabilityProfile.canManageAcceptanceArtifacts,
  }), [canOpenReportDeviceControlLink, capabilityProfile]);

  const firstReportIncident = useMemo(() => {
    const incidents = reportData?.recent_incidents;
    return Array.isArray(incidents)
      ? incidents.find((item: any) => item && typeof item === 'object')
      : undefined;
  }, [reportData]);
  const firstProofCaseIncident = useMemo(() => {
    const caseIncidents = proofPackPreviewData?.case_file?.incidents;
    return Array.isArray(caseIncidents)
      ? caseIncidents.find((item: any) => item && typeof item === 'object')
      : undefined;
  }, [proofPackPreviewData]);

  const canonicalProofScopeId = useMemo(
    () => firstDefinedString(
      searchParams.get('proof_scope_id'),
      firstStringFromPaths(proofPackPreviewData, [
        'proof_scope.proof_scope_id',
        'case_file.proof_scope.proof_scope_id',
        'manifest.proof_scope.proof_scope_id',
        'evidence_pack.proof_scope.proof_scope_id',
      ]),
      firstStringFromPaths(firstProofCaseIncident, [
        'proof_scope.proof_scope_id',
        'safety_case.proof_scope.proof_scope_id',
      ]),
      firstStringFromPaths(firstReportIncident, [
        'proof_scope.proof_scope_id',
        'safety_case.proof_scope.proof_scope_id',
      ]),
    ),
    [firstProofCaseIncident, firstReportIncident, proofPackPreviewData, searchParams],
  );

  const canonicalProofDeviceId = useMemo(
    () => firstDefinedString(
      reportDeviceId,
      searchParams.get('device_id'),
      searchParams.get('device'),
      firstStringFromPaths(reportData, ['filters.device_id']),
      firstStringFromPaths(firstReportIncident, ['device_id', 'deviceId']),
      firstStringFromPaths(firstProofCaseIncident, ['device_id', 'deviceId']),
      firstStringFromPaths(proofPackPreviewData, [
        'proof_scope.device_id',
        'case_file.proof_scope.device_id',
        'manifest.device_id',
        'manifest.filters.device_id',
        'manifest.scope.device_id',
        'evidence_pack.device_id',
        'evidence_pack.proof_scope.device_id',
      ]),
    ),
    [firstProofCaseIncident, firstReportIncident, proofPackPreviewData, reportData, reportDeviceId, searchParams],
  );

  const canonicalProofZoneId = useMemo(
    () => firstDefinedString(
      reportZoneId,
      searchParams.get('zone_id'),
      searchParams.get('zone'),
      firstStringFromPaths(reportData, ['filters.zone_id']),
      firstStringFromPaths(firstReportIncident, ['zone_id', 'zoneId']),
      firstStringFromPaths(firstProofCaseIncident, ['zone_id', 'zoneId']),
      firstStringFromPaths(proofPackPreviewData, [
        'proof_scope.zone_id',
        'case_file.proof_scope.zone_id',
        'manifest.zone_id',
        'manifest.filters.zone_id',
        'manifest.scope.zone_id',
        'evidence_pack.zone_id',
        'evidence_pack.proof_scope.zone_id',
      ]),
    ),
    [firstProofCaseIncident, firstReportIncident, proofPackPreviewData, reportData, reportZoneId, searchParams],
  );

  const canonicalProofZoneName = useMemo(
    () => firstDefinedString(
      firstStringFromPaths(firstReportIncident, ['zone_name', 'zoneName']),
      firstStringFromPaths(firstProofCaseIncident, ['zone_name', 'zoneName']),
      firstStringFromPaths(proofPackPreviewData, [
        'manifest.zone_name',
        'manifest.filters.zone_name',
        'manifest.scope.zone_name',
        'evidence_pack.zone_name',
      ]),
      canonicalProofZoneId ? lookupLabeledValue(zoneNameById, canonicalProofZoneId) : undefined,
    ),
    [canonicalProofZoneId, firstProofCaseIncident, firstReportIncident, proofPackPreviewData, zoneNameById],
  );

  const canonicalProofSiteId = useMemo(
    () => firstDefinedString(
      reportSiteId,
      searchParams.get('site_id'),
      searchParams.get('site'),
      firstStringFromPaths(reportData, ['filters.site_id', 'filters.site']),
      firstStringFromPaths(firstReportIncident, ['site_id', 'siteId', 'site_slug', 'siteSlug']),
      firstStringFromPaths(firstProofCaseIncident, ['site_id', 'siteId', 'site_slug', 'siteSlug']),
      firstStringFromPaths(proofPackPreviewData, [
        'proof_scope.site_id',
        'case_file.proof_scope.site_id',
        'manifest.site_id',
        'manifest.site_slug',
        'manifest.filters.site_id',
        'manifest.filters.site',
        'manifest.scope.site_id',
        'manifest.scope.site',
        'evidence_pack.site_id',
        'evidence_pack.proof_scope.site_id',
      ]),
      scopedSiteId,
    ),
    [firstProofCaseIncident, firstReportIncident, proofPackPreviewData, reportData, reportSiteId, scopedSiteId, searchParams],
  );
  const siteTimeZone = useSiteTimeZone(canonicalProofSiteId || scopedSiteId);
  const observationFunnelQueue = useObservationFunnelQueue(persistedDraftReportId);
  const observationFunnelProgress = observationFunnelQueue.progress;
  const operatorLifecycleSnapshot = useOperatorLifecycleSnapshot(persistedDraftReportId);
  const measurementReferencesRaw = operatorLifecycleSnapshot.measurementReferences;

  const canonicalProofWindow = useMemo(() => resolveCanonicalProofWindow({
    persistedReport: proofPackPreviewData?.persisted_report,
    queueWindow: observationFunnelQueue.window,
    reportFrom,
    reportTo,
    searchParams,
    reportData,
    proofPackPreviewData,
    workspaceFromDate: workspaceTimeRange.fromDate,
    workspaceToDate: workspaceTimeRange.toDate,
  }), [observationFunnelQueue.window, proofPackPreviewData, reportData, reportFrom, reportTo, searchParams, workspaceTimeRange.fromDate, workspaceTimeRange.toDate]);
  const canonicalProofWindowStart = canonicalProofWindow.start;
  const canonicalProofWindowEnd = canonicalProofWindow.end;

  const canonicalProofIncidentType = useMemo(
    () => firstDefinedString(
      searchParams.get('policy_type'),
      reportPolicyType,
      searchParams.get('incident_type'),
      searchParams.get('type'),
      firstStringFromPaths(reportData, ['filters.policy_type']),
      firstStringFromPaths(proofPackPreviewData, [
        'proof_scope.policy_type',
        'case_file.proof_scope.policy_type',
        'manifest.policy_type',
        'manifest.filters.policy_type',
        'manifest.scope.policy_type',
        'evidence_pack.policy_type',
        'evidence_pack.proof_scope.policy_type',
      ]),
      firstStringFromPaths(firstProofCaseIncident, [
        'canonical_labels.incident_type_key',
        'safety_case.scope.policy_type',
      ]),
      firstStringFromPaths(firstReportIncident, ['incident_type', 'incidentType']),
      firstStringFromPaths(firstProofCaseIncident, ['incident_type', 'incidentType']),
    ),
    [firstProofCaseIncident, firstReportIncident, proofPackPreviewData, reportData, reportPolicyType, searchParams],
  );

  const explicitProofPolicyId = useMemo(
    () => firstDefinedString(
      searchParams.get('policy_id'),
      firstStringFromPaths(firstProofCaseIncident, ['policy_id', 'triggered_by.policy_id']),
      firstStringFromPaths(firstReportIncident, ['policy_id', 'triggered_by.policy_id']),
      firstStringFromPaths(proofPackPreviewData, [
        'proof_scope.policy_ids.0',
        'case_file.proof_scope.policy_ids.0',
        'manifest.policy_id',
        'manifest.filters.policy_id',
        'manifest.scope.policy_id',
        'evidence_pack.policy_id',
      ]),
    ),
    [firstProofCaseIncident, firstReportIncident, proofPackPreviewData, searchParams],
  );

  const explicitProofPolicyName = useMemo(
    () => firstDefinedString(
      searchParams.get('policy_name'),
      firstStringFromPaths(firstProofCaseIncident, ['policy_name', 'triggered_by.policy_name']),
      firstStringFromPaths(firstReportIncident, ['policy_name', 'triggered_by.policy_name']),
      firstStringFromPaths(proofPackPreviewData, [
        'manifest.policy_name',
        'manifest.filters.policy_name',
        'manifest.scope.policy_name',
        'evidence_pack.policy_name',
      ]),
    ),
    [firstProofCaseIncident, firstReportIncident, proofPackPreviewData, searchParams],
  );

  const explicitAnchorIncidentId = useMemo(
    () => firstDefinedString(
      searchParams.get('incident_id'),
      firstStringFromPaths(proofPackPreviewData, [
        'proof_scope.anchor_incident_id',
        'case_file.proof_scope.anchor_incident_id',
        'manifest.proof_scope.anchor_incident_id',
      ]),
      firstStringFromPaths(firstProofCaseIncident, ['incident_id', 'id']),
      firstStringFromPaths(firstReportIncident, ['incident_id', 'id']),
    ),
    [firstProofCaseIncident, firstReportIncident, proofPackPreviewData, searchParams],
  );

  const supportingPackScope = useMemo<'site' | 'device' | 'zone'>(() => {
    if (String(canonicalProofZoneId || '').trim()) return 'zone';
    if (String(canonicalProofDeviceId || '').trim()) return 'device';
    return 'site';
  }, [canonicalProofDeviceId, canonicalProofZoneId]);

  const supportingPackScopeId = useMemo(
    () => (supportingPackScope === 'zone'
      ? String(canonicalProofZoneId || '').trim()
      : supportingPackScope === 'device'
        ? String(canonicalProofDeviceId || '').trim()
        : ''),
    [canonicalProofDeviceId, canonicalProofZoneId, supportingPackScope],
  );

  const canGenerateSupportingPacks = Boolean(
    scopedOrgId && (canonicalProofSiteId || scopedSiteId) && canonicalProofWindowStart && canonicalProofWindowEnd,
  );
  const supportingPackSiteId = String(canonicalProofSiteId || scopedSiteId || '').trim();
  const supportingPackWindowStart = String(canonicalProofWindowStart || '').trim();
  const supportingPackWindowEnd = String(canonicalProofWindowEnd || '').trim();

  const canonicalProofReportId = useMemo(
    () => firstDefinedString(
      searchParams.get('report_id'),
      searchParams.get('case_id'),
      firstStringFromPaths(proofPackPreviewData, [
        'manifest.report_id',
        'manifest.case_id',
        'evidence_pack.report_id',
        'evidence_pack.case_id',
      ]),
      explicitAnchorIncidentId,
    ),
    [explicitAnchorIncidentId, proofPackPreviewData, searchParams],
  );

  const explicitProofCaseId = useMemo(
    () =>
      firstDefinedString(
        searchParams.get('caseId'),
        searchParams.get('case_id'),
      ),
    [searchParams],
  );

  const currentProofScopeCaseId = useMemo(
    () => {
      const scopedCaseId = String(
        searchParams.get('case_id')
        || searchParams.get('caseId')
        || explicitProofCaseId
        || explicitAnchorIncidentId
        || '',
      ).trim();
      if (scopedCaseId && scopedCaseId === persistedDraftReportId) return String(explicitAnchorIncidentId || '').trim();
      return scopedCaseId;
    },
    [explicitAnchorIncidentId, explicitProofCaseId, persistedDraftReportId, searchParams],
  );

  const currentSelectedIncidentIdsKey = useMemo(() => {
    const rejectReportIdentity = (value: string) => {
      const id = String(value || '').trim();
      return id && id !== persistedDraftReportId ? id : '';
    };
    const explicitIds = String(searchParams.get('incident_ids') || '').trim();
    if (explicitIds) {
      return explicitIds
        .split(',')
        .map((value) => rejectReportIdentity(value))
        .filter(Boolean)
        .join(',');
    }
    return rejectReportIdentity(currentProofScopeCaseId) || String(explicitAnchorIncidentId || '').trim();
  }, [currentProofScopeCaseId, explicitAnchorIncidentId, persistedDraftReportId, searchParams]);

  const hasAppliedIncidentSelection = useMemo(
    () => Boolean(String(currentSelectedIncidentIdsKey || '').trim()),
    [currentSelectedIncidentIdsKey],
  );

  const demoProofCase = useMemo(
    () =>
      isWebSummitDemoProofCaseId(explicitProofCaseId)
        ? buildWebSummitDemoProofCase(searchParams.toString(), explicitProofCaseId)
        : null,
    [explicitProofCaseId, searchParams],
  );

  const canonicalProofPackageId = useMemo(
    () => firstDefinedString(
      firstStringFromPaths(proofPackPreviewData, [
        'manifest.id',
        'manifest.package_id',
        'manifest.proof_package_id',
        'evidence_pack.id',
        'evidence_pack.package_id',
      ]),
      proofPackJobId,
    ),
    [proofPackJobId, proofPackPreviewData],
  );
  const evidenceBundleReviewKey = useMemo(() => {
    const caseScope = canonicalProofReportId || 'case';
    const bundleScope = canonicalProofPackageId || supportPackageGeneratedAt || proofPageState;
    if (!scopedOrgId || !scopedSiteId || !caseScope || !bundleScope) return '';
    return `${EVIDENCE_BUNDLE_REVIEWED_PREFIX}:${scopedOrgId}:${scopedSiteId}:${caseScope}:${bundleScope}`;
  }, [
    canonicalProofPackageId,
    canonicalProofReportId,
    proofPageState,
    scopedOrgId,
    scopedSiteId,
    supportPackageGeneratedAt,
  ]);
  const [evidenceBundleReviewed, setEvidenceBundleReviewed] = useState(false);
  useEffect(() => {
    setEvidenceBundleReviewed(readEvidenceBundleReviewed(evidenceBundleReviewKey));
  }, [evidenceBundleReviewKey]);

  const proofScopedDeviceIds = useMemo(
    () => [
      canonicalProofDeviceId,
      firstStringFromPaths(firstReportIncident, ['device_id', 'deviceId']),
      firstStringFromPaths(firstProofCaseIncident, ['device_id', 'deviceId']),
      firstStringFromPaths(reportData, ['filters.device_id']),
    ].filter((value, index, all) => Boolean(value) && all.indexOf(value) === index) as string[],
    [canonicalProofDeviceId, firstProofCaseIncident, firstReportIncident, reportData],
  );

  const proofRoutingFallbackDeviceId = useMemo(
    () => canonicalProofDeviceId ? undefined : trimmedString(filterDeviceId),
    [canonicalProofDeviceId, filterDeviceId],
  );

  const proofScopedZoneId = useMemo(
    () => canonicalProofZoneId || trimmedString(filterZoneId),
    [canonicalProofZoneId, filterZoneId],
  );

  const proofActionRoutingContext = useMemo<ProofActionRoutingContext>(() => ({
    orgId: scopedOrgId || undefined,
    siteId: canonicalProofSiteId || undefined,
    windowStart: canonicalProofWindowStart || undefined,
    windowEnd: canonicalProofWindowEnd || undefined,
    hotspot: proofTopHotspotRaw ? humanizeZoneLabel(String(proofTopHotspotRaw)) : undefined,
    zoneId: proofScopedZoneId,
    dominantIncidentType: proofDominantPatternLabel || undefined,
    incidentType: canonicalProofIncidentType || undefined,
    affectedDevices: proofScopedDeviceIds,
    affectedStages: [],
    evidenceConfidence: evidenceConfidenceSummary,
    proofCaseDeviceId: canonicalProofDeviceId || undefined,
    proofCaseZoneId: canonicalProofZoneId || undefined,
    proofCaseZoneName: canonicalProofZoneName || undefined,
    proofCaseSiteId: canonicalProofSiteId || undefined,
    proofCaseWindowStart: canonicalProofWindowStart || undefined,
    proofCaseWindowEnd: canonicalProofWindowEnd || undefined,
    proofCaseIncidentType: canonicalProofIncidentType || undefined,
    proofReportId: canonicalProofReportId || undefined,
    proofPackageId: canonicalProofPackageId || undefined,
    fallbackDeviceId: proofRoutingFallbackDeviceId || undefined,
    source: 'proof',
    proofSurface: activeProofSurface,
  }), [
    activeProofSurface,
    canonicalProofDeviceId,
    canonicalProofIncidentType,
    canonicalProofPackageId,
    canonicalProofReportId,
    canonicalProofSiteId,
    canonicalProofWindowEnd,
    canonicalProofWindowStart,
    canonicalProofZoneId,
    canonicalProofZoneName,
    proofDominantPatternLabel,
    evidenceConfidenceSummary,
    proofTopHotspotRaw,
    proofRoutingFallbackDeviceId,
    proofScopedDeviceIds,
    proofScopedZoneId,
    scopedOrgId,
  ]);

  const routedActionCache = useMemo(
    () => new Map<string, RoutedActionResult>(),
    [],
  );

  const getActionKey = useCallback(
    (action?: ProofReadinessAction | null, actionType?: ProofActionType) => `${String(actionType || 'unknown')}::${String(action?.id || action?.label || '')}`,
    [],
  );

  const getRoutedAction = useCallback((action: ProofReadinessAction, actionType: ProofActionType): RoutedActionResult => {
    const key = getActionKey(action, actionType);
    const cached = routedActionCache.get(key);
    if (cached) return cached;
    const routed = withPreferredActionHref(
      action,
      routeProofAction({
        actionId: String(action?.id || ''),
        actionTitle: String(action?.label || ''),
        actionType,
        context: proofActionRoutingContext,
        capabilities: proofActionRoutingCapabilities,
      }),
    );
    routedActionCache.set(key, routed);
    return routed;
  }, [getActionKey, proofActionRoutingCapabilities, proofActionRoutingContext, routedActionCache]);

  const safetyPrimaryRoutedAction = useMemo(() => {
    const a = safetyActionsForUi[0];
    if (!a) return null;
    return getRoutedAction(a, 'safety_intervention');
  }, [getRoutedAction, safetyActionsForUi]);

  const navigateToOperationalSurface = useCallback((raw?: string, openInNewTab = false) => {
    const target = parseInternalPath(raw);
    if (!target) return;
    if (openInNewTab) {
      window.open(target, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(target);
  }, [navigate]);
  const selectProofSurface = useCallback((surface: ProofSurfaceKey) => {
    userSelectedProofSurfaceRef.current = true;
    setActiveProofSurface(surface);
  }, []);

  const prepareProofPackAction = useCallback(() => {
    setReportKind('proof_pack');
    setReportTask('investigate_incident');
  }, [setReportKind, setReportTask]);

  useEffect(() => {
    if (didSeedDefaults.current) return;
    if (!hasScope) return;
    didSeedDefaults.current = true;
    if (artifactFlowPreset) return;
    if (String(searchParams.get('safety_case_id') || searchParams.get('workflow_id') || '').trim()) return;
    if (!hasAppliedIncidentSelection) return;
    setActiveProofSurface('package');
    if (!proofPackExists && !proofPackGenerationInProgress && !reportError) {
      // Page mount is read-only. Missing artifacts stay Not generated until an explicit build.
    }
  }, [
    _runReportAction,
    artifactFlowPreset,
    hasAppliedIncidentSelection,
    hasScope,
    proofPackExists,
    proofPackGenerationInProgress,
    reportError,
    searchParams,
  ]);

  useEffect(() => {
    if (!explicitPersistedReportId) return;
    if (proofPackExists || proofPackGenerationInProgress || reportLoading) return;
    if (reportKind !== 'proof_pack') {
      setReportKind('proof_pack');
      setReportTask('investigate_incident');
      setActiveProofSurface('package');
      return;
    }
    if (autoOpenedPersistedReportIdRef.current === explicitPersistedReportId) return;
    autoOpenedPersistedReportIdRef.current = explicitPersistedReportId;
    setActiveProofSurface('package');
    void loadPersistedSafetyReportReadOnly(explicitPersistedReportId);
  }, [
    loadPersistedSafetyReportReadOnly,
    explicitPersistedReportId,
    proofPackExists,
    proofPackGenerationInProgress,
    reportKind,
    reportLoading,
    setReportKind,
    setReportTask,
  ]);

  const {
    customerHandoffStatus,
    runCustomerHandoffGeneration,
    downloadCustomerHandoffBundle,
  } = useSupportingPacks({
    canGenerateSupportingPacks,
    scopedOrgId,
    supportingPackSiteId,
    supportingPackWindowStart,
    supportingPackWindowEnd,
    supportingPackScope,
    supportingPackScopeId,
    reliabilityPreviewData: _reliabilityPreviewData,
    downloadBlob,
    describeTrustPackStartError,
  });

  const focusCorrectiveSummarySection = useCallback(() => {
    const node = correctiveSummarySectionRef.current;
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const runCorrectiveSummaryAction = useCallback(() => {
    selectProofSurface('corrective');
    window.setTimeout(() => {
      focusCorrectiveSummarySection();
    }, 0);
    const summaryReady = Boolean(reportData?.ok);
    const summaryCreating = reportKind === 'incidents_rollup' && reportLoading;
    if (!summaryReady && !summaryCreating) {
      void _runReportAction();
    }
  }, [focusCorrectiveSummarySection, reportData, reportKind, reportLoading, selectProofSurface, _runReportAction]);

  const reviewCorrectiveSummaryAction = useCallback(() => {
    selectProofSurface('corrective');
    window.setTimeout(() => {
      focusCorrectiveSummarySection();
    }, 0);
  }, [focusCorrectiveSummarySection, selectProofSurface]);

  const focusEvidenceBundleSection = useCallback(() => {
    const node = evidenceBundleSectionRef.current;
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const focusProofDetailSection = useCallback((sectionRef: React.RefObject<HTMLDivElement | null>) => {
    const node = sectionRef.current;
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const focusCorrectiveActionStatusSection = useCallback(() => {
    focusProofDetailSection(correctiveActionStatusSectionRef);
  }, [focusProofDetailSection]);

  const focusImpactMeasurementStatusSection = useCallback(() => {
    focusProofDetailSection(impactMeasurementStatusSectionRef);
  }, [focusProofDetailSection]);

  const focusReportPackageSection = useCallback(() => {
    focusProofDetailSection(reportPackageSectionRef);
  }, [focusProofDetailSection]);

  const focusFinalSignoffSection = useCallback(() => {
    focusProofDetailSection(finalSignoffSectionRef);
  }, [focusProofDetailSection]);

  const focusReliabilityReviewSection = useCallback(() => {
    const node = reliabilityReviewSectionRef.current;
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const _focusCaseSummarySection = useCallback(() => {
    const node = caseSummarySectionRef.current;
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const _focusCaseActionsSection = useCallback(() => {
    const node = caseActionsSectionRef.current;
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const technicalDetailsPath = useMemo(() => {
    const query = searchParams.toString();
    return query ? `/acceptance/proof/technical?${query}` : '/acceptance/proof/technical';
  }, [searchParams]);

  const technicalDetailsBackPath = useMemo(() => {
    const next = new URLSearchParams(searchParams);
    next.set('surface', 'package');
    next.delete('refresh_evidence_pack');
    const query = next.toString();
    return query ? `/acceptance/proof?${query}` : '/acceptance/proof?surface=package';
  }, [searchParams]);

  const refreshEvidencePackRequested = useMemo(() => {
    const value = String(searchParams.get('refresh_evidence_pack') || '').trim().toLowerCase();
    return value === '1' || value === 'true' || value === 'yes';
  }, [searchParams]);

  const proofFocusTarget = useMemo(
    () => String(searchParams.get('proof_focus') || '').trim().toLowerCase(),
    [searchParams],
  );

  useEffect(() => {
    if (!refreshEvidencePackRequested) return;
    selectProofSurface('package');
    setReportKind('proof_pack');
    setReportTask('investigate_incident');
    const refreshSiteId = String(searchParams.get('site_id') || canonicalProofSiteId || reportSiteId || scopedSiteId).trim();
    const refreshDeviceId = String(searchParams.get('device_id') || canonicalProofDeviceId || '').trim();
    const refreshZoneId = String(searchParams.get('zone_id') || canonicalProofZoneId || '').trim();
    _setReportSiteId(refreshSiteId);
    _setReportDeviceId(refreshDeviceId);
    _setReportZoneId(refreshZoneId);
    _setReportCameraIds([]);
    const refreshFrom = String(searchParams.get('report_from') || reportFrom || '').trim();
    const refreshTo = String(searchParams.get('report_to') || reportTo || '').trim();
    if (refreshFrom) _setReportFrom(refreshFrom);
    if (refreshTo) _setReportTo(refreshTo);
    const next = new URLSearchParams(searchParams);
    next.delete('refresh_evidence_pack');
    next.set('surface', 'package');
    const timer = window.setTimeout(() => {
      setSearchParams(next, { replace: true });
      void _runReportAction({ forceGenerate: true, reportKindOverride: 'proof_pack' });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [
    _runReportAction,
    _setReportCameraIds,
    _setReportDeviceId,
    _setReportFrom,
    _setReportSiteId,
    _setReportTo,
    _setReportZoneId,
    canonicalProofDeviceId,
    canonicalProofSiteId,
    canonicalProofZoneId,
    refreshEvidencePackRequested,
    reportFrom,
    reportSiteId,
    reportTo,
    scopedSiteId,
    searchParams,
    selectProofSurface,
    setReportKind,
    setReportTask,
    setSearchParams,
  ]);

  const runEvidenceBundleAction = useCallback(() => {
    prepareProofPackAction();
    selectProofSurface('package');
    window.setTimeout(() => {
      focusEvidenceBundleSection();
    }, 0);
    if (proofPageState === 'GENERATING' || proofPageState === 'READY') {
      return;
    }
    if (proofPageState === 'GENERATED_WITHOUT_READINESS') {
      void _runReportAction({ forceGenerate: true, reportKindOverride: 'proof_pack' });
      return;
    }
    void _runReportAction({ reportKindOverride: 'proof_pack' });
  }, [focusEvidenceBundleSection, prepareProofPackAction, proofPageState, selectProofSurface, _runReportAction]);

  const markEvidenceBundleReviewed = useCallback(() => {
    if (!evidenceBundleReviewKey) return;
    writeEvidenceBundleReviewed(evidenceBundleReviewKey);
    setEvidenceBundleReviewed(true);
  }, [evidenceBundleReviewKey]);

  const reviewEvidenceBundleAction = useCallback(() => {
    prepareProofPackAction();
    selectProofSurface('package');
    markEvidenceBundleReviewed();
    window.setTimeout(() => {
      focusEvidenceBundleSection();
    }, 0);
  }, [focusEvidenceBundleSection, markEvidenceBundleReviewed, prepareProofPackAction, selectProofSurface]);

  const navigateToSignedSafetyScan = useCallback((reportId: string, revision?: unknown) => {
    const reportKey = String(reportId || '').trim();
    if (!reportKey) return;
    const workflowAnchor = proofPackPreviewData?.persisted_report?.workflow_anchor || null;
    const scope = workflowAnchor?.scope || null;
    const timeWindow = workflowAnchor?.time_window || null;
    const scopeZones = scope?.zones;
    const zones = Array.isArray(scopeZones) ? scopeZones : [];
    const anchorIncidentId = String(
      workflowAnchor?.representative_incident_id
      || searchParams.get('incident_id')
      || searchParams.get('case_id')
      || '',
    ).trim();
    const next = resolvePostFinalizeNavigation({
      reportId: reportKey,
      revision,
      searchParams,
      artifactArgs: {
        reportId: reportKey,
        orgId: searchParams.get('org_id'),
        siteId: String(scope?.site_id || searchParams.get('site_id') || '').trim() || undefined,
        workflowId: String(
          proofPackPreviewData?.persisted_report?.workflow_id
          || workflowAnchor?.workflow_id
          || searchParams.get('workflow_id')
          || '',
        ).trim() || undefined,
        deviceId: String(scope?.device_id || searchParams.get('device_id') || '').trim() || undefined,
        zoneId: String(zones[0] || searchParams.get('zone_id') || '').trim() || undefined,
        cameraId: String(scope?.camera_id || searchParams.get('camera_id') || '').trim() || undefined,
        reportFrom: String(timeWindow?.start || timeWindow?.from || searchParams.get('report_from') || '').trim() || undefined,
        reportTo: String(timeWindow?.end || timeWindow?.to || searchParams.get('report_to') || '').trim() || undefined,
        caseId: anchorIncidentId || undefined,
        incidentId: anchorIncidentId || undefined,
        policyType: String(scope?.risk_type || searchParams.get('policy_type') || '').trim() || undefined,
        policyName: String(searchParams.get('policy_name') || '').trim() || undefined,
      },
    });
    _setReportNotice(next.notice);
    navigate(next.path, { replace: true });
  }, [_setReportNotice, navigate, proofPackPreviewData, searchParams]);

  const finalizeSafetyScanAction = useCallback(async () => {
    if (finalizedPersistedReport || safetyScanFinalizing) return;
    const reportId = persistedDraftReportId;
    if (!reportId) {
      _setReportError('This Draft Safety Scan is missing a persisted report id. Reopen it from the linked Safety Case and try again.');
      return;
    }
    if (measurementGateAdmitted !== true) {
      _setReportError('A frozen detection check must be recorded before signing this Safety Scan.');
      window.setTimeout(() => {
        measurementAssuranceSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
      return;
    }
    if (fieldVerificationAllowed !== true) {
      _setReportError(fieldVerificationLabel || 'Control confirmation on site required');
      return;
    }
    setSafetyScanFinalizing(true);
    _setReportError(null);
    _setReportNotice(null);
    try {
      const response = await reportsApi.finalizeSafetyReport(reportId);
      if (!response?.ok) {
        const blockerSummary = Array.isArray(response?.blockers)
          ? response.blockers
            .map((blocker) => String(blocker?.message || blocker?.code || '').trim())
            .filter(Boolean)
            .join('; ')
          : '';
        throw new Error(
          blockerSummary
          || (String(response?.error || '').trim() === 'report_finalize_blocked'
            ? 'Safety Scan sign-off was blocked by a server check. Review Status details for the remaining item.'
            : String(response?.error || '').trim())
          || 'Safety Scan sign-off was blocked.',
        );
      }
      _setReportNotice(null);
      const signedRevision = (response as { report?: { report_revision?: unknown; revision?: unknown; current_revision?: unknown } })?.report;
      navigateToSignedSafetyScan(
        reportId,
        signedRevision?.revision ?? signedRevision?.report_revision ?? signedRevision?.current_revision,
      );
    } catch (err: any) {
      _setReportError(
        err?.response?.data?.error
        || err?.response?.data?.blockers?.map?.((blocker: any) => blocker?.message || blocker?.code)?.filter(Boolean)?.join('; ')
        || err?.message
        || 'Safety Scan sign-off failed.',
      );
    } finally {
      setSafetyScanFinalizing(false);
    }
  }, [
    _setReportError,
    _setReportNotice,
    finalizedPersistedReport,
    measurementGateAdmitted,
    fieldVerificationAllowed,
    fieldVerificationLabel,
    navigateToSignedSafetyScan,
    persistedDraftReportId,
    safetyScanFinalizing,
  ]);

  const focusMeasurementAssuranceSection = useCallback(() => {
    window.setTimeout(() => {
      const target = measurementAssuranceSectionRef.current
        || document.querySelector('[data-testid="draft-safety-scan-setup-state"]');
      if (target && 'scrollIntoView' in target) {
        (target as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  }, []);

  useEffect(() => {
    setMeasurementGateAdmitted(null);
    setFieldVerificationAllowed(null);
    setFieldVerificationLabel(null);
  }, [persistedDraftReportId]);

  const reviewTrustFindingsAction = useCallback(() => {
    selectProofSurface('reliability');
    window.setTimeout(() => {
      focusReliabilityReviewSection();
    }, 0);
  }, [focusReliabilityReviewSection, selectProofSurface]);

  const _reviewEvidenceSettingsAction = useCallback(() => {
    navigate(technicalDetailsPath);
  }, [navigate, technicalDetailsPath]);

  const incidentReviewReturnHref = useMemo(
    () => buildIncidentReviewReturnHref({
      reportZoneId: canonicalProofZoneId || reportZoneId,
      filterZoneId,
      reportDeviceId: canonicalProofDeviceId || reportDeviceId,
      filterDeviceId,
      reportCameraIds,
      filterCameraId,
      reportPolicyType: canonicalProofIncidentType || reportPolicyType,
      fromDate: canonicalProofWindowStart,
      toDate: canonicalProofWindowEnd,
      reportFrom: canonicalProofWindowStart || reportFrom,
      reportTo: canonicalProofWindowEnd || reportTo,
      reportId: persistedDraftReportId,
      safetyCaseId: handoffParams.safetyCaseId || searchParams.get('safety_case_id'),
      workflowId: handoffParams.workflowId || searchParams.get('workflow_id'),
      tableMode: 'grouped',
    }),
    [
      canonicalProofDeviceId,
      canonicalProofIncidentType,
      canonicalProofWindowEnd,
      canonicalProofWindowStart,
      canonicalProofZoneId,
      filterCameraId,
      filterDeviceId,
      filterZoneId,
      handoffParams.safetyCaseId,
      handoffParams.workflowId,
      persistedDraftReportId,
      reportCameraIds,
      reportDeviceId,
      reportFrom,
      reportPolicyType,
      reportTo,
      reportZoneId,
      searchParams,
    ],
  );

  const configurationHandoffHref = useMemo(
    () => buildConfigurationHandoffHref({
      reportZoneId: canonicalProofZoneId || reportZoneId,
      filterZoneId,
      reportCameraIds,
      filterDeviceId,
      reportDeviceId: canonicalProofDeviceId || reportDeviceId,
      filterCameraId,
      reportPolicyType: canonicalProofIncidentType || reportPolicyType,
      reportPolicyIds,
    }),
    [
      canonicalProofDeviceId,
      canonicalProofIncidentType,
      canonicalProofZoneId,
      filterCameraId,
      filterDeviceId,
      filterZoneId,
      reportCameraIds,
      reportDeviceId,
      reportPolicyIds,
      reportPolicyType,
      reportZoneId,
    ],
  );

  const handleRoutedCtaClick = useCallback((cta: RoutedCta) => {
    if (cta.targetSurface === 'followup_review') {
      selectProofSurface('corrective');
      setReportKind('proof_pack');
      setReportTask('investigate_incident');
      return;
    }
    if (cta.targetSurface === 'evidence_package') {
      navigate(technicalDetailsPath);
      return;
    }
    if (cta.href) {
      navigateToOperationalSurface(cta.href);
      return;
    }
    if (cta.navigationTarget) {
      navigateToOperationalSurface(cta.navigationTarget);
    }
  }, [navigate, navigateToOperationalSurface, selectProofSurface, setReportKind, setReportTask, technicalDetailsPath]);

  const openCorrectiveActionSurface = useCallback(() => {
    selectProofSurface('corrective');
  }, [selectProofSurface]);

  const {
    reviewScopeIncidents,
    reviewScopeIncidentsLoading,
    reviewOutcomeRollup,
    reviewOutcomeRollupLoading,
    reviewOutcomeHistory,
    reviewOutcomeHistoryLoading,
    ownershipRollup,
    ownershipRollupLoading,
    correctiveActionRollup,
    correctiveActionRollupLoading,
    measurementRollup,
    measurementRollupLoading,
    actionSnapshotRollup,
    actionSnapshotRollupLoading,
    escalationRollup,
    escalationRollupLoading,
  } = useAcceptanceProofRollups({
    hasScope,
    scopedOrgId,
    scopedSiteId,
    canonicalProofSiteId,
    canonicalProofWindowStart,
    canonicalProofWindowEnd,
    canonicalProofIncidentType,
    reportPolicyType,
    canonicalProofZoneId,
    reportZoneId,
    canonicalProofDeviceId,
    reportDeviceId,
  });
  const [selectedReviewIncidentIds, setSelectedReviewIncidentIds] = useState<string[]>([]);
  const [reviewRecordPolicyFilter, setReviewRecordPolicyFilter] = useState('');
  const [reviewRecordCameraFilter, setReviewRecordCameraFilter] = useState('');

  const [proofScopeZoneDraft, setProofScopeZoneDraft] = useState('');
  const [proofScopePolicyTypeDraft, setProofScopePolicyTypeDraft] = useState('');
  const [proofScopeFromDraft, setProofScopeFromDraft] = useState('');
  const [proofScopeToDraft, setProofScopeToDraft] = useState('');
  const [proofScopeCaseDraft, setProofScopeCaseDraft] = useState('');
  const proofScopeWindowDraftEditedRef = useRef(false);
  const proofScopeDraftSyncKeyRef = useRef('');
  const lastDerivedProofScopeAnchorRef = useRef('');

  useEffect(() => {
    if (!handoffParams.isUpdatedScan || proofScopeWindowDraftEditedRef.current) return;
    const fromIso = normalizeScopeWindowValue(
      handoffParams.scanScopeStart
      || handoffParams.newActivityWindowStart
      || searchParams.get('scan_scope_start')
      || searchParams.get('report_from')
      || '',
    );
    const toIso = normalizeScopeWindowValue(
      handoffParams.scanScopeEnd
      || searchParams.get('scan_scope_end')
      || searchParams.get('report_to')
      || '',
    );
    if (fromIso) setProofScopeFromDraft(formatSiteDateTimeLocalInput(fromIso, siteTimeZone));
    if (toIso) setProofScopeToDraft(formatSiteDateTimeLocalInput(toIso, siteTimeZone));
  }, [
    handoffParams.isUpdatedScan,
    handoffParams.newActivityWindowStart,
    handoffParams.scanScopeEnd,
    handoffParams.scanScopeStart,
    searchParams,
    siteTimeZone,
  ]);

  const selectedIncidentReviewHref = useMemo(() => {
    const incidentId = String(
      selectedReviewIncidentIds[0]
      || currentProofScopeCaseId
      || explicitAnchorIncidentId
      || '',
    ).trim();
    if (!incidentId || incidentId === persistedDraftReportId) return '';
    return buildIncidentReviewReturnHref({
      reportZoneId: canonicalProofZoneId || reportZoneId,
      filterZoneId,
      reportDeviceId: canonicalProofDeviceId || reportDeviceId,
      filterDeviceId,
      reportCameraIds,
      filterCameraId,
      reportPolicyType: canonicalProofIncidentType || reportPolicyType,
      fromDate: canonicalProofWindowStart,
      toDate: canonicalProofWindowEnd,
      reportFrom: canonicalProofWindowStart || reportFrom,
      reportTo: canonicalProofWindowEnd || reportTo,
      reportId: persistedDraftReportId,
      safetyCaseId: handoffParams.safetyCaseId || searchParams.get('safety_case_id'),
      workflowId: handoffParams.workflowId || searchParams.get('workflow_id'),
      incidentId,
      tableMode: 'grouped',
    });
  }, [
    canonicalProofDeviceId,
    canonicalProofIncidentType,
    canonicalProofWindowEnd,
    canonicalProofWindowStart,
    canonicalProofZoneId,
    currentProofScopeCaseId,
    explicitAnchorIncidentId,
    filterCameraId,
    filterDeviceId,
    filterZoneId,
    handoffParams.safetyCaseId,
    handoffParams.workflowId,
    persistedDraftReportId,
    reportCameraIds,
    reportDeviceId,
    reportFrom,
    reportPolicyType,
    reportTo,
    reportZoneId,
    searchParams,
    selectedReviewIncidentIds,
  ]);

  const hasMinimumReportPackageScope = useMemo(() => {
    if (!hasScope) return false;
    const fromIso = normalizeScopeWindowValue(
      proofScopeFromDraft
      || reportFrom
      || canonicalProofWindowStart
      || searchParams.get('report_from')
      || '',
    );
    const toIso = normalizeScopeWindowValue(
      proofScopeToDraft
      || reportTo
      || canonicalProofWindowEnd
      || searchParams.get('report_to')
      || '',
    );
    return Boolean(fromIso && toIso);
  }, [
    canonicalProofWindowEnd,
    canonicalProofWindowStart,
    hasScope,
    proofScopeFromDraft,
    proofScopeToDraft,
    reportFrom,
    reportTo,
    searchParams,
  ]);

  const canOperateOnProofPackage = useMemo(
    () => Boolean(
      String(currentProofScopeCaseId || explicitAnchorIncidentId || canonicalProofReportId || '').trim(),
    ) || hasAppliedIncidentSelection || proofPackExists || hasMinimumReportPackageScope,
    [
      canonicalProofReportId,
      currentProofScopeCaseId,
      explicitAnchorIncidentId,
      hasAppliedIncidentSelection,
      hasMinimumReportPackageScope,
      proofPackExists,
    ],
  );

  useEffect(() => {
    setProofScopeZoneDraft(resolveCanonicalZoneDraft({
      reportZoneId,
      canonicalZoneId: canonicalProofZoneId,
      persistedReport: proofPackPreviewData?.persisted_report,
      workflowId: handoffParams.workflowId || searchParams.get('workflow_id'),
    }));
  }, [canonicalProofZoneId, handoffParams.workflowId, proofPackPreviewData?.persisted_report, reportZoneId, searchParams]);

  useEffect(() => {
    const cameraId = resolveCanonicalCameraDraft({
      cameraFilter: reviewRecordCameraFilter,
      persistedReport: proofPackPreviewData?.persisted_report,
      workflowId: handoffParams.workflowId || searchParams.get('workflow_id'),
      urlCameraId: searchParams.get('camera_id'),
      reportCameraIds,
    });
    if (cameraId && cameraId !== reviewRecordCameraFilter) setReviewRecordCameraFilter(cameraId);
  }, [
    handoffParams.workflowId,
    proofPackPreviewData?.persisted_report,
    reportCameraIds,
    reviewRecordCameraFilter,
    searchParams,
  ]);

  useEffect(() => {
    const raw = firstDefinedString(
      reportPolicyType,
      canonicalProofIncidentType,
      explicitProofPolicyId,
    ) || '';
    const normalized = normalizeProofScopePolicyType(raw, _allPolicies);
    setProofScopePolicyTypeDraft(normalized);
  }, [_allPolicies, canonicalProofIncidentType, explicitProofPolicyId, reportPolicyType]);

  const proofScopeServerSyncKey = useMemo(
    () => [
      currentProofScopeCaseId,
      reportZoneId,
      reportPolicyType,
      reportFrom,
      reportTo,
      canonicalProofWindowStart,
      canonicalProofWindowEnd,
      explicitAnchorIncidentId,
    ].join('|'),
    [
      canonicalProofWindowEnd,
      canonicalProofWindowStart,
      currentProofScopeCaseId,
      explicitAnchorIncidentId,
      reportFrom,
      reportPolicyType,
      reportTo,
      reportZoneId,
    ],
  );

  const appliedProofScopeFromLocal = useMemo(
    () => formatSiteDateTimeLocalInput(String(reportFrom || canonicalProofWindowStart || '').trim(), siteTimeZone),
    [canonicalProofWindowStart, reportFrom, siteTimeZone],
  );

  const appliedProofScopeToLocal = useMemo(
    () => formatSiteDateTimeLocalInput(String(reportTo || canonicalProofWindowEnd || '').trim(), siteTimeZone),
    [canonicalProofWindowEnd, reportTo, siteTimeZone],
  );

  useEffect(() => {
    if (proofScopeDraftSyncKeyRef.current === proofScopeServerSyncKey) {
      if (!proofScopeWindowDraftEditedRef.current) {
        if (
          handoffParams.isUpdatedScan
          && (handoffParams.scanScopeStart || handoffParams.scanScopeEnd)
        ) {
          const fromIso = normalizeScopeWindowValue(
            handoffParams.scanScopeStart
            || searchParams.get('scan_scope_start')
            || '',
          );
          const toIso = normalizeScopeWindowValue(
            handoffParams.scanScopeEnd
            || searchParams.get('scan_scope_end')
            || '',
          );
          if (fromIso) setProofScopeFromDraft(formatSiteDateTimeLocalInput(fromIso, siteTimeZone));
          if (toIso) setProofScopeToDraft(formatSiteDateTimeLocalInput(toIso, siteTimeZone));
        } else {
          setProofScopeFromDraft(appliedProofScopeFromLocal);
          setProofScopeToDraft(appliedProofScopeToLocal);
        }
      }
      return;
    }
    proofScopeDraftSyncKeyRef.current = proofScopeServerSyncKey;
    proofScopeWindowDraftEditedRef.current = false;
    if (
      handoffParams.isUpdatedScan
      && (handoffParams.scanScopeStart || handoffParams.scanScopeEnd)
    ) {
      const fromIso = normalizeScopeWindowValue(
        handoffParams.scanScopeStart
        || searchParams.get('scan_scope_start')
        || '',
      );
      const toIso = normalizeScopeWindowValue(
        handoffParams.scanScopeEnd
        || searchParams.get('scan_scope_end')
        || '',
      );
      if (fromIso) setProofScopeFromDraft(formatSiteDateTimeLocalInput(fromIso, siteTimeZone));
      if (toIso) setProofScopeToDraft(formatSiteDateTimeLocalInput(toIso, siteTimeZone));
      return;
    }
    setProofScopeFromDraft(appliedProofScopeFromLocal);
    setProofScopeToDraft(appliedProofScopeToLocal);
  }, [
    appliedProofScopeFromLocal,
    appliedProofScopeToLocal,
    handoffParams.isUpdatedScan,
    handoffParams.scanScopeEnd,
    handoffParams.scanScopeStart,
    proofScopeServerSyncKey,
    searchParams,
    siteTimeZone,
  ]);

  useEffect(() => {
    setProofScopeCaseDraft(currentProofScopeCaseId);
  }, [currentProofScopeCaseId]);

  useEffect(() => {
    if (hasCanonicalPersistedWindow({
      persistedReport: proofPackPreviewData?.persisted_report,
      queueWindow: observationFunnelQueue.window,
    })) return;
    const urlFrom = String(searchParams.get('report_from') || '').trim();
    const urlTo = String(searchParams.get('report_to') || '').trim();
    if (urlFrom && urlFrom !== String(reportFrom || '').trim()) _setReportFrom(urlFrom);
    if (urlTo && urlTo !== String(reportTo || '').trim()) _setReportTo(urlTo);
  }, [_setReportFrom, _setReportTo, observationFunnelQueue.window, proofPackPreviewData?.persisted_report, reportFrom, reportTo, searchParams]);

  const handleProofScopeFromDraftChange = useCallback((value: string) => {
    proofScopeWindowDraftEditedRef.current = true;
    setProofScopeFromDraft(value);
  }, []);

  const handleProofScopeToDraftChange = useCallback((value: string) => {
    proofScopeWindowDraftEditedRef.current = true;
    setProofScopeToDraft(value);
  }, []);

  const applyProofScopeSelection = useCallback(() => {
    const persistedReport = proofPackPreviewData?.persisted_report;
    const planned = planCanonicalProofScopeApply({
      persistedReport,
      workflowId: persistedReport?.workflow_id
        || persistedReport?.workflow_anchor?.workflow_id
        || handoffParams.workflowId
        || searchParams.get('workflow_id'),
      reportId: persistedDraftReportId,
      zoneDraft: proofScopeZoneDraft,
      policyDraft: proofScopePolicyTypeDraft,
      cameraDraft: reviewRecordCameraFilter
        || (Array.isArray(reportCameraIds) ? reportCameraIds[0] : '')
        || searchParams.get('camera_id'),
      windowStartDraft: proofScopeFromDraft,
      windowEndDraft: proofScopeToDraft,
      windowEdited: proofScopeWindowDraftEditedRef.current,
      siteTimeZone,
      urlCameraId: searchParams.get('camera_id'),
      searchParams,
      selectedReviewIncidentIds,
      proofScopeCaseDraft,
      safetyReportTypeMode,
      hazardClassKey,
    });
    if (!planned.canApply) return;
    const { resolved } = planned;
    _setReportZoneId(resolved.zoneId);
    _setReportPolicyType(resolved.policyType);
    _setReportPolicyIds([]);
    _setReportCameraIds([resolved.cameraId]);
    setReviewRecordCameraFilter(resolved.cameraId);
    if (resolved.windowStart) _setReportFrom(resolved.windowStart);
    if (resolved.windowEnd) _setReportTo(resolved.windowEnd);
    proofScopeWindowDraftEditedRef.current = false;
    if (planned.fromLocal) setProofScopeFromDraft(planned.fromLocal);
    if (planned.toLocal) setProofScopeToDraft(planned.toLocal);
    if (resolved.zoneId) setProofScopeZoneDraft(resolved.zoneId);
    if (resolved.policyType) setProofScopePolicyTypeDraft(resolved.policyType);
    setReportKind('proof_pack');
    setReportTask('investigate_incident');
    selectProofSurface('package');
    setSearchParams(planned.nextSearchParams, { replace: true });
    window.setTimeout(() => {
      void _runReportAction({ forceGenerate: true, reportKindOverride: 'proof_pack' });
    }, 0);
  }, [
    _runReportAction,
    _setReportCameraIds,
    _setReportFrom,
    _setReportPolicyIds,
    _setReportPolicyType,
    _setReportTo,
    _setReportZoneId,
    handoffParams.workflowId,
    hazardClassKey,
    persistedDraftReportId,
    proofPackPreviewData?.persisted_report,
    proofScopeCaseDraft,
    proofScopeFromDraft,
    proofScopePolicyTypeDraft,
    proofScopeToDraft,
    proofScopeZoneDraft,
    reportCameraIds,
    reviewRecordCameraFilter,
    safetyReportTypeMode,
    searchParams,
    selectProofSurface,
    selectedReviewIncidentIds,
    setReportKind,
    setReportTask,
    setSearchParams,
    siteTimeZone,
  ]);

  const openIncidentEvidencePackageSurface = useCallback(() => {
    prepareProofPackAction();
    selectProofSurface('package');
    if (!canOperateOnProofPackage) {
      return;
    }
    if (proofPageState === 'READY') {
      markEvidenceBundleReviewed();
    }
    if (!proofPackExists || proofPageState === 'ERROR') {
      void _runReportAction({ reportKindOverride: 'proof_pack' });
      return;
    }
    if (proofPageState === 'GENERATED_WITHOUT_READINESS') {
      void _runReportAction({ forceGenerate: true, reportKindOverride: 'proof_pack' });
    }
  }, [_runReportAction, canOperateOnProofPackage, markEvidenceBundleReviewed, prepareProofPackAction, proofPackExists, proofPageState, selectProofSurface]);

  const syncReportWindowBeforePackageAction = useCallback(() => {
    const draftFromIso = normalizeScopeWindowValue(proofScopeFromDraft);
    const draftToIso = normalizeScopeWindowValue(proofScopeToDraft);
    const canonicalFromIso = normalizeScopeWindowValue(canonicalProofWindowStart);
    const canonicalToIso = normalizeScopeWindowValue(canonicalProofWindowEnd);
    const { fromIso: stateFromIso, toIso: stateToIso } = resolveReportWindowIso(
      reportFrom,
      reportTo,
      searchParams,
    );
    const fromIso = stateFromIso || draftFromIso || canonicalFromIso;
    const toIso = stateToIso || draftToIso || canonicalToIso;
    if (fromIso) _setReportFrom(fromIso);
    if (toIso) _setReportTo(toIso);
    const next = new URLSearchParams(searchParams);
    if (fromIso) next.set('report_from', fromIso);
    else next.delete('report_from');
    if (toIso) next.set('report_to', toIso);
    else next.delete('report_to');
    setSearchParams(next, { replace: true });
    return { fromIso, toIso };
  }, [
    _setReportFrom,
    _setReportTo,
    canonicalProofWindowEnd,
    canonicalProofWindowStart,
    proofScopeFromDraft,
    proofScopeToDraft,
    reportFrom,
    reportTo,
    searchParams,
    setSearchParams,
  ]);

  const supportsDualCustomerPdfFaces = useMemo(
    () => usesCompactBuyerFace({
      riskType: canonicalProofIncidentType || reportPolicyType,
      policyType: reportPolicyType,
      persistedReport: (proofPackPreviewData?.persisted_report as Record<string, unknown> | null) || null,
    }),
    [
      canonicalProofIncidentType,
      proofPackPreviewData?.persisted_report,
      reportPolicyType,
    ],
  );

  const primaryIncidentEvidencePackageAction = useCallback(() => {
    if (persistedDraftReportId && workflowLinkedDraftModeEligible) {
      void downloadPersistedSafetyScanPdf(
        persistedDraftReportId,
        supportsDualCustomerPdfFaces ? { face: 'customer_compact' } : undefined,
      );
      return;
    }
    prepareProofPackAction();
    selectProofSurface('package');
    if (!canOperateOnProofPackage) {
      _setReportError(SAFETY_REPORT_BUILD_SCOPE_HELPER);
      return;
    }
    const { fromIso, toIso } = syncReportWindowBeforePackageAction();
    if (!fromIso || !toIso) {
      _setReportError(
        'Set both the report start and end time in Report scope, then click Update Report (or Regenerate ZIP Package).',
      );
      return;
    }
    const shouldRebuild =
      proofPageState === 'ERROR'
      || proofPageState === 'GENERATED_WITHOUT_READINESS';
    if (!shouldRebuild && proofPackExists && proofPageState !== 'GENERATING') {
      if (proofPageState === 'READY') {
        markEvidenceBundleReviewed();
      }
      void _downloadProofPackAction();
      return;
    }
    _setReportError(null);
    _setReportNotice(
      resolveReportBuildingNotice(safetyReportTypeMode, hazardClassKey),
    );
    void _runReportAction({
      forceGenerate: true,
      reportKindOverride: 'proof_pack',
      windowOverride: { fromIso, toIso },
    });
  }, [
    _downloadProofPackAction,
    _runReportAction,
    _setReportError,
    _setReportNotice,
    canOperateOnProofPackage,
    downloadPersistedSafetyScanPdf,
    markEvidenceBundleReviewed,
    persistedDraftReportId,
    prepareProofPackAction,
    proofPackExists,
    proofPageState,
    hazardClassKey,
    safetyReportTypeMode,
    selectProofSurface,
    supportsDualCustomerPdfFaces,
    syncReportWindowBeforePackageAction,
    workflowLinkedDraftModeEligible,
  ]);

  const detailedSignedArtifactPdfAction = useCallback(() => {
    if (!persistedDraftReportId || !workflowLinkedDraftModeEligible) return;
    void downloadPersistedSafetyScanPdf(persistedDraftReportId, { face: 'detailed' });
  }, [
    downloadPersistedSafetyScanPdf,
    persistedDraftReportId,
    workflowLinkedDraftModeEligible,
  ]);

  const secondaryIncidentEvidencePackageAction = useCallback(() => {
    if (persistedDraftReportId && workflowLinkedDraftModeEligible) {
      void downloadPersistedSafetyScanPackage(persistedDraftReportId);
      return;
    }
    primaryIncidentEvidencePackageAction();
  }, [
    downloadPersistedSafetyScanPackage,
    persistedDraftReportId,
    primaryIncidentEvidencePackageAction,
    workflowLinkedDraftModeEligible,
  ]);

  const _openReliabilityDependencyPackageView = useCallback(() => {
    selectProofSurface('package');
  }, [selectProofSurface]);

  const openReliabilityReviewSurface = useCallback(() => {
    selectProofSurface('reliability');
    if (!hasReadinessDecision) {
      if (!proofPackExists || proofPageState === 'GENERATED_WITHOUT_READINESS' || proofPageState === 'ERROR') {
        void _runReportAction({
          forceGenerate: proofPageState === 'GENERATED_WITHOUT_READINESS',
          reportKindOverride: 'proof_pack',
        });
      }
      return;
    }
  }, [_runReportAction, hasReadinessDecision, proofPackExists, proofPageState, selectProofSurface]);

  const _renderInlineCtaStrip = useCallback((
    actionId: string,
    actionType: ProofActionType,
    routed: RoutedActionResult,
  ) => {
    const ctas = [routed.primaryCta, ...routed.secondaryCtas].filter(Boolean) as RoutedCta[];
    if (!ctas.length) return null;
    return (
      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
        {ctas.map((cta, index) => {
          const rank = index === 0 ? 'primary' : index === 1 ? 'secondary_1' : 'secondary_2';
          return (
            <Button
              key={`${actionId}:${cta.targetSurface}:${rank}`}
              size="small"
              variant={index === 0 ? 'contained' : 'outlined'}
              onClick={() => {
                emitSafetyPatternActionCtaEvent('safety_pattern_action_cta_clicked', {
                  actionId,
                  actionType,
                  matchedRule: routed.matchedRule,
                  routingReason: routed.routingReason,
                  ctaSurface: cta.targetSurface,
                  ctaRank: rank,
                  siteId: proofActionRoutingContext.siteId || '',
                  hotspot: proofActionRoutingContext.hotspot || '',
                  dominantIncidentType: proofActionRoutingContext.dominantIncidentType || '',
                  evidenceConfidence: proofActionRoutingContext.evidenceConfidence || '',
                });
                handleRoutedCtaClick(cta);
              }}
            >
              {cta.label}
            </Button>
          );
        })}
      </Stack>
    );
  }, [handleRoutedCtaClick, proofActionRoutingContext]);

  const heroRoutedAction = useMemo(() => {
    if (!pedestrianZoneHero) return null;
    const action: ProofReadinessAction = {
      id: 'review_pedestrian_zone_controls',
      label: `Review ${pedestrianZoneHero.controlLabel} in the ${pedestrianZoneHero.areaLabel}`,
      reason: 'Repeated danger-zone entries suggest a recurring pedestrian-flow or zone-control issue.',
      href: pedestrianZoneHero.href,
    };
    return getRoutedAction(action, 'safety_intervention');
  }, [getRoutedAction, pedestrianZoneHero]);

  const measurementRoutedAction = useMemo(
    () => routeProofAction({
      actionId: 'create_followup_review',
      actionTitle: 'Create follow-up review',
      actionType: 'measurement',
      context: proofActionRoutingContext,
      capabilities: proofActionRoutingCapabilities,
    }),
    [proofActionRoutingCapabilities, proofActionRoutingContext],
  );

  const heroShownRef = useRef(false);
  useEffect(() => {
    if (heroShownRef.current || !heroRoutedAction) return;
    const ctas = [heroRoutedAction.primaryCta, ...heroRoutedAction.secondaryCtas].filter(Boolean) as RoutedCta[];
    if (!ctas.length) return;
    ctas.forEach((cta, index) => {
      emitSafetyPatternActionCtaEvent('safety_pattern_action_cta_shown', {
        actionId: 'review_pedestrian_zone_controls',
        actionType: 'safety_intervention',
        matchedRule: heroRoutedAction.matchedRule,
        routingReason: heroRoutedAction.routingReason,
        ctaSurface: cta.targetSurface,
        ctaRank: index === 0 ? 'primary' : index === 1 ? 'secondary_1' : 'secondary_2',
        siteId: proofActionRoutingContext.siteId || '',
        hotspot: proofActionRoutingContext.hotspot || '',
        dominantIncidentType: proofActionRoutingContext.dominantIncidentType || '',
        evidenceConfidence: proofActionRoutingContext.evidenceConfidence || '',
      });
    });
    heroShownRef.current = true;
  }, [heroRoutedAction, proofActionRoutingContext]);

  const measurementShownRef = useRef(false);
  useEffect(() => {
    if (measurementShownRef.current) return;
    const ctas = [measurementRoutedAction.primaryCta, ...measurementRoutedAction.secondaryCtas].filter(Boolean) as RoutedCta[];
    if (!ctas.length) return;
    ctas.forEach((cta, index) => {
      emitSafetyPatternActionCtaEvent('safety_pattern_action_cta_shown', {
        actionId: 'create_followup_review',
        actionType: 'measurement',
        matchedRule: measurementRoutedAction.matchedRule,
        routingReason: measurementRoutedAction.routingReason,
        ctaSurface: cta.targetSurface,
        ctaRank: index === 0 ? 'primary' : index === 1 ? 'secondary_1' : 'secondary_2',
        siteId: proofActionRoutingContext.siteId || '',
        hotspot: proofActionRoutingContext.hotspot || '',
        dominantIncidentType: proofActionRoutingContext.dominantIncidentType || '',
        evidenceConfidence: proofActionRoutingContext.evidenceConfidence || '',
      });
    });
    measurementShownRef.current = true;
  }, [measurementRoutedAction, proofActionRoutingContext]);

  const findCtaBySurface = useCallback((routed: RoutedActionResult | null, surface: RoutedCta['targetSurface']): RoutedCta | null => {
    if (!routed) return null;
    const ctas = [routed.primaryCta, ...routed.secondaryCtas].filter(Boolean) as RoutedCta[];
    return ctas.find((cta) => cta.targetSurface === surface) || null;
  }, []);

  const operationalRunbookCta = useMemo(
    () => findCtaBySurface(heroRoutedAction, 'runbook') || findCtaBySurface(safetyPrimaryRoutedAction, 'runbook'),
    [findCtaBySurface, heroRoutedAction, safetyPrimaryRoutedAction],
  );

  const operationalCopilotCta = useMemo(
    () => findCtaBySurface(heroRoutedAction, 'copilot') || findCtaBySurface(safetyPrimaryRoutedAction, 'copilot'),
    [findCtaBySurface, heroRoutedAction, safetyPrimaryRoutedAction],
  );

  const operationalIncidentsCta = useMemo(
    () => findCtaBySurface(heroRoutedAction, 'incidents') || findCtaBySurface(safetyPrimaryRoutedAction, 'incidents'),
    [findCtaBySurface, heroRoutedAction, safetyPrimaryRoutedAction],
  );

  const operationalFollowupReviewCta = useMemo(
    () => findCtaBySurface(measurementRoutedAction, 'followup_review'),
    [findCtaBySurface, measurementRoutedAction],
  );

  const operationalFollowThroughModel = useMemo(() => {
    return buildOperationalFollowThrough({
      runbookStatus: 'idle',
      copilotStatus: 'none',
      runbookTitle: operationalRunbookCta?.label || null,
      completedChannels: [],
      followupReviewStatus: 'none',
      measured: false,
      actionTargets: {
        runbookLabel: operationalRunbookCta?.label || null,
        copilotLabel: operationalCopilotCta?.label || null,
        incidentsLabel: null,
        followupReviewLabel: operationalFollowupReviewCta?.label || null,
        measurementLabel: null,
      },
    });
  }, [
    operationalCopilotCta?.label,
    operationalFollowupReviewCta?.label,
    operationalRunbookCta?.label,
  ]);

  const actionRecorded =
    operationalFollowThroughModel.stage !== 'not_started' && operationalFollowThroughModel.stage !== 'copilot_opened';
  const outcomeRecorded =
    operationalFollowThroughModel.stage === 'followup_scheduled' || operationalFollowThroughModel.stage === 'measured';
  const customerHandoffReady = hasReadinessDecision && actionRecorded;

  const _proofJourneySteps = useMemo<ProofJourneyStepProps[]>(
    () => [
      {
        title: 'Detection',
        statusTone: 'Done',
        statusLabel: 'Done',
        detail: 'The case is scoped and the incident story is visible on this page.',
      },
      {
        title: 'Evidence pack',
        statusTone: proofPageState === 'READY'
          ? 'Done'
          : proofPageState === 'GENERATING'
            ? 'In progress'
            : proofPageState === 'GENERATED_WITHOUT_READINESS'
              ? 'Needs review'
              : proofPageState === 'ERROR'
                ? 'Needs review'
              : 'Needs review',
        statusLabel: proofPageState === 'READY'
          ? 'Done'
          : proofPageState === 'GENERATING'
            ? 'In progress'
            : proofPageState === 'GENERATED_WITHOUT_READINESS'
              ? 'Needs refresh'
              : proofPageState === 'ERROR'
                ? 'Failed'
                : 'Not started',
        detail: proofPageState === 'READY'
          ? 'The Scan Package is ready and supports review.'
          : proofPageState === 'GENERATING'
            ? 'The Scan Package is being assembled now.'
            : proofPageState === 'GENERATED_WITHOUT_READINESS'
              ? 'The Scan Package exists, but it is missing trust metrics and needs regeneration.'
              : proofPageState === 'ERROR'
                ? (reportError || 'The Scan Package failed and should be retried for this case.')
                : 'The Scan Package has not been started yet.',
      },
      {
        title: 'Trust / reliability',
        statusTone: hasReadinessDecision
          ? 'Done'
          : proofPageState === 'GENERATING'
            ? 'In progress'
            : proofPageState === 'ERROR'
              ? 'Needs review'
            : proofPackExists
              ? 'Needs review'
              : 'In progress',
        statusLabel: hasReadinessDecision
          ? 'Done'
          : proofPageState === 'GENERATING'
            ? 'Waiting on evidence'
            : proofPageState === 'ERROR'
            ? 'Blocked by Scan Package'
            : proofPackExists
              ? 'Needs review'
              : 'Waiting on evidence',
        detail: hasReadinessDecision
          ? 'A trust posture is available for this case.'
          : proofPageState === 'ERROR'
            ? 'Trust posture is blocked until the Scan Package is retried successfully.'
          : proofPackExists
            ? 'Trust posture still needs evidence-strength and reliability metrics.'
            : 'Trust posture will appear once the Scan Package is ready.',
      },
      {
        title: 'Action taken',
        statusTone: actionRecorded
          ? 'Done'
          : proofPackExists
            ? 'Needs review'
            : 'Needs review',
        statusLabel: actionRecorded ? 'Done' : 'Needs action',
        detail: actionRecorded
          ? 'Corrective follow-through is visible for this case.'
          : 'This report still needs visible corrective ownership or follow-through.',
      },
      {
        title: 'Outcome / resolution',
        statusTone: outcomeRecorded
          ? 'Done'
          : actionRecorded
            ? 'In progress'
            : 'Needs review',
        statusLabel: outcomeRecorded
          ? 'Done'
          : actionRecorded
            ? 'In progress'
            : 'Waiting on action',
        detail: outcomeRecorded
          ? 'A follow-up review or measurement baseline is recorded.'
          : actionRecorded
            ? 'Corrective work is visible, but the outcome window still needs recording.'
            : 'Outcome proof depends on corrective follow-through first.',
      },
      {
        title: 'Customer handoff',
        statusTone: customerHandoffReady ? 'Ready' : 'Needs review',
        statusLabel: customerHandoffReady ? 'Ready' : 'Not ready yet',
        detail: customerHandoffReady
          ? 'The customer-facing bundle can be prepared from the assembled proof story.'
          : 'Customer handoff will be ready once trust and action proof are complete.',
      },
    ],
    [actionRecorded, customerHandoffReady, hasReadinessDecision, outcomeRecorded, proofPackExists, proofPageState, reportError],
  );

  const _completedJourneyItems = useMemo(() => {
    const items: string[] = ['The case is detected and scoped for this reporting period.'];
    if (proofPackExists) items.push('The Scan Package has been created for this case.');
    if (hasReadinessDecision) items.push('A trust posture is available and can support the proof narrative.');
    if (actionRecorded) items.push('Corrective follow-through is visible in this report.');
    if (outcomeRecorded) items.push('A follow-up review or outcome baseline is already recorded.');
    return items;
  }, [actionRecorded, hasReadinessDecision, outcomeRecorded, proofPackExists]);

  const _remainingJourneyItems = useMemo(() => {
    const items: string[] = [];
    if (proofPageState === 'ERROR') {
      items.push('Retry the package build so this case has a usable Scan Package.');
    } else if (!proofPackExists) {
      items.push('Build the Scan Package so this case has a downloadable package.');
    } else if (!hasReadinessDecision) {
      items.push('Finish or regenerate the Scan Package so trust and reliability metrics are available.');
    }
    if (!actionRecorded) items.push('Capture the corrective action, owner, or follow-through path for this case.');
    if (!outcomeRecorded) items.push('Record the outcome window, follow-up review, or measurement baseline.');
    if (!customerHandoffReady) items.push('Prepare the customer-facing handoff bundle only after the proof story is complete.');
    return items;
  }, [actionRecorded, customerHandoffReady, hasReadinessDecision, outcomeRecorded, proofPackExists, proofPageState]);

  const _nextProofStep = useMemo(() => {
    if (!proofPackExists || proofPageState === 'GENERATED_WITHOUT_READINESS' || proofPageState === 'ERROR') {
      return {
        title: proofPageState === 'ERROR'
          ? 'Retry Scan Package Build'
          : proofPackExists ? 'Regenerate Scan Package' : 'Build Scan Package',
        detail: proofPageState === 'ERROR'
          ? (reportError || 'The Scan Package failed for this case and should be retried.')
          : proofPackExists
          ? 'The pack exists, but it still needs trust metrics before this page can show a firm conclusion.'
          : 'Build the Scan Package so this Safety Scan has a downloadable package.',
        actionLabel: proofPageState === 'ERROR'
          ? 'Try again'
          : proofPackExists ? 'Regenerate Scan Package' : 'Build Scan Package',
        onClick: runEvidenceBundleAction,
      };
    }
    if (!hasReadinessDecision) {
      return {
        title: 'Wait for trust posture',
        detail: 'The Scan Package is still being processed. Once it completes, this page will show the trust posture automatically.',
        actionLabel: 'Jump to conclusion',
        onClick: focusReliabilityReviewSection,
      };
    }
    if (!actionRecorded) {
      return {
        title: 'Capture action taken',
        detail: 'Return to Review to record the corrective action and ownership that close the case story.',
        actionLabel: 'Return to Review',
        onClick: () => navigate(incidentReviewReturnHref),
      };
    }
    if (!outcomeRecorded) {
      return {
        title: 'Record the outcome window',
        detail: 'Use the action and outcome section to capture the follow-up review or measurement baseline.',
        actionLabel: 'Jump to outcome',
        onClick: focusCorrectiveSummarySection,
      };
    }
    return {
      title: 'Prepare customer handoff',
      detail: 'The Scan Package is assembled. Generate the broader platform trust pack when you are ready to hand off audit, governance, and resilience evidence beyond this Safety Scan.',
      actionLabel: customerHandoffStatus === 'ready' ? 'Download platform trust pack' : 'Generate platform trust pack',
      onClick: customerHandoffStatus === 'ready' ? () => void downloadCustomerHandoffBundle() : () => void runCustomerHandoffGeneration(),
    };
  }, [
    actionRecorded,
    customerHandoffStatus,
    downloadCustomerHandoffBundle,
    focusCorrectiveSummarySection,
    focusReliabilityReviewSection,
    hasReadinessDecision,
    incidentReviewReturnHref,
    navigate,
    outcomeRecorded,
    proofPackExists,
    proofPageState,
    reportError,
    runCustomerHandoffGeneration,
    runEvidenceBundleAction,
  ]);

  const proofScopedDeviceLabel = useMemo(
    () => lookupLabeledValue(deviceNameById, String(canonicalProofDeviceId || proofRoutingFallbackDeviceId || '').trim()),
    [canonicalProofDeviceId, deviceNameById, proofRoutingFallbackDeviceId],
  );
  const proofScopedZoneLabel = useMemo(
    () => canonicalProofZoneName || lookupLabeledValue(zoneNameById, String(proofScopedZoneId || '').trim()),
    [canonicalProofZoneName, proofScopedZoneId, zoneNameById],
  );
  const proofScopedPatternLabel = useMemo(
    () => firstDefinedString(
      firstStringFromPaths(firstProofCaseIncident, [
        'canonical_labels.incident_label',
        'safety_case.labels.incident_label',
      ]),
      firstStringFromPaths(firstReportIncident, [
        'canonical_labels.incident_label',
        'safety_case.labels.incident_label',
      ]),
      incidentTypeLabel(String(canonicalProofIncidentType || '').trim()),
    ) || 'Incident',
    [canonicalProofIncidentType, firstProofCaseIncident, firstReportIncident],
  );

  const _proofSharedContextChips = useMemo(() => {
    const chips: string[] = [];
    if (proofScopedDeviceLabel) chips.push(`Device: ${proofScopedDeviceLabel}`);
    if (proofScopedZoneLabel) chips.push(`Zone: ${proofScopedZoneLabel}`);
    if (String(canonicalProofIncidentType || '').trim()) chips.push(`Risk type: ${proofScopedPatternLabel}`);
    if (workspaceTimeRange.label) chips.push(`Window: ${workspaceTimeRange.label}`);
    if (canonicalProofScopeId) chips.push(`Scope: ${canonicalProofScopeId}`);
    return chips;
  }, [canonicalProofIncidentType, canonicalProofScopeId, proofScopedDeviceLabel, proofScopedPatternLabel, proofScopedZoneLabel, workspaceTimeRange.label]);

  const caseTotalIncidents = useMemo(
    () => formatCount(
      normalizedSummary?.total_incidents
      ?? proofPackPreviewData?.case_file?.summary?.incidents_total
      ?? reportData?.totals?.total,
    ),
    [normalizedSummary?.total_incidents, proofPackPreviewData?.case_file?.summary?.incidents_total, reportData?.totals?.total],
  );

  const caseHighRiskIncidents = useMemo(
    () => formatCount(
      normalizedSummary?.high_risk_incidents
      ?? proofPackPreviewData?.case_file?.summary?.high_or_critical_total,
    ),
    [normalizedSummary?.high_risk_incidents, proofPackPreviewData?.case_file?.summary?.high_or_critical_total],
  );

  const trustPostureLabel = useMemo(() => {
    const verdictStatus = String(normalizedVerdict?.status || '').toUpperCase();
    if (verdictStatus === 'READY') return 'Ready for approval';
    if (verdictStatus === 'READY_WITH_WARNINGS' || verdictStatus === 'NEEDS_REVIEW') return 'Needs review';
    if (verdictStatus === 'NOT_READY') return 'Not ready yet';
    if (proofPageState === 'ERROR' || proofPageState === 'EMPTY' || proofPageState === 'GENERATING') return 'Waiting';
    if (proofPageState === 'GENERATED_WITHOUT_READINESS') return 'Waiting';
    return hasReadinessDecision ? 'Needs review' : 'Waiting';
  }, [hasReadinessDecision, normalizedVerdict?.status, proofPageState]);

  const _evidenceStateLabel = useMemo(() => {
    if (proofPageState === 'EMPTY') return 'Bundle missing';
    if (proofPageState === 'GENERATING') return 'Bundle generating';
    if (proofPageState === 'GENERATED_WITHOUT_READINESS') return 'Bundle missing readiness metrics';
    if (proofPageState === 'ERROR') return 'Bundle retry needed';
    if (evidenceConfidenceSummary === 'Weak') return 'Evidence weak';
    if (hasReadinessDecision) return 'Evidence ready for audit';
    return 'Bundle ready';
  }, [evidenceConfidenceSummary, hasReadinessDecision, proofPageState]);

  const caseSummaryNarrative = useMemo(() => {
    if (demoProofCase) return demoProofCase.impactSentence;
    const areaLabel = proofScopedZoneLabel || canonicalProofZoneName || 'the selected area';
    const patternLabel = proofScopedPatternLabel || humanizeZoneLabel(String(dominantPattern?.label || '').trim()) || 'This recurring risk';
    const totalLabel = caseTotalIncidents;
    const highRiskLabel = caseHighRiskIncidents;
    if (pedestrianZoneHero) {
      const intensity = highRiskLabel
        ? `${highRiskLabel} high-risk incidents were observed in the selected window, suggesting a recurring pedestrian-flow or zone-control issue.`
        : totalLabel
          ? `${totalLabel} incidents were observed in the selected window, suggesting a recurring pedestrian-flow or zone-control issue.`
          : 'The selected window suggests a recurring pedestrian-flow or zone-control issue.';
      return `Repeated qualifying entries occurred in the danger zone in ${pedestrianZoneHero.areaLabel}. ${intensity}`;
    }
    const genericPatternLabel = patternLabel === 'Incident' ? 'Incident activity' : patternLabel;
    const observedVerb = genericPatternLabel === 'Incident activity' ? 'was' : 'were';
    if (highRiskLabel) {
      return `${genericPatternLabel} ${observedVerb} observed in ${areaLabel}. ${highRiskLabel} high-risk incidents were observed in the selected window.`;
    }
    if (totalLabel) {
      return `${genericPatternLabel} ${observedVerb} observed in ${areaLabel}. ${totalLabel} incidents were observed in the selected window.`;
    }
    return `${genericPatternLabel} is being reviewed for ${areaLabel} in the current case window.`;
  }, [
    canonicalProofZoneName,
    caseHighRiskIncidents,
    caseTotalIncidents,
    dominantPattern?.label,
    demoProofCase,
    pedestrianZoneHero,
    proofScopedPatternLabel,
    proofScopedZoneLabel,
  ]);

  const correctiveActionSurface = useMemo(() => {
    const stage = operationalFollowThroughModel.stage;
    const hasCorrectiveNeed = Boolean(pedestrianZoneHero || safetyActionsForUi.length > 0);
    let status: ProofSurfaceCardStatus = 'Ready';
    const summary = operationalFollowThroughModel.summaryLine || undefined;
    let blockingReason = 'Use the exhaustive corrective summary to decide ownership, the next action, and what should happen on the floor.';
    let primaryCtaLabel = 'Open follow-up summary';
    if (!hasReadinessDecision) {
      status = proofPageState === 'GENERATING' ? 'In progress' : 'Needs review';
      blockingReason = proofPageState === 'GENERATING'
        ? 'The corrective summary is available now while the Scan Package continues generating in parallel.'
        : 'Generate or review the exhaustive corrective summary first, then use the package only when you need deeper evidence support.';
    } else if (stage === 'measured' || stage === 'followup_scheduled') {
      status = 'Done';
      blockingReason = 'Owner, follow-through, and next review timing are already recorded and ready for comparison.';
      primaryCtaLabel = 'Review follow-up summary';
    } else if (stage === 'runbook_started' || stage === 'in_progress' || stage === 'completed' || stage === 'copilot_opened') {
      status = 'In progress';
      blockingReason = 'Open the corrective surface to continue execution, confirm ownership, and close the loop.';
    } else if (hasCorrectiveNeed) {
      status = 'Needs review';
      blockingReason = pedestrianZoneHero
        ? `The current case still needs a floor-level corrective action in ${pedestrianZoneHero.areaLabel}.`
        : 'The current case still needs an owner, action, and next review plan.';
    }
    return {
      status,
      summary,
      blockingReason,
      primaryCtaLabel,
      completionCriteria: [
        'Assign an owner for the next action.',
        'Choose or launch the corrective workflow.',
        'Record the next review or comparison window.',
      ],
    };
  }, [hasReadinessDecision, operationalFollowThroughModel.stage, operationalFollowThroughModel.summaryLine, pedestrianZoneHero, proofPageState, safetyActionsForUi.length]);

  const incidentEvidencePackageSurface = useMemo(() => {
    let status: ProofSurfaceCardStatus = 'Ready';
    let blockingReason = evidenceBundleReviewed
      ? 'You already reviewed this Scan Package once for the current case.'
      : 'The Scan Package is ready to open, review, and share for this case.';
    let primaryCtaLabel = evidenceBundleReviewed ? 'Review again' : 'Review evidence';
    if (proofPageState === 'EMPTY') {
      status = 'Needs review';
      blockingReason = 'No Scan Package has been created for this case yet.';
      primaryCtaLabel = 'Build Scan Package';
    } else if (proofPageState === 'GENERATING') {
      status = 'In progress';
      blockingReason = 'The Scan Package is generating now for this scoped case.';
      primaryCtaLabel = 'Open package status';
    } else if (proofPageState === 'GENERATED_WITHOUT_READINESS') {
      status = 'Needs review';
      blockingReason = 'This bundle exists, but this version is missing the strength checks used for trust review.';
      primaryCtaLabel = 'Regenerate Scan Package';
    } else if (proofPageState === 'ERROR') {
      status = 'Needs review';
      blockingReason = reportError || 'Evidence bundle generation failed for this case.';
      primaryCtaLabel = 'Retry bundle';
    } else if (evidenceBundleReviewed) {
      status = 'Done';
    }
    return {
      status,
      blockingReason,
      primaryCtaLabel,
      completionCriteria: [
        'Open the ZIP package for this case.',
        'Check whether the snapshots, clips, and summary tell a clear story.',
        'Note any evidence gaps that still need follow-up.',
      ],
    };
  }, [evidenceBundleReviewed, proofPageState, reportError]);

  const reliabilityReviewSurface = useMemo(() => {
    let status: ProofSurfaceCardStatus = 'Ready';
    let blockingReason = 'Evidence delivery and pipeline trust signals are available for review.';
    let primaryCtaLabel = 'Open reliability review';
    if (proofPageState === 'EMPTY' || proofPageState === 'ERROR') {
      status = 'Needs review';
      blockingReason = proofPageState === 'ERROR'
        ? 'Trust review is waiting on a successful ZIP build. Fix the ZIP error first, then reopen trust review.'
        : 'Trust review will unlock after the first ZIP package is created for this case.';
      primaryCtaLabel = 'Fix ZIP build';
    } else if (proofPageState === 'GENERATING') {
      status = 'Needs review';
      blockingReason = 'Reliability review is waiting for the ZIP package to finish generating.';
      primaryCtaLabel = 'Open package status';
    } else if (proofPageState === 'GENERATED_WITHOUT_READINESS') {
      status = 'Needs review';
      blockingReason = 'Regenerate the bundle with evidence-strength metrics before relying on trust review for this case.';
      primaryCtaLabel = 'Regenerate ZIP Package';
    } else if (clipCaptureDisabledUi) {
      status = 'Needs review';
      blockingReason = 'Required clip capture is disabled for some incident types in this case scope.';
    } else if (pipelineDegradedForUi) {
      status = 'Needs review';
      blockingReason = 'Detection-health issues reduce trust in this reporting period and should be checked before Safety Scan sign-off.';
    } else if (!reliabilityAvailable) {
      status = 'Needs review';
      blockingReason = 'Reliability metrics are not included in this package yet, so trust remains incomplete.';
    } else if (evidenceActions.length > 0) {
      status = 'Needs review';
      blockingReason = String(evidenceActions[0]?.reason || 'Evidence delivery or capture gaps still need review for this case.');
    }
    return {
      status,
      blockingReason,
      primaryCtaLabel,
      completionCriteria: [
        'Review delivery, capture, and pipeline trust signals.',
        'Identify any trust blockers affecting the case.',
        'Confirm the remediation path when trust is degraded.',
      ],
    };
  }, [clipCaptureDisabledUi, evidenceActions, pipelineDegradedForUi, proofPageState, reliabilityAvailable]);

  const recommendedProofSurface = useMemo<ProofSurfaceKey>(() => {
    if (correctiveActionSurface.status === 'Needs review' || correctiveActionSurface.status === 'In progress') {
      return 'corrective';
    }
    if (
      incidentEvidencePackageSurface.status === 'In progress'
      || incidentEvidencePackageSurface.status === 'Needs review'
    ) {
      return 'package';
    }
    if (reliabilityReviewSurface.status === 'Needs review') {
      return 'reliability';
    }
    return 'corrective';
  }, [correctiveActionSurface.status, incidentEvidencePackageSurface.status, reliabilityReviewSurface.status]);

  const evidenceBundleCreating = proofPageState === 'GENERATING';
  const evidenceBundleFailed = proofPageState === 'ERROR';
  const evidenceBundleNeedsRefresh = proofPageState === 'GENERATED_WITHOUT_READINESS';
  const evidenceBundleReady = proofPageState === 'READY';
  const _evidenceBundleTechnicalError = evidenceBundleFailed ? String(reportError || '').trim() : '';
  const _evidenceBundleUserError = useMemo(() => {
    if (!evidenceBundleFailed) return '';
    return String(reportError || '').trim() || 'Evidence bundle could not be created. Try again.';
  }, [evidenceBundleFailed, reportError]);
  const _evidenceBundleStatusLabel = useMemo(() => {
    if (evidenceBundleCreating) return 'Building Scan Package';
    if (evidenceBundleFailed) return 'Scan Package failed';
    if (evidenceBundleNeedsRefresh) return 'Scan Package needs refresh';
    if (evidenceBundleReady && evidenceBundleReviewed) return 'Scan Package reviewed';
    if (evidenceBundleReady) return 'Scan Package ready';
    return 'Scan Package not created';
  }, [evidenceBundleCreating, evidenceBundleFailed, evidenceBundleNeedsRefresh, evidenceBundleReady, evidenceBundleReviewed]);
  const evidenceBundlePrimaryLabel = useMemo(() => {
    if (!canOperateOnProofPackage) return 'Selected scan scope required';
    if (evidenceBundleCreating) return 'Building Scan Package...';
    if (evidenceBundleFailed) return 'Retry Scan Package Build';
    if (evidenceBundleNeedsRefresh) return 'Regenerate Scan Package';
    if (evidenceBundleReady) return evidenceBundleReviewed ? 'Review package again' : 'Review package';
    return 'Build Scan Package';
  }, [canOperateOnProofPackage, evidenceBundleCreating, evidenceBundleFailed, evidenceBundleNeedsRefresh, evidenceBundleReady, evidenceBundleReviewed]);

  const _evidenceBundleStatusTone = useMemo<'info' | 'success' | 'warning' | 'error'>(() => {
    if (evidenceBundleCreating) return 'info';
    if (evidenceBundleReady) return 'success';
    if (evidenceBundleFailed) return 'error';
    return 'warning';
  }, [evidenceBundleCreating, evidenceBundleFailed, evidenceBundleReady]);

  const _evidenceBundleStatusHeading = useMemo(() => {
    if (!canOperateOnProofPackage) return 'Selected scan scope required';
    if (evidenceBundleCreating) return 'Building Scan Package';
    if (evidenceBundleReady && evidenceBundleReviewed) return 'Scan Package reviewed';
    if (evidenceBundleReady) return 'Scan Package ready';
    if (evidenceBundleFailed) return 'Scan Package could not be prepared';
    if (evidenceBundleNeedsRefresh) return 'Regenerate Scan Package';
    return 'Build Scan Package';
  }, [canOperateOnProofPackage, evidenceBundleCreating, evidenceBundleFailed, evidenceBundleNeedsRefresh, evidenceBundleReady, evidenceBundleReviewed]);

  const _evidenceBundleStatusBody = useMemo(() => {
    if (!canOperateOnProofPackage) {
      return 'Declare one scan scope first. This page should package one policy family with its zone and time window before building the Scan Package.';
    }
    if (evidenceBundleCreating) {
      return 'We’re assembling the Scan Package for this case. This may take a moment while the system gathers the case file, supporting evidence, and trust context.';
    }
    if (evidenceBundleReady) {
      return 'The Scan Package is ready to review, open, or download for this case.';
    }
    if (evidenceBundleFailed) {
      return 'The system could not prepare the Scan Package for this case. Try again, or review the current package settings.';
    }
    if (evidenceBundleNeedsRefresh) {
      return 'This package is missing some trust signals needed for a stronger conclusion. Refresh it for this case before relying on the package.';
    }
    return 'Build the Scan Package for this case. Once ready, use it to review what happened, what evidence supports it, and what limits remain.';
  }, [canOperateOnProofPackage, evidenceBundleCreating, evidenceBundleFailed, evidenceBundleNeedsRefresh, evidenceBundleReady]);

  const _incidentEvidencePackOutputStatus = useMemo(() => {
    if (evidenceBundleCreating) return 'In progress';
    if (evidenceBundleFailed) return 'Failed';
    if (evidenceBundleNeedsRefresh) return 'Needs refresh';
    if (evidenceBundleReady) return 'Ready';
    return 'Not started';
  }, [evidenceBundleCreating, evidenceBundleFailed, evidenceBundleNeedsRefresh, evidenceBundleReady]);

  const canDownloadSafetyReportZip = packageBuilt && !evidenceBundleCreating;

  const primaryIncidentEvidencePackageHelper = useMemo(() => {
    if (!canOperateOnProofPackage) {
      return SAFETY_REPORT_BUILD_SCOPE_HELPER;
    }
    if (evidenceBundleFailed) {
      return String(reportError || '').trim() || SAFETY_REPORT_BUILD_FAILED_HELPER;
    }
    if (evidenceBundleCreating) {
      return `Generating the ${customerReportProductName} package for this scope…`;
    }
    if (proofPackDownloading) {
      return `Downloading the ${customerReportProductName} ZIP to your device…`;
    }
    if (canDownloadSafetyReportZip && reportError) {
      return String(reportError || '').trim() || SAFETY_REPORT_DOWNLOAD_FAILED_HELPER;
    }
    if (canDownloadSafetyReportZip && !customerHandoffReady) {
      return SAFETY_REPORT_SIGNOFF_PENDING_HELPER;
    }
    if (!canDownloadSafetyReportZip && !evidenceBundleFailed) {
      return `Build the ${customerReportProductName} package to download the Scan Package for this scope.`;
    }
    return undefined;
  }, [
    canDownloadSafetyReportZip,
    canOperateOnProofPackage,
    customerHandoffReady,
    customerReportProductName,
    evidenceBundleCreating,
    evidenceBundleFailed,
    proofPackDownloading,
    reportError,
  ]);

  const _incidentEvidencePackOutputPrimaryLabel = useMemo(() => {
    if (!canOperateOnProofPackage) {
      return 'Build Scan Package';
    }
    if (proofPackDownloading) {
      return `Downloading ${customerReportProductName}...`;
    }
    if (evidenceBundleCreating) return 'Building Scan Package...';
    if (canDownloadSafetyReportZip) {
      return resolveReportDownloadButtonLabel(safetyReportTypeMode, hazardClassKey, {
        signOffReady: customerHandoffReady,
      });
    }
    if (evidenceBundleFailed) return 'Retry Scan Package Build';
    return 'Build Scan Package';
  }, [
    canDownloadSafetyReportZip,
    canOperateOnProofPackage,
    customerHandoffReady,
    customerReportProductName,
    evidenceBundleCreating,
    evidenceBundleFailed,
    hazardClassKey,
    proofPackDownloading,
    safetyReportTypeMode,
  ]);

  const canOpenSelectedSafetyEvent = selectedReviewIncidentIds.length === 1;

  const _evidenceBundleStageLabel = useMemo(() => {
    if (!evidenceBundleCreating) return null;
    return humanizeJobState(proofPackJobState);
  }, [evidenceBundleCreating, proofPackJobState]);

  const _proofHomeSummaryLine = useMemo(() => {
    const allReady =
      incidentEvidencePackageSurface.status === 'Ready'
      && reliabilityReviewSurface.status === 'Ready'
      && (correctiveActionSurface.status === 'Ready' || correctiveActionSurface.status === 'Done');
    if (allReady) {
      return 'This case has enough support for review. Choose whether to act, document, or approve.';
    }
    if (activeProofSurface === 'corrective') {
      return 'Create a follow-up summary, assign ownership, and start the corrective workflow for this case.';
    }
    if (activeProofSurface === 'package') {
      if (evidenceBundleCreating) {
        return 'The ZIP package is being created now. Wait for it to finish, then review support strength and handoff readiness.';
      }
      if (evidenceBundleNeedsRefresh) {
        return 'Regenerate the ZIP package so this case includes the evidence-strength metrics needed for trust review.';
      }
      if (evidenceBundleFailed) {
        return 'Try the ZIP build again so this case has a usable package for review and handoff.';
      }
      if (evidenceBundleReady) {
        return 'Review the ZIP package, then use it to support trust review and handoff for this case.';
      }
      return 'Generate the ZIP package for this case before trust review or handoff.';
    }
    if (activeProofSurface === 'reliability') {
      return hasReadinessDecision
        ? 'Review the trust findings below and confirm whether this case is strong enough to trust.'
        : 'Generate or regenerate the ZIP package first, then review the trust blockers for this case.';
    }
    if (recommendedProofSurface === 'corrective') {
      return 'Create or review the corrective action for this incident case.';
    }
    if (recommendedProofSurface === 'package') {
      return 'Review or refresh the case evidence before approval.';
    }
    if (recommendedProofSurface === 'reliability') {
      return 'Check whether the evidence and system signals are strong enough to trust this case.';
    }
    return 'Review the case summary, then choose the lane that matches the next decision.';
  }, [
    activeProofSurface,
    correctiveActionSurface.status,
    evidenceBundleCreating,
    evidenceBundleFailed,
    evidenceBundleNeedsRefresh,
    evidenceBundleReady,
    hasReadinessDecision,
    incidentEvidencePackageSurface.status,
    recommendedProofSurface,
    reliabilityReviewSurface.status,
  ]);

  const _correctiveActionPrimaryCtaLabel = useMemo(() => {
    if (activeProofSurface === 'corrective') {
      return 'Current lane';
    }
    return 'Open action plan';
  }, [activeProofSurface]);

  const _incidentEvidencePackagePrimaryCtaLabel = useMemo(() => {
    if (activeProofSurface === 'package') {
      return 'Current lane';
    }
    return 'Open case evidence';
  }, [activeProofSurface]);

  const _reliabilityReviewPrimaryCtaLabel = useMemo(() => {
    if (activeProofSurface === 'reliability') return 'Current lane';
    return 'Open trust review';
  }, [activeProofSurface]);

  const correctiveSummaryReady = Boolean(reportData?.ok);
  const correctiveSummaryCreating = reportKind === 'incidents_rollup' && reportLoading;
  const correctiveSummaryFailed = reportKind === 'incidents_rollup' && Boolean(reportError) && !correctiveSummaryCreating && !correctiveSummaryReady;
  const _correctiveSummaryTechnicalError = correctiveSummaryFailed ? String(reportError || '').trim() : '';
  const _correctiveSummaryUserError = useMemo(() => {
    if (!correctiveSummaryFailed) return '';
    return 'Follow-up summary could not be created. The service returned an error. Try again.';
  }, [correctiveSummaryFailed]);
  const _correctiveSummaryStatusLabel = useMemo(() => {
    if (correctiveSummaryCreating) return 'Creating follow-up summary';
    if (correctiveSummaryFailed) return 'Follow-up summary failed';
    if (correctiveSummaryReady) return 'Follow-up summary ready';
    return 'Follow-up summary not created';
  }, [correctiveSummaryCreating, correctiveSummaryFailed, correctiveSummaryReady]);

  const _reliabilityTaskStatusLabel = useMemo(() => {
    if (!hasReadinessDecision) {
      if (evidenceBundleCreating) return 'Waiting for ZIP package';
      if (evidenceBundleFailed) return 'Trust review waiting';
      if (evidenceBundleNeedsRefresh) return 'Evidence bundle needs refresh';
      return 'Trust review waiting';
    }
    return 'Trust review ready';
  }, [evidenceBundleCreating, evidenceBundleFailed, evidenceBundleNeedsRefresh, hasReadinessDecision]);

  const _nextDecisionCta = useMemo<NextDecisionCta>(() => {
    const allReady =
      incidentEvidencePackageSurface.status === 'Ready'
      && reliabilityReviewSurface.status === 'Ready'
      && (correctiveActionSurface.status === 'Ready' || correctiveActionSurface.status === 'Done');
    if (activeProofSurface === 'corrective') {
      if (correctiveSummaryCreating) {
        return {
          label: 'Creating follow-up summary',
          onClick: reviewCorrectiveSummaryAction,
          disabled: true,
        };
      }
      if (correctiveSummaryReady) {
        return {
          label: 'Review follow-up summary',
          onClick: reviewCorrectiveSummaryAction,
        };
      }
      if (correctiveSummaryFailed) {
        return {
          label: 'Try again',
          onClick: runCorrectiveSummaryAction,
        };
      }
      return {
        label: 'Create follow-up summary',
        onClick: runCorrectiveSummaryAction,
      };
    }
    if (activeProofSurface === 'package') {
      return {
        label: evidenceBundlePrimaryLabel,
        onClick: evidenceBundleReady ? reviewEvidenceBundleAction : runEvidenceBundleAction,
        disabled: evidenceBundleCreating,
      };
    }
    if (activeProofSurface === 'reliability') {
      if (!hasReadinessDecision) {
        return {
          label: evidenceBundlePrimaryLabel,
          onClick: runEvidenceBundleAction,
          disabled: evidenceBundleCreating,
        };
      }
      return {
        label: 'Review trust findings',
        onClick: reviewTrustFindingsAction,
      };
    }
    if (allReady) {
      return {
        label: 'Open trust review',
        onClick: openReliabilityReviewSurface,
      };
    }
    if (recommendedProofSurface === 'corrective') {
      return {
        label: 'Open action plan',
        onClick: openCorrectiveActionSurface,
      };
    }
    if (recommendedProofSurface === 'package') {
      return {
        label: 'Review case evidence',
        onClick: openIncidentEvidencePackageSurface,
      };
    }
    return {
      label: 'Open trust review',
      onClick: openReliabilityReviewSurface,
    };
  }, [
    activeProofSurface,
    correctiveActionSurface.status,
    correctiveSummaryCreating,
    correctiveSummaryFailed,
    correctiveSummaryReady,
    evidenceBundleCreating,
    evidenceBundlePrimaryLabel,
    evidenceBundleReady,
    hasReadinessDecision,
    incidentEvidencePackageSurface.status,
    openCorrectiveActionSurface,
    openIncidentEvidencePackageSurface,
    openReliabilityReviewSurface,
    reviewCorrectiveSummaryAction,
    reviewEvidenceBundleAction,
    reviewTrustFindingsAction,
    recommendedProofSurface,
    reliabilityReviewSurface.status,
    runEvidenceBundleAction,
    runCorrectiveSummaryAction,
  ]);

  const _activeProofSurfaceMeta = useMemo(() => {
    if (activeProofSurface === 'corrective') {
      return {
        title: 'Corrective action',
        description: 'Use this area to decide what should change, who owns it, and when the team will check again.',
      };
    }
    if (activeProofSurface === 'reliability') {
      return {
        title: 'Case trust',
        description: hasReadinessDecision
          ? 'Use this area to decide whether the evidence and system health are strong enough to rely on this case.'
          : 'Use this area to decide whether the evidence and system health are strong enough to rely on this case. Start by generating or regenerating the ZIP package first.',
      };
    }
    return {
      title: 'Evidence bundle',
      description: evidenceBundleCreating
        ? 'The ZIP package is being created for this case now. Review will be available when it finishes.'
        : 'Use this area to open the case summary, snapshots, clips, and files you may need for review or handoff.',
    };
  }, [activeProofSurface, evidenceBundleCreating, hasReadinessDecision]);

  const _applyOperationalFollowThroughNext = useCallback(
    (model: OperationalFollowThroughModel) => {
      const surface = model.nextBestAction.targetSurface;
      if (surface === 'runbook' && operationalRunbookCta) return void handleRoutedCtaClick(operationalRunbookCta);
      if (surface === 'copilot' && operationalCopilotCta) return void handleRoutedCtaClick(operationalCopilotCta);
      if (surface === 'incidents' && operationalIncidentsCta) return void handleRoutedCtaClick(operationalIncidentsCta);
      if (surface === 'followup_review' && operationalFollowupReviewCta) return void handleRoutedCtaClick(operationalFollowupReviewCta);
    },
    [handleRoutedCtaClick, operationalCopilotCta, operationalFollowupReviewCta, operationalIncidentsCta, operationalRunbookCta],
  );

  const _evidenceStateColor = useMemo<'success' | 'warning' | 'error' | 'info'>(
    () => (proofPageState === 'READY' ? (evidenceConfidenceSummary === 'Weak' ? 'warning' : 'success') : proofPageState === 'GENERATING' ? 'info' : 'error'),
    [evidenceConfidenceSummary, proofPageState],
  );
  const _trustStateColor = useMemo<'success' | 'warning' | 'error'>(
    () => (trustPostureLabel === 'Ready for approval' ? 'success' : trustPostureLabel === 'Not ready yet' ? 'error' : 'warning'),
    [trustPostureLabel],
  );
  const _operationalStatusColor = useMemo<'success' | 'warning' | 'info' | 'default'>(
    () => (correctiveActionSurface.status === 'Done' ? 'success' : correctiveActionSurface.status === 'In progress' ? 'info' : correctiveActionSurface.status === 'Needs review' ? 'warning' : 'default'),
    [correctiveActionSurface.status],
  );

  const outcomeStateLabel = useMemo(() => {
    if (outcomeRecorded) return 'Measured';
    if (actionRecorded) return 'In progress';
    return 'Waiting';
  }, [actionRecorded, outcomeRecorded]);

  const proofScopedPolicyLabel = useMemo(
    () => explicitProofPolicyName
      || proofScopedPatternLabel
      || humanizeZoneLabel(String(dominantPattern?.label || '').trim())
      || canonicalProofIncidentType
      || (explicitProofPolicyId ? `Policy ${explicitProofPolicyId}` : ''),
    [
      canonicalProofIncidentType,
      dominantPattern?.label,
      explicitProofPolicyId,
      explicitProofPolicyName,
      proofScopedPatternLabel,
    ],
  );

  const currentResolutionProofCase = useMemo(
    () =>
      demoProofCase ||
      buildOperationalResolutionProofCase({
        caseId: explicitProofCaseId || canonicalProofReportId || 'current-proof-case',
        site: canonicalProofSiteId,
        zone: proofScopedZoneLabel || canonicalProofZoneName || canonicalProofZoneId,
        pattern: proofScopedPolicyLabel,
        windowLabel: workspaceTimeRange.label,
        trustPosture: trustPostureLabel,
        outcomeStatus: outcomeStateLabel,
        correctiveActionStatus: operationalFollowThroughModel.statusLabel,
        evidencePackStatus: proofPackExists ? (hasReadinessDecision ? 'Ready' : 'In progress') : 'Waiting',
        reliabilityPackStatus: hasReadinessDecision ? 'Ready' : 'Waiting',
        handoffBundleStatus: customerHandoffReady ? 'Ready' : 'Not ready yet',
        impactSentence: caseSummaryNarrative,
        orgId: scopedOrgId,
        siteId: canonicalProofSiteId || scopedSiteId,
        zoneId: canonicalProofZoneId,
        deviceId: canonicalProofDeviceId,
        incidentType: canonicalProofIncidentType,
        reportFrom: canonicalProofWindowStart,
        reportTo: canonicalProofWindowEnd,
        exportedAt: supportPackageGeneratedAt,
        source: canonicalProofReportId ? 'live_proof' : 'neutral_fallback',
      }),
    [
      canonicalProofDeviceId,
      canonicalProofIncidentType,
      canonicalProofReportId,
      canonicalProofSiteId,
      canonicalProofWindowEnd,
      canonicalProofWindowStart,
      canonicalProofZoneId,
      canonicalProofZoneName,
      caseSummaryNarrative,
      customerHandoffReady,
      demoProofCase,
      explicitProofCaseId,
      hasReadinessDecision,
      operationalFollowThroughModel.statusLabel,
      outcomeStateLabel,
      proofPackExists,
      proofScopedPolicyLabel,
      proofScopedZoneLabel,
      scopedOrgId,
      scopedSiteId,
      supportPackageGeneratedAt,
      trustPostureLabel,
      workspaceTimeRange.label,
    ],
  );

  const isLoopedDemoProofCase = currentResolutionProofCase.source === 'demo_fixture';

  const includedIncidentCaseCount = useMemo(() => {
    const explicitCount = Number(searchParams.get('included_incident_cases') || '');
    if (Number.isFinite(explicitCount) && explicitCount > 0) return explicitCount;
    const caseIncidents = proofPackPreviewData?.case_file?.incidents;
    if (Array.isArray(caseIncidents) && caseIncidents.length > 0) return caseIncidents.length;
    if (canonicalProofReportId) return 1;
    return null;
  }, [canonicalProofReportId, proofPackPreviewData, searchParams]);

  const anchorIncidentCaseLabel = useMemo(() => {
    const title = firstDefinedString(
      firstStringFromPaths(firstProofCaseIncident, [
        'canonical_labels.incident_label',
        'safety_case.labels.incident_label',
      ]),
      firstStringFromPaths(firstReportIncident, [
        'canonical_labels.incident_label',
        'safety_case.labels.incident_label',
      ]),
      firstStringFromPaths(firstProofCaseIncident, ['title', 'incident_title', 'summary_text']),
      firstStringFromPaths(firstReportIncident, ['title', 'incident_title', 'summary_text']),
    );
    if (title) return title;
    return explicitAnchorIncidentId || canonicalProofReportId || explicitProofCaseId || '';
  }, [canonicalProofReportId, explicitAnchorIncidentId, explicitProofCaseId, firstProofCaseIncident, firstReportIncident]);

  const selectableReviewIncidents = useMemo(() => {
    const byId = new Map<string, RepresentativeSafetyCaseOption>();

    const registerIncident = (incident: any, sourceLabel?: string) => {
      const value = String(
        firstDefinedString(
          firstStringFromPaths(incident, ['incident_id', 'id']),
        ) || '',
      ).trim();
      if (!value) return;
      const zoneId = String(firstStringFromPaths(incident, ['zone_id', 'zoneId']) || '').trim();
      const zoneName =
        zoneId
          ? (
            zoneNameById instanceof Map
              ? zoneNameById.get(zoneId)
              : (zoneNameById as Record<string, string> | undefined)?.[zoneId]
          ) || ''
          : '';
      const rawZoneLabel = firstDefinedString(
        firstStringFromPaths(incident, [
          'canonical_labels.zone_label',
          'safety_case.labels.zone_label',
        ]),
        firstStringFromPaths(incident, ['zone_name', 'zoneName']),
        zoneName,
        zoneId,
      );
      const zoneLabel = customerFacingAreaLabel(rawZoneLabel || zoneId) || rawZoneLabel || zoneId;
      const cameraId = String(firstStringFromPaths(incident, ['camera_id', 'cameraId']) || '').trim();
      const rawCameraLabel = firstDefinedString(
        firstStringFromPaths(incident, [
          'canonical_labels.camera_label',
          'safety_case.labels.camera_label',
        ]),
        firstStringFromPaths(incident, ['camera_name', 'cameraName']),
        cameraId ? lookupLabeledValue(_cameraNameById, cameraId) : '',
        cameraId,
      );
      const cameraLabel = rawCameraLabel || cameraId;
      const physicalLocation = resolveSafetyLocationDisplay(
        {
          location_snapshot: incident?.location_snapshot
            || incident?.envelope_metadata?.location_snapshot
            || null,
          envelope_metadata: incident?.envelope_metadata || null,
          camera_id: cameraId,
          zone_id: zoneId,
          zoneLabel: rawZoneLabel || zoneLabel,
          cameraName: rawCameraLabel,
        },
        locationLookups,
      );
      const areaLabel = (
        physicalLocation.primary !== 'Location not recorded'
          ? physicalLocation.primary
          : (zoneLabel || zoneId)
      );
      const policyType = String(firstDefinedString(
        firstStringFromPaths(incident, [
          'canonical_labels.incident_type_key',
          'safety_case.scope.policy_type',
        ]),
        firstStringFromPaths(incident, ['incident_type', 'incidentType']),
      ) || '').trim();
      const rawPolicyLabel = firstDefinedString(
        firstStringFromPaths(incident, [
          'canonical_labels.policy_label',
          'safety_case.labels.policy_label',
        ]),
        firstStringFromPaths(incident, ['policy_name', 'triggered_by.policy_name']),
        policyType,
      );
      const policyLabel = customerFacingSafetyRuleLabel(rawPolicyLabel || policyType);
      const rawLabel = firstDefinedString(
        firstStringFromPaths(incident, [
          'canonical_labels.incident_label',
          'safety_case.labels.incident_label',
        ]),
        firstStringFromPaths(incident, ['title', 'incident_title', 'summary_text']),
        value,
      ) || value;
      const label = customerFacingSafetyRuleLabel(rawLabel);
      const windowStart = firstDefinedString(
        firstStringFromPaths(incident, ['ts_start', 'timing.started_at', 'created_at']),
        firstStringFromPaths(incident, ['updated_at']),
      ) || '';
      if (!byId.has(value)) {
        byId.set(value, buildRepresentativeCaseFromParts({
          value,
          patternLabel: policyLabel || label,
          areaLabel,
          cameraLabel,
          windowStart,
          windowEnd: firstDefinedString(
            firstStringFromPaths(incident, ['ts_end', 'timing.ended_at', 'updated_at']),
            firstStringFromPaths(incident, ['ts_start', 'timing.started_at', 'created_at']),
          ) || '',
          cameraId,
          zoneId,
          policyType,
          policyLabel: policyLabel || policyType,
          zoneLabel: areaLabel || zoneLabel || zoneId,
          rawIncident: incident,
          sourceLabel,
        }));
      }
    };

    const caseIncidents = proofPackPreviewData?.case_file?.incidents;
    const proofPackIncidents = Array.isArray(caseIncidents) ? caseIncidents : [];
    reviewScopeIncidents.forEach((incident) => registerIncident(incident, 'Available in report window'));
    proofPackIncidents.forEach((incident) => registerIncident(incident, 'Included in current report'));
    registerIncident(firstReportIncident, 'Current selected incident');
    registerIncident(firstProofCaseIncident, 'Included in current report');

    if (
      currentProofScopeCaseId
      && currentProofScopeCaseId !== persistedDraftReportId
      && !byId.has(currentProofScopeCaseId)
    ) {
      byId.set(currentProofScopeCaseId, buildRepresentativeCaseFromParts({
        value: currentProofScopeCaseId,
        patternLabel: resolveReportScopePatternLabel(anchorIncidentCaseLabel || '', isSafetyReportBuilding(proofPageState)),
        areaLabel: resolveReportScopeAreaLabel(
          customerFacingAreaLabel(proofScopedZoneLabel || currentResolutionProofCase.zone || '') || '',
          isSafetyReportBuilding(proofPageState),
        ),
        cameraLabel: resolveReportScopeCameraLabel('', isSafetyReportBuilding(proofPageState)),
        windowStart: '',
        windowEnd: '',
        cameraId: '',
        zoneId: '',
        policyType: '',
        policyLabel: '',
        zoneLabel: '',
        rawIncident: null,
        sourceLabel: 'Current selection',
      }));
    }

    return Array.from(byId.values());
  }, [
    _cameraNameById,
    anchorIncidentCaseLabel,
    currentProofScopeCaseId,
    currentResolutionProofCase.zone,
    firstProofCaseIncident,
    firstReportIncident,
    locationLookups,
    proofPageState,
    proofPackPreviewData,
    proofScopedZoneLabel,
    persistedDraftReportId,
    reviewScopeIncidents,
    zoneNameById,
  ]);

  useEffect(() => {
    const sameIds = (prev: string[], next: string[]) =>
      prev.length === next.length && prev.every((id, index) => id === next[index]);
    const nextIds = (() => {
      if (selectableReviewIncidents.length <= 0) {
        return currentSelectedIncidentIdsKey
          ? currentSelectedIncidentIdsKey.split(',').map((value) => String(value || '').trim()).filter(Boolean)
          : [];
      }
      const urlIds = currentSelectedIncidentIdsKey
        ? currentSelectedIncidentIdsKey.split(',').map((value) => String(value || '').trim()).filter(Boolean)
        : [];
      if (urlIds.length > 0) return urlIds;
      const defaultId = pickDefaultRepresentativeCaseId(selectableReviewIncidents, [
        explicitAnchorIncidentId,
        currentProofScopeCaseId !== persistedDraftReportId ? currentProofScopeCaseId : '',
      ]);
      return defaultId ? [defaultId] : [];
    })();
    setSelectedReviewIncidentIds((prev) => {
      if (sameIds(prev, nextIds)) return prev;
      const prevIsInProgressPicker = prev.length > 0
        && prev.every((id) => String(id || '').trim() !== persistedDraftReportId)
        && prev.join(',') !== currentSelectedIncidentIdsKey
        && nextIds.join(',') === currentSelectedIncidentIdsKey
        && (
          selectableReviewIncidents.length === 0
          || prev.every((id) => selectableReviewIncidents.some((option) => String(option.value || '').trim() === id))
        );
      return prevIsInProgressPicker ? prev : nextIds;
    });
  }, [
    currentProofScopeCaseId,
    currentSelectedIncidentIdsKey,
    explicitAnchorIncidentId,
    persistedDraftReportId,
    selectableReviewIncidents,
  ]);

  const selectedReviewIncidentOptions = useMemo(
    () => selectableReviewIncidents.filter((option) => selectedReviewIncidentIds.includes(String(option.value || '').trim())),
    [selectableReviewIncidents, selectedReviewIncidentIds],
  );

  const activeRepresentativeCase = useMemo(
    () => selectedReviewIncidentOptions[0] || null,
    [selectedReviewIncidentOptions],
  );

  const reviewRecordPolicyOptions = useMemo(
    () => {
      const byPolicyType = new Map<string, string>();
      selectableReviewIncidents.forEach((option) => {
        const value = String(option.policyType || '').trim();
        const label = String(option.policyLabel || option.policyType || '').trim();
        if (!value || byPolicyType.has(value)) return;
        byPolicyType.set(value, label || value);
      });
      return Array.from(byPolicyType.entries()).map(([value, label]) => ({ value, label }));
    },
    [selectableReviewIncidents],
  );

  const reviewRecordCameraOptions = useMemo(
    () => {
      const byCameraId = new Map<string, string>();
      selectableReviewIncidents.forEach((option) => {
        const value = String(option.cameraId || '').trim();
        const label = String(option.cameraLabel || option.cameraId || '').trim();
        if (!value || byCameraId.has(value)) return;
        byCameraId.set(value, label || value);
      });
      return Array.from(byCameraId.entries()).map(([value, label]) => ({ value, label }));
    },
    [selectableReviewIncidents],
  );

  const filteredSelectableReviewIncidents = useMemo(() => {
    const activePolicyFilter = String(reviewRecordPolicyFilter || '').trim();
    const activeCameraFilter = String(reviewRecordCameraFilter || '').trim();
    return selectableReviewIncidents.filter((option) => {
      if (activePolicyFilter && String(option.policyType || '').trim() !== activePolicyFilter) {
        return false;
      }
      if (activeCameraFilter) {
        const cameraId = String(option.cameraId || '').trim();
        const cameraLabel = String(option.cameraLabel || '').trim();
        if (cameraId !== activeCameraFilter && cameraLabel !== activeCameraFilter) {
          return false;
        }
      }
      return true;
    });
  }, [reviewRecordCameraFilter, reviewRecordPolicyFilter, selectableReviewIncidents]);

  const reportScopeIncidents = useMemo(() => {
    const proofPackCaseIncidents: any[] = Array.isArray(proofPackPreviewData?.case_file?.incidents)
      ? [...(proofPackPreviewData?.case_file?.incidents || [])]
      : [];
    const merged = [
      ...selectedReviewIncidentOptions.map((option) => option.rawIncident).filter(Boolean),
      firstReportIncident,
      firstProofCaseIncident,
      ...proofPackCaseIncidents,
    ].filter(Boolean);
    const seen = new Set<string>();
    return merged.filter((incident) => {
      const key = String(firstStringFromPaths(incident, ['incident_id', 'id', 'group_key', 'groupKey']) || '').trim()
        || JSON.stringify(incidentGroupingKey(incident));
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [firstProofCaseIncident, firstReportIncident, proofPackPreviewData, selectedReviewIncidentOptions]);

  const selectedIncidentScopeDerivation = useMemo(() => {
    if (!selectedReviewIncidentOptions.length) {
      return {
        incidentCount: 0,
        isValid: false,
        invalidReason: 'Select representative evidence from Review to confirm this scan scope.',
      };
    }

    const zoneIds = Array.from(new Set(selectedReviewIncidentOptions.map((option) => String(option.zoneId || '').trim()).filter(Boolean)));
    const policyTypes = Array.from(new Set(selectedReviewIncidentOptions.map((option) => String(option.policyType || '').trim()).filter(Boolean)));
    const startCandidates = selectedReviewIncidentOptions.map((option) => String(option.windowStart || '').trim()).filter(Boolean);
    const endCandidates = selectedReviewIncidentOptions.map((option) => String(option.windowEnd || '').trim()).filter(Boolean);

    if (zoneIds.length > 1) {
      return {
        incidentCount: selectedReviewIncidentOptions.length,
        isValid: false,
        invalidReason: 'Selected incidents span multiple zones. The Scan Package should stay on one operational scope at a time.',
      };
    }

    if (policyTypes.length > 1) {
      return {
        incidentCount: selectedReviewIncidentOptions.length,
        isValid: false,
        invalidReason: 'Selected incidents span multiple policy families. Pick one family per ZIP package.',
      };
    }

    const sortedStarts = startCandidates.map((value) => new Date(value).getTime()).filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
    const sortedEnds = endCandidates.map((value) => new Date(value).getTime()).filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
    const derivedStart = sortedStarts.length ? toLocalInputValue(new Date(sortedStarts[0]).toISOString()) : '';
    const derivedEnd = sortedEnds.length ? toLocalInputValue(new Date(sortedEnds[sortedEnds.length - 1]).toISOString()) : derivedStart;
    const anchorIncident = selectedReviewIncidentOptions[0];

    return {
      incidentCount: selectedReviewIncidentOptions.length,
      isValid: true,
      invalidReason: '',
      anchorIncidentId: String(anchorIncident?.value || '').trim(),
      anchorIncidentLabel: String(anchorIncident?.label || '').trim(),
      zoneId: zoneIds[0] || '',
      zoneLabel: String(anchorIncident?.zoneLabel || anchorIncident?.zoneId || '').trim(),
      policyType: policyTypes[0] || '',
      policyLabel: String(anchorIncident?.policyLabel || anchorIncident?.policyType || '').trim(),
      windowStart: derivedStart,
      windowEnd: derivedEnd,
    };
  }, [selectedReviewIncidentOptions]);

  useEffect(() => {
    if (!selectedIncidentScopeDerivation.isValid) return;
    const anchorId = String(selectedIncidentScopeDerivation.anchorIncidentId || '').trim();
    const anchorChanged = lastDerivedProofScopeAnchorRef.current !== anchorId;
    if (anchorChanged) lastDerivedProofScopeAnchorRef.current = anchorId;
    setProofScopeCaseDraft(anchorId);
    if (selectedIncidentScopeDerivation.zoneId) setProofScopeZoneDraft(String(selectedIncidentScopeDerivation.zoneId).trim());
    if (selectedIncidentScopeDerivation.policyType) setProofScopePolicyTypeDraft(String(selectedIncidentScopeDerivation.policyType).trim());
    if (anchorChanged) {
      proofScopeWindowDraftEditedRef.current = false;
      if (selectedIncidentScopeDerivation.windowStart) {
        setProofScopeFromDraft(String(selectedIncidentScopeDerivation.windowStart).trim());
      }
      if (selectedIncidentScopeDerivation.windowEnd) {
        setProofScopeToDraft(String(selectedIncidentScopeDerivation.windowEnd).trim());
      }
    }
  }, [selectedIncidentScopeDerivation]);

  const proofScopeFacts = useMemo(
    () => {
      const rawPattern = String(currentResolutionProofCase.pattern || '').trim();
      const explicitPolicy = explicitProofPolicyId ? `Policy ${explicitProofPolicyId}` : '';
      const ruleValue = rawPattern
        ? customerFacingSafetyRuleLabel(rawPattern)
        : explicitPolicy
          ? customerFacingSafetyRuleLabel(explicitPolicy)
          : 'Not selected';
      return [
      {
        label: 'Safety rule',
        value: ruleValue === 'Safety event' ? 'Not selected' : ruleValue,
      },
      {
        label: 'Area',
        value: customerFacingAreaLabel(currentResolutionProofCase.zone) || currentResolutionProofCase.zone || 'Not captured yet',
      },
      { label: 'Selected period', value: currentResolutionProofCase.windowLabel || 'Not captured yet' },
      { label: 'Selected incidents', value: includedIncidentCaseCount != null ? String(includedIncidentCaseCount) : 'Not captured yet' },
      { label: 'Alerts/events in selected period', value: caseTotalIncidents || 'Not captured yet' },
      { label: 'Review anchor ID', value: anchorIncidentCaseLabel || 'Not captured yet' },
    ];
    },
    [anchorIncidentCaseLabel, caseTotalIncidents, currentResolutionProofCase, explicitProofPolicyId, includedIncidentCaseCount],
  );

  const _resolutionProofLifecycle = _proofJourneySteps;

  const _resolutionProofDoneItems = useMemo(
    () => (_completedJourneyItems.length ? _completedJourneyItems.slice(0, 3) : ['The case is detected and scoped for this reporting period.']),
    [_completedJourneyItems],
  );

  const _resolutionProofNeededItems = useMemo(
    () => (_remainingJourneyItems.length ? _remainingJourneyItems.slice(0, 3) : ['Waiting for the next proof update.']),
    [_remainingJourneyItems],
  );

  const _whatHappenedFacts = useMemo(
    () => [
      {
        label: 'Key recurring risk',
        value: currentResolutionProofCase.pattern || 'Unavailable',
      },
      {
        label: 'Location / timing',
        value: `${currentResolutionProofCase.zone || 'Unavailable'} · ${currentResolutionProofCase.windowLabel || 'Unavailable'}`,
      },
      {
        label: 'Supporting counts',
        value:
          currentResolutionProofCase.baselineCount != null && currentResolutionProofCase.followUpCount != null
            ? `${currentResolutionProofCase.baselineCount} -> ${currentResolutionProofCase.followUpCount} across baseline and follow-up windows`
            : caseTotalIncidents
              ? `${caseTotalIncidents} incidents in the current reporting period`
              : 'Not captured yet',
      },
    ],
    [
      caseTotalIncidents,
      currentResolutionProofCase,
    ],
  );
  const {
    reportProjection: _reportProjection,
    reportProjectionClaimList: _reportProjectionClaimList,
    reportProjectionCustomerSummary: _reportProjectionCustomerSummary,
    reportProjectionWorkflowState: _reportProjectionWorkflowState,
    reportProjectionEvidenceBreakdown: _reportProjectionEvidenceBreakdown,
    reportProjectionReportCounts: _reportProjectionReportCounts,
    reportProjectionWindowContext: _reportProjectionWindowContext,
    reportProjectionLabelBundle: _reportProjectionLabelBundle,
    reportProjectionSourceContext: _reportProjectionSourceContext,
    investigationAlignment,
    claimsRoiAlignment,
    safetyControlReview,
    customerOwnerLabel,
    selectedIncidentCountForCustomer: _selectedIncidentCountForCustomer,
    relatedAlertEventCount: _relatedAlertEventCount,
    latestEventTimestampLabel: _latestEventTimestampLabel,
    snapshotReadyCount: _snapshotReadyCount,
    clipReadyCount: _clipReadyCount,
    clipPendingCount: _clipPendingCount,
    customerAreaLabel: _customerAreaLabel,
    customerSafetyRuleLabel: _customerSafetyRuleLabel,
    customerIncidentLabel: _customerIncidentLabel,
    projectionFollowupFact: _projectionFollowupFact,
    projectionCurrentActionLabel: _projectionCurrentActionLabel,
    customerSummarySentence,
    customerStatusLabel: _customerStatusLabel,
    customerTopStatusPanel,
    customerSecondaryStatusFacts,
    customerWhatHappened,
    customerRiskPatternSummary,
    customerRiskPatternFacts,
    visibleRiskPatternDominantLabel,
    customerEvidencePoints,
    customerReviewStatusSummary,
    customerReviewChecklist,
    customerReviewOutcomeRows,
    customerReviewOutcomeSummary,
    customerReviewTrendSummary,
    customerReviewBoundarySummary,
    customerOwnershipDetailFacts,
    customerOwnershipRows,
    customerOwnershipSummary,
    customerEscalationRows,
    customerEscalationSummary,
    customerCorrectiveActionRows,
    customerCorrectiveActionSummary,
    customerMeasurementRows,
    customerMeasurementSummary,
    customerMeasurementBoundarySummary,
    customerActionSnapshotRows,
    customerActionSnapshotSummary,
    customerFollowupFacts,
    customerUnprovenSummary,
    safetyReportReadinessState,
    safetyWorkflowProgress,
    proofReviewCorrelationLine,
    packageExportSummary,
    visibleProofScopeFacts,
    appliedProofScopeSummary,
    technicalDetailsModel,
  } = useAcceptanceProofViewModel({
    proofPackPreviewData,
    reportScopeIncidents,
    reviewScopeIncidents,
    selectedReviewIncidentIds,
    includedIncidentCaseCount,
    selectedReviewIncidentOptions,
    currentResolutionProofCase,
    explicitProofPolicyId,
    caseTotalIncidents,
    proofScopedZoneLabel,
    proofScopedPatternLabel,
    pedestrianZoneHero,
    proofRiskPatternSummary,
    proofTopHotspotLabel,
    proofDominantPatternLabel,
    hasReadinessDecision,
    actionRecorded,
    outcomeRecorded,
    proofPageState,
    evidenceBundleReady,
    evidenceBundleCreating,
    evidenceBundleFailed,
    safetyActionsForUi,
    operationalFollowThroughModel,
    supportPackageGeneratedAt,
    isLoopedDemoProofCase,
    anchorIncidentCaseLabel,
    hasAppliedIncidentSelection,
    proofPackExists,
    proofScopeFacts,
    policyTypeOptions: _policyTypeOptions,
    zoneOptions: _zoneOptions,
    incidentTypeHint: String(
      searchParams.get('incident_type')
      || canonicalProofIncidentType
      || reportPolicyType
      || '',
    ).trim(),
    proofScopePolicyTypeDraft,
    proofScopeZoneDraft,
    proofScopeFromDraft,
    proofScopeToDraft,
    reportSiteId,
    canonicalProofSiteId,
    scopedSiteId,
    reportZoneId,
    canonicalProofZoneId,
    reportDeviceId,
    canonicalProofDeviceId,
    filterCameraId,
    technicalDetailsBackPath,
    reviewOutcomeRollup,
    reviewOutcomeRollupLoading,
    reviewOutcomeHistory,
    reviewOutcomeHistoryLoading,
    ownershipRollup,
    ownershipRollupLoading,
    escalationRollup,
    escalationRollupLoading,
    correctiveActionRollup,
    correctiveActionRollupLoading,
    measurementRollup,
    measurementRollupLoading,
    actionSnapshotRollup,
    actionSnapshotRollupLoading,
    evidenceConfidenceSummary,
  });

  const [reportFiltersExpanded, setReportFiltersExpanded] = useState(false);

  const currentSelectionSummary = useMemo(() => {
    const rawZone = _zoneOptions.find((option) => option.value === proofScopeZoneDraft)?.label
      || proofScopeZoneDraft
      || currentResolutionProofCase.zone
      || '';
    const zone = normalizeSafetyReportAreaLabel(customerFacingAreaLabel(rawZone) || rawZone);
    const from = formatTimestamp(toIsoFromLocalInput(proofScopeFromDraft) || proofScopeFromDraft) || 'start time not set';
    const to = formatTimestamp(toIsoFromLocalInput(proofScopeToDraft) || proofScopeToDraft) || 'end time not set';
    if (handoffParams.isUpdatedScan) {
      return `Updated Safety Scan scope covers new activity after the latest Signed Safety Scan (${from} → ${to}). Adjust if needed, then confirm scope before building.`;
    }
    return formatSafetyReportSelectionSummary({ areaLabel: zone, fromLabel: from, toLabel: to });
  }, [_zoneOptions, currentResolutionProofCase.zone, handoffParams.isUpdatedScan, proofScopeFromDraft, proofScopeToDraft, proofScopeZoneDraft]);

  const reviewRecordCameraOptionsResolved = useMemo(
    () => mergeCameraOptionsForScope(reviewRecordCameraOptions, {
      cameraId: activeRepresentativeCase?.cameraId || filterCameraId || reportDeviceId,
      cameraLabel: activeRepresentativeCase?.cameraLabel
        || lookupLabeledValue(_cameraNameById, filterCameraId || reportDeviceId || '')
        || '',
    }),
    [
      activeRepresentativeCase,
      _cameraNameById,
      filterCameraId,
      reportDeviceId,
      reviewRecordCameraOptions,
    ],
  );

  const reviewRecordCameraSelectValue = useMemo(
    () => resolveCameraFilterSelectValue(
      reviewRecordCameraFilter,
      reviewRecordCameraOptionsResolved,
      activeRepresentativeCase?.cameraLabel,
    ),
    [activeRepresentativeCase?.cameraLabel, reviewRecordCameraFilter, reviewRecordCameraOptionsResolved],
  );

  const reportFiltersScopedToCamera = Boolean(String(reviewRecordCameraSelectValue || '').trim());

  const scopedZoneOptionsForReport = useMemo(
    () => buildZoneOptionsForCamera({
      allZones: _allZones,
      cameraId: reviewRecordCameraSelectValue,
      incidentZones: selectableReviewIncidents.map((option) => ({
        zoneId: option.zoneId,
        areaLabel: option.areaLabel || option.zoneLabel,
      })),
    }),
    [_allZones, reviewRecordCameraSelectValue, selectableReviewIncidents],
  );

  const scopedPolicyTypeOptionsForReport = useMemo(
    () => buildPolicyTypeOptionsForCamera({
      allPolicies: _allPolicies,
      allZones: _allZones,
      cameraId: reviewRecordCameraSelectValue,
      incidentPolicyTypes: [
        ...selectableReviewIncidents.map((option) => ({
          policyType: option.policyType,
          policyLabel: option.policyLabel || option.patternLabel,
        })),
        ...reviewRecordPolicyOptions.map((option) => ({
          policyType: option.value,
          policyLabel: option.label,
        })),
      ],
    }),
    [
      _allPolicies,
      _allZones,
      reviewRecordCameraSelectValue,
      reviewRecordPolicyOptions,
      selectableReviewIncidents,
    ],
  );

  const proofScopePolicySelectValue = useMemo(
    () => resolveProofScopePolicySelectValue(
      proofScopePolicyTypeDraft,
      scopedPolicyTypeOptionsForReport,
      _allPolicies,
    ),
    [_allPolicies, proofScopePolicyTypeDraft, scopedPolicyTypeOptionsForReport],
  );

  const handleReviewRecordCameraFilterChange = useCallback((value: string) => {
    setReviewRecordCameraFilter(value);
    setReviewRecordPolicyFilter('');
  }, []);

  const customerFacingPatternLabelResolved = useMemo(
    () => resolveCustomerFacingPatternLabel({
      preferredLabel: visibleRiskPatternDominantLabel || proofScopedPatternLabel,
      policyTypeId: proofScopePolicySelectValue || proofScopePolicyTypeDraft || reportPolicyType || canonicalProofIncidentType,
      incidents: reportScopeIncidents,
      policyTypeOptions: scopedPolicyTypeOptionsForReport.length > 0
        ? scopedPolicyTypeOptionsForReport
        : _policyTypeOptions,
    }),
    [
      _policyTypeOptions,
      canonicalProofIncidentType,
      proofScopePolicySelectValue,
      proofScopePolicyTypeDraft,
      proofScopedPatternLabel,
      reportPolicyType,
      reportScopeIncidents,
      scopedPolicyTypeOptionsForReport,
      visibleRiskPatternDominantLabel,
    ],
  );

  const scopeCarriedFromReview = Boolean(
    handoffParams.isUpdatedScan
    || (hasAppliedIncidentSelection && String(explicitAnchorIncidentId || currentProofScopeCaseId || '').trim()),
  );

  const reportScopeDisplayLines = useMemo(() => {
    const cameraOption = reviewRecordCameraOptionsResolved.find(
      (option) => option.value === reviewRecordCameraSelectValue,
    );
    const camera = resolveReportScopeCameraLabel(
      cameraOption?.label || activeRepresentativeCase?.cameraLabel || handoffParams.cameraId || '',
      isSafetyReportBuilding(proofPageState),
    );
    const rawZone = scopedZoneOptionsForReport.find((option) => option.value === proofScopeZoneDraft)?.label
      || _zoneOptions.find((option) => option.value === proofScopeZoneDraft)?.label
      || proofScopeZoneDraft
      || activeRepresentativeCase?.areaLabel
      || currentResolutionProofCase.zone
      || handoffParams.zoneId
      || '';
    const area = resolveReportScopeAreaLabel(
      customerFacingAreaLabel(rawZone) || rawZone,
      isSafetyReportBuilding(proofPageState),
    );
    const from = formatTimestamp(toIsoFromLocalInput(proofScopeFromDraft) || proofScopeFromDraft) || 'start not set';
    const to = formatTimestamp(toIsoFromLocalInput(proofScopeToDraft) || proofScopeToDraft) || 'end not set';
    if (safetyReportTypeMode === 'hazard_class') {
      const hazardOption = resolveHazardClassOption(hazardClassKey);
      return buildHazardClassReportScopeDisplayLines({
        hazardClassDisplayName: hazardOption?.displayName || hazardClassKey,
        cameraLabel: camera,
        areaLabel: area,
        startLabel: from,
        endLabel: to,
      });
    }
    const patternLabel = resolveReportScopePatternLabel(
      customerFacingPatternLabelResolved || handoffParams.riskPatternLabel || '',
      isSafetyReportBuilding(proofPageState),
    );
    if (handoffParams.isUpdatedScan) {
      const signedScanTimestamp = formatTimestamp(
        handoffParams.newActivityWindowStart || handoffParams.scanScopeStart || '',
      ) || 'Not recorded';
      const signedScanReference = handoffParams.latestSignedScanId
        ? `${handoffParams.latestSignedScanId}${signedScanTimestamp !== 'Not recorded' ? ` · signed ${signedScanTimestamp}` : ''}`
        : signedScanTimestamp;
      const linkedSafetyCaseReference = String(
        handoffParams.safetyCaseId || currentProofScopeCaseId || explicitAnchorIncidentId || '',
      ).trim() || 'Not linked yet';
      return [
        { label: 'Scope type', value: 'New activity after latest Signed Safety Scan' },
        { label: 'From', value: from },
        { label: 'To', value: to },
        { label: 'Area', value: area },
        { label: 'Camera group', value: camera },
        { label: 'Risk pattern', value: patternLabel },
        { label: 'Linked Safety Case', value: linkedSafetyCaseReference },
        { label: 'Latest signed scan', value: signedScanReference },
      ];
    }
    return buildReportScopeDisplayLines({
      patternLabel,
      cameraLabel: camera,
      areaLabel: area,
      startLabel: from,
      endLabel: to,
      representativeCaseSummary: activeRepresentativeCase ? 'Available' : 'Representative evidence not selected',
    });
  }, [
    _zoneOptions,
    activeRepresentativeCase,
    currentProofScopeCaseId,
    currentResolutionProofCase.zone,
    customerFacingPatternLabelResolved,
    explicitAnchorIncidentId,
    handoffParams.cameraId,
    handoffParams.isUpdatedScan,
    handoffParams.latestSignedScanId,
    handoffParams.newActivityWindowStart,
    handoffParams.riskPatternLabel,
    handoffParams.safetyCaseId,
    handoffParams.scanScopeStart,
    handoffParams.zoneId,
    hazardClassKey,
    proofPageState,
    proofScopeFromDraft,
    proofScopeToDraft,
    proofScopeZoneDraft,
    reviewRecordCameraOptionsResolved,
    reviewRecordCameraSelectValue,
    safetyReportTypeMode,
    scopedZoneOptionsForReport,
  ]);

  const reportScopeResolutionHelper = useMemo(() => {
    if (!isSafetyReportBuildComplete(proofPageState)) return null;
    return reportScopeDisplayLines.some((line) => reportScopeValueNeedsSelection(line.value))
      ? REPORT_SCOPE_RESOLUTION_HELPER
      : null;
  }, [proofPageState, reportScopeDisplayLines]);

  const reportBuildingScopeSummary = useMemo(() => {
    const visibleScope = reportScopeDisplayLines
      .filter((line) => !/loading/i.test(line.value) && !reportScopeValueNeedsSelection(line.value))
      .map((line) => `${line.label}: ${line.value}`);
    return visibleScope.length > 0 ? visibleScope.join(' · ') : 'Selected scan scope';
  }, [reportScopeDisplayLines]);
  const repeatSafetyScanPath = useMemo(() => {
    if (!canConfigureScheduledScans) return null;
    if (!(handoffParams.isUpdatedScan || finalizedPersistedReport || handoffParams.latestSignedScanId)) return null;
    const scopeStart = toIsoFromLocalInput(proofScopeFromDraft) || handoffParams.scanScopeStart || null;
    const scopeEnd = toIsoFromLocalInput(proofScopeToDraft) || handoffParams.scanScopeEnd || null;
    const selectedCameraOption = reviewRecordCameraOptionsResolved.find(
      (option) => option.value === reviewRecordCameraSelectValue,
    );
    const handoffCameraOption = reviewRecordCameraOptionsResolved.find(
      (option) => option.value === handoffParams.cameraId,
    );
    const selectedZoneOption = scopedZoneOptionsForReport.find((option) => option.value === proofScopeZoneDraft)
      || _zoneOptions.find((option) => option.value === proofScopeZoneDraft);
    const handoffZoneOption = scopedZoneOptionsForReport.find((option) => option.value === handoffParams.zoneId)
      || _zoneOptions.find((option) => option.value === handoffParams.zoneId);
    const selectedCameraId = String(reviewRecordCameraSelectValue || '').trim();
    const selectedCameraLabel = String(selectedCameraOption?.label || '').trim();
    const selectedRiskType = String(proofScopePolicySelectValue || '').trim();
    const selectedPatternLabel = String(customerFacingPatternLabelResolved || '').trim();
    const selectedZoneLabel = String(
      selectedZoneOption?.label || activeRepresentativeCase?.areaLabel || currentResolutionProofCase.zone || '',
    ).trim();
    const cameraId = /^all camera groups$/i.test(selectedCameraId) ? '' : selectedCameraId;
    const cameraLabel = /^all camera groups$/i.test(selectedCameraLabel) ? '' : selectedCameraLabel;
    const riskType = /^safety event$/i.test(selectedRiskType) ? '' : selectedRiskType;
    const patternLabel = /^safety event$/i.test(selectedPatternLabel) ? '' : selectedPatternLabel;
    const zoneLabel = /^unavailable$/i.test(selectedZoneLabel) ? '' : selectedZoneLabel;
    return buildRepeatSafetyScanPath({
      sourceWorkflowId: handoffParams.workflowId || searchParams.get('workflow_id') || null,
      siteId: handoffParams.siteId || scopedSiteId,
      windowStart: scopeStart,
      windowEnd: scopeEnd,
      cameraId: cameraId || handoffParams.cameraId || null,
      cameraLabel: cameraLabel
        || handoffCameraOption?.label
        || activeRepresentativeCase?.cameraLabel
        || handoffParams.cameraId
        || null,
      zoneId: proofScopeZoneDraft || handoffParams.zoneId || null,
      zoneLabel: zoneLabel
        || handoffZoneOption?.label
        || activeRepresentativeCase?.areaLabel
        || currentResolutionProofCase.zone
        || handoffParams.zoneId
        || null,
      riskType: riskType || handoffParams.riskType || null,
      name: patternLabel
        || handoffParams.riskPatternLabel
        || riskType
        || handoffParams.workflowId
        || searchParams.get('workflow_id')
        || null,
      safetyCaseId: handoffParams.safetyCaseId || currentProofScopeCaseId || explicitAnchorIncidentId || null,
    });
  }, [
    _zoneOptions,
    activeRepresentativeCase?.cameraLabel,
    activeRepresentativeCase?.areaLabel,
    canConfigureScheduledScans,
    currentProofScopeCaseId,
    currentResolutionProofCase.zone,
    customerFacingPatternLabelResolved,
    explicitAnchorIncidentId,
    finalizedPersistedReport,
    handoffParams.cameraId,
    handoffParams.isUpdatedScan,
    handoffParams.latestSignedScanId,
    handoffParams.riskPatternLabel,
    handoffParams.riskType,
    handoffParams.safetyCaseId,
    handoffParams.scanScopeEnd,
    handoffParams.scanScopeStart,
    handoffParams.siteId,
    handoffParams.workflowId,
    handoffParams.zoneId,
    proofScopeFromDraft,
    proofScopePolicySelectValue,
    proofScopeToDraft,
    proofScopeZoneDraft,
    reviewRecordCameraOptionsResolved,
    reviewRecordCameraSelectValue,
    scopedSiteId,
    scopedZoneOptionsForReport,
    searchParams,
  ]);

  const showingPreviousBuiltReport = isSafetyReportBuilding(proofPageState) && proofPackExists;
  const showSafetyReportContent = !isSafetyReportBuilding(proofPageState) || proofPackExists;
  const workflowId = useMemo(
    () => firstDefinedString(
      searchParams.get('workflow_id'),
      _reportProjectionWorkflowState?.workflow_id,
      proofPackPreviewData?.persisted_report?.workflow_id,
      proofPackPreviewData?.evidence_pack?.workflow_anchor?.workflow_id,
      proofPackPreviewData?.evidence_pack?.workflow_anchor?.group_key,
      proofPackPreviewData?.evidence_pack?.report_projection?.workflow_anchor?.workflow_id,
      proofPackPreviewData?.evidence_pack?.report_projection?.workflow_anchor?.group_key,
    ),
    [_reportProjectionWorkflowState?.workflow_id, proofPackPreviewData, searchParams],
  );
  const safetyCasesHomePath = useMemo(() => {
    const query = new URLSearchParams();
    if (scopedOrgId) query.set('org_id', scopedOrgId);
    if (canonicalProofSiteId || scopedSiteId) query.set('site_id', canonicalProofSiteId || scopedSiteId);
    const suffix = query.toString();
    return suffix ? `/acceptance/cases?${suffix}` : '/acceptance/cases';
  }, [canonicalProofSiteId, scopedOrgId, scopedSiteId]);
  const [linkedSafetyCases, setLinkedSafetyCases] = useState<SafetyCaseRecord[]>([]);
  const [linkedSafetyCasesLoading, setLinkedSafetyCasesLoading] = useState(() => Boolean(
    String(searchParams.get('safety_case_id') || searchParams.get('finding_id') || searchParams.get('report_id') || '').trim(),
  ));
  const reportScopeHierarchy = useMemo(() => {
    const workflowId = String(
      proofPackPreviewData?.persisted_report?.workflow_id
      || proofPackPreviewData?.persisted_report?.workflow_anchor?.workflow_id
      || handoffParams.workflowId
      || '',
    ).trim();
    const persistedScope = (proofPackPreviewData?.persisted_report?.workflow_anchor?.scope || {}) as Record<string, unknown>;
    const cameraId = resolveScanCameraId({
      scopeCameraId: String(persistedScope.camera_id || '').trim() || null,
      workflowId,
      linkedCameraId: linkedSafetyCases[0]?.camera_id || activeRepresentativeCase?.cameraId || null,
      filterCameraId: reviewRecordCameraSelectValue || null,
    });
    const cameraOption = reviewRecordCameraOptionsResolved.find(
      (option) => option.value === cameraId || option.value === reviewRecordCameraSelectValue,
    );
    const camera = resolveReportScopeCameraLabel(
      cameraId || cameraOption?.label || activeRepresentativeCase?.cameraLabel || '',
      isSafetyReportBuilding(proofPageState),
    );
    const zoneId = String(
      persistedScope.zone_id
      || (Array.isArray(persistedScope.zones) ? persistedScope.zones[0] : '')
      || parseWorkflowScope(workflowId).zone_id
      || proofScopeZoneDraft
      || '',
    ).trim();
    const rawZone = customerScanZoneLabel(zoneId)
      || scopedZoneOptionsForReport.find((option) => option.value === proofScopeZoneDraft)?.label
      || _zoneOptions.find((option) => option.value === proofScopeZoneDraft)?.label
      || proofScopeZoneDraft
      || activeRepresentativeCase?.areaLabel
      || currentResolutionProofCase.zone
      || '';
    const area = resolveReportScopeAreaLabel(
      customerScanZoneLabel(zoneId) || customerFacingAreaLabel(rawZone) || rawZone,
      isSafetyReportBuilding(proofPageState),
    );
    const persistedStart = String(
      proofPackPreviewData?.persisted_report?.workflow_anchor?.time_window?.start
      || canonicalProofWindowStart
      || toIsoFromLocalInput(proofScopeFromDraft)
      || proofScopeFromDraft
      || '',
    ).trim();
    const persistedEnd = String(
      proofPackPreviewData?.persisted_report?.workflow_anchor?.time_window?.end
      || canonicalProofWindowEnd
      || toIsoFromLocalInput(proofScopeToDraft)
      || proofScopeToDraft
      || '',
    ).trim();
    const windowLabel = buildSafetyScanWindowLabel(persistedStart, persistedEnd, siteTimeZone);
    const from = persistedStart
      ? (windowLabel ? windowLabel.split(' → ')[0] : (formatTimestamp(persistedStart) || 'start not set'))
      : 'start not set';
    const to = persistedEnd
      ? (windowLabel ? windowLabel.split(' → ').slice(1).join(' → ') : (formatTimestamp(persistedEnd) || 'end not set'))
      : 'end not set';
    const hierarchy = buildReportScopeHierarchySummary({
      patternLabel: resolveReportScopePatternLabel(
        customerFacingPatternLabelResolved,
        isSafetyReportBuilding(proofPageState),
      ),
      cameraLabel: camera,
      areaLabel: area,
      startLabel: from,
      endLabel: to,
      representativeCase: activeRepresentativeCase,
    });
    return {
      ...hierarchy,
      timeWindowLabel: windowLabel || hierarchy.timeWindowLabel,
    };
  }, [
    _zoneOptions,
    activeRepresentativeCase,
    canonicalProofWindowEnd,
    canonicalProofWindowStart,
    currentResolutionProofCase.zone,
    customerFacingPatternLabelResolved,
    handoffParams.workflowId,
    linkedSafetyCases,
    proofPackPreviewData?.persisted_report,
    proofPageState,
    proofScopeFromDraft,
    proofScopeToDraft,
    proofScopeZoneDraft,
    reviewRecordCameraOptionsResolved,
    reviewRecordCameraSelectValue,
    scopedZoneOptionsForReport,
    siteTimeZone,
  ]);
  const [pendingLinkCases, setPendingLinkCases] = useState<SafetyCaseRecord[]>([]);
  const updatedScanScopeConfirmationLabels = useMemo(() => {
    const persistedReport = proofPackPreviewData?.persisted_report;
    const cameraOption = reviewRecordCameraOptionsResolved.find(
      (option) => option.value === reviewRecordCameraSelectValue,
    );
    return formatUpdatedScanScopeLabels({
      persistedReport,
      workflowId: persistedReport?.workflow_id
        || persistedReport?.workflow_anchor?.workflow_id
        || handoffParams.workflowId
        || searchParams.get('workflow_id'),
      reportId: persistedDraftReportId,
      zoneDraft: proofScopeZoneDraft,
      policyDraft: proofScopePolicyTypeDraft,
      cameraDraft: reviewRecordCameraSelectValue || searchParams.get('camera_id'),
      windowStartDraft: proofScopeFromDraft,
      windowEndDraft: proofScopeToDraft,
      windowEdited: proofScopeWindowDraftEditedRef.current,
      siteTimeZone,
      urlCameraId: searchParams.get('camera_id'),
      canonicalWindowStart: canonicalProofWindowStart,
      canonicalWindowEnd: canonicalProofWindowEnd,
      fallbackZoneId: handoffParams.zoneId,
      fallbackCameraId: handoffParams.cameraId,
      fallbackCameraLabel: cameraOption?.label || activeRepresentativeCase?.cameraLabel || '',
      patternLabel: customerFacingPatternLabelResolved || handoffParams.riskPatternLabel || '',
      reportBuilding: isSafetyReportBuilding(proofPageState),
    });
  }, [
    activeRepresentativeCase,
    canonicalProofWindowEnd,
    canonicalProofWindowStart,
    customerFacingPatternLabelResolved,
    handoffParams.cameraId,
    handoffParams.riskPatternLabel,
    handoffParams.workflowId,
    handoffParams.zoneId,
    persistedDraftReportId,
    proofPackPreviewData?.persisted_report,
    proofPageState,
    proofScopeFromDraft,
    proofScopePolicyTypeDraft,
    proofScopeToDraft,
    proofScopeZoneDraft,
    reviewRecordCameraOptionsResolved,
    reviewRecordCameraSelectValue,
    searchParams,
    siteTimeZone,
  ]);

  const updatedScanObservationCount = useMemo(() => {
    if (!handoffParams.isUpdatedScan) return null;
    if (reviewScopeIncidentsLoading) return null;
    return reviewScopeIncidents.length;
  }, [handoffParams.isUpdatedScan, reviewScopeIncidents.length, reviewScopeIncidentsLoading]);

  const confirmUpdatedSafetyScanScope = useCallback(() => {
    setReportFiltersExpanded(false);
    applyProofScopeSelection();
  }, [applyProofScopeSelection]);

  const [linkDisambiguationOpen, setLinkDisambiguationOpen] = useState(false);
  const isCategoryReport = safetyReportTypeMode === 'hazard_class';
  const representativeEvidenceRequired = safetyReportTypeMode !== 'hazard_class';
  const representativeEvidenceSelected = !representativeEvidenceRequired || Boolean(activeRepresentativeCase);
  const requiresLinkedSafetyCases = !isCategoryReport && !finalizedPersistedReport;
  const hasLinkedSafetyCases = !requiresLinkedSafetyCases || linkedSafetyCases.length > 0;
  const reportScopeFieldsComplete = useMemo(
    () => reportScopeLinesAreComplete(reportScopeDisplayLines),
    [reportScopeDisplayLines],
  );
  const reportNarrativeScopeReady = reportScopeFieldsComplete && representativeEvidenceSelected;
  const reviewComplete = Boolean(safetyWorkflowProgress?.completedStepIds.includes('review'));
  const actionOwnerAssigned = !/not assigned|needs owner|resolving ownership/i.test(String(customerOwnerLabel || ''));
  const correctiveActionComplete = actionRecorded;
  const inheritedReadiness = useMemo(
    () => deriveInheritedWorkspaceReadiness(linkedSafetyCases[0] || null, handoffParams),
    [handoffParams, linkedSafetyCases],
  );
  const usesInheritedReadiness = linkedSafetyCases.length > 0;
  const effectiveReviewComplete = usesInheritedReadiness ? inheritedReadiness.reviewComplete : reviewComplete;
  const effectiveRepresentativeEvidenceSelected = usesInheritedReadiness
    ? inheritedReadiness.representativeEvidence.state === 'ready'
    : representativeEvidenceSelected;
  const effectiveActionOwnerAssigned = usesInheritedReadiness
    ? inheritedReadiness.actionOwnerAssigned
    : actionOwnerAssigned;
  const effectiveCorrectiveActionComplete = usesInheritedReadiness
    ? inheritedReadiness.correctiveActionRecorded
    : correctiveActionComplete;
  const effectiveOutcomeRecorded = usesInheritedReadiness
    ? inheritedReadiness.measurementReady
    : outcomeRecorded;
  const linkedCaseActionOwnerLabel = useMemo(
    () => (linkedSafetyCases[0] ? formatSafetyCaseActionOwnerLabel(linkedSafetyCases[0]) : ''),
    [linkedSafetyCases],
  );
  const displayCustomerOwnerLabel = usesInheritedReadiness && linkedSafetyCases[0]
    ? linkedCaseActionOwnerLabel
    : customerOwnerLabel;
  const displayActionOwnerAssigned = usesInheritedReadiness
    ? hasCanonicalFollowupOwner(linkedSafetyCases[0] || {})
    : actionOwnerAssigned;
  const reportPackageActionEnabled =
    canOperateOnProofPackage
    && hasMinimumReportPackageScope
    && reportScopeFieldsComplete
    && effectiveRepresentativeEvidenceSelected
    && hasLinkedSafetyCases;

  useEffect(() => {
    let cancelled = false;
    async function loadLinkedCases() {
      setLinkedSafetyCasesLoading(true);
      try {
        const resolution = await loadLinkedSafetyCasesForHandoff({
          explicitCaseId: handoffParams.safetyCaseId,
          workflowId: handoffParams.workflowId || workflowId,
          representativeIncidentId: handoffParams.representativeIncidentId,
          openSafetyCase: (safetyCaseId) => reportsApi.openSafetyCase(safetyCaseId),
          listSafetyCases: (opts) => reportsApi.listSafetyCases(opts),
        });
        if (cancelled) return;
        if (resolution.pendingSelection.length > 1) {
          setPendingLinkCases(resolution.pendingSelection);
          setLinkedSafetyCases([]);
          setLinkDisambiguationOpen(true);
        } else {
          setPendingLinkCases([]);
          setLinkedSafetyCases(resolution.linkedCases);
          setLinkDisambiguationOpen(false);
        }
      } catch {
        if (!cancelled) {
          setLinkedSafetyCases([]);
          setPendingLinkCases([]);
        }
      } finally {
        if (!cancelled) {
          setLinkedSafetyCasesLoading(false);
        }
      }
    }

    void loadLinkedCases();
    return () => {
      cancelled = true;
    };
  }, [handoffParams.representativeIncidentId, handoffParams.safetyCaseId, handoffParams.workflowId, workflowId]);

  const reportLanguagePreviewRef = useRef<HTMLDivElement | null>(null);
  const [reportLanguagePreviewHighlighted, setReportLanguagePreviewHighlighted] = useState(false);
  const safetyCaseAttestationPanelRef = useRef<SafetyCaseAttestationPanelHandle | null>(null);
  const linkedSafetyCaseWorkplaceCondition = useMemo(
    () => readWorkplaceCondition(linkedSafetyCases[0]?.scope || {}),
    [linkedSafetyCases],
  );
  const reportPayloadWorkplaceCondition = useMemo(
    () => readWorkplaceCondition(draftReportPayload || persistedReportPayload || {}),
    [draftReportPayload, persistedReportPayload],
  );
  const effectiveReportWorkplaceCondition = useMemo(
    () => (
      hasWorkplaceConditionContent(reportPayloadWorkplaceCondition)
        ? reportPayloadWorkplaceCondition
        : linkedSafetyCaseWorkplaceCondition
    ),
    [linkedSafetyCaseWorkplaceCondition, reportPayloadWorkplaceCondition],
  );
  const hasReportWorkplaceCondition = useMemo(
    () => hasWorkplaceConditionContent(effectiveReportWorkplaceCondition),
    [effectiveReportWorkplaceCondition],
  );
  const reportLanguagePreview = useMemo(
    () => buildReportLanguagePreview({
      conditionSummary: effectiveReportWorkplaceCondition.condition_summary,
      whyThisMattersText: effectiveReportWorkplaceCondition.why_this_matters_text,
      likelyContributingCondition: effectiveReportWorkplaceCondition.likely_contributing_condition,
      contributingConditionStatus: effectiveReportWorkplaceCondition.contributing_condition_status,
      workerSupportNoteEnabled: effectiveReportWorkplaceCondition.worker_support_note_enabled !== false,
    }),
    [effectiveReportWorkplaceCondition],
  );
  const reportLanguagePreviewSnapshot = useMemo(
    () => readReportLanguagePreviewSnapshot(draftReportPayload || persistedReportPayload),
    [draftReportPayload, persistedReportPayload],
  );
  const reportLanguagePreviewConfirmedCurrent = useMemo(
    () => (
      hasConfirmedLanguagePreviewSnapshot(reportLanguagePreviewSnapshot)
      && languagePreviewMatchesWorkplaceCondition(
        reportLanguagePreviewSnapshot,
        effectiveReportWorkplaceCondition,
      )
    ),
    [effectiveReportWorkplaceCondition, reportLanguagePreviewSnapshot],
  );
  const reportConditionTypeLabel = useMemo(
    () => resolveSiteFlowConditionTypeLabel(effectiveReportWorkplaceCondition.condition_type) || 'Not selected',
    [effectiveReportWorkplaceCondition.condition_type],
  );
  const [workflowActionsState, setWorkflowActionsState] = useState<{
    active_actions: WorkflowCorrectiveAction[];
    completed_actions: WorkflowCorrectiveAction[];
    cancelled_actions: WorkflowCorrectiveAction[];
    evidence_summary: WorkflowCorrectiveActionEvidenceSummary | null;
  }>({
    active_actions: [],
    completed_actions: [],
    cancelled_actions: [],
    evidence_summary: null,
  });
  const [workflowImpactState, setWorkflowImpactState] = useState<WorkflowImpactSummary | null>(null);
  const [workflowActionTitleDraft, setWorkflowActionTitleDraft] = useState('');
  const [workflowActionAssigneeDraft, setWorkflowActionAssigneeDraft] = useState('');
  const [workflowActionDueDateDraft, setWorkflowActionDueDateDraft] = useState('');
  const [workflowEvidenceDrafts, setWorkflowEvidenceDrafts] = useState<Record<string, string>>({});
  const [workflowSectionBusy, setWorkflowSectionBusy] = useState(false);
  const [workflowSectionError, setWorkflowSectionError] = useState('');
  const [workflowClaimReadinessState, setWorkflowClaimReadinessState] = useState<WorkflowClaimReadinessResponse | null>(null);

  const projectedWorkflowClaimReadiness = useMemo(() => {
    const projected = _reportProjectionWorkflowState?.claim_readiness;
    return projected && typeof projected === 'object'
      ? ({ ok: true, ...projected } as WorkflowClaimReadinessResponse)
      : null;
  }, [_reportProjectionWorkflowState?.claim_readiness]);

  const effectiveWorkflowClaimReadiness = useMemo<WorkflowClaimReadinessResponse | null>(() => {
    const liveHasControls = Boolean(
      workflowClaimReadinessState
      && (
        Array.isArray(workflowClaimReadinessState.controls)
        || typeof workflowClaimReadinessState.total_count === 'number'
      ),
    );
    if (liveHasControls) return workflowClaimReadinessState;
    return projectedWorkflowClaimReadiness || workflowClaimReadinessState;
  }, [projectedWorkflowClaimReadiness, workflowClaimReadinessState]);

  const claimReadinessControls = useMemo<ClaimReadinessControlSummary[]>(
    () => (Array.isArray(effectiveWorkflowClaimReadiness?.controls) ? effectiveWorkflowClaimReadiness?.controls || [] : []),
    [effectiveWorkflowClaimReadiness],
  );
  const hasCanonicalClaimReadiness = useMemo(
    () => claimReadinessControls.length > 0 || Number(effectiveWorkflowClaimReadiness?.total_count || 0) > 0,
    [claimReadinessControls.length, effectiveWorkflowClaimReadiness?.total_count],
  );
  const claimReadinessControlsById = useMemo(
    () => new Map(
      claimReadinessControls
        .map((control) => [String(control.control_id || '').trim(), control] as const)
        .filter(([controlId]) => Boolean(controlId)),
    ),
    [claimReadinessControls],
  );
  const claimReadinessSummaryLabel = useMemo(() => {
    if (!hasCanonicalClaimReadiness) return '';
    const resolvedCount = Number(effectiveWorkflowClaimReadiness?.resolved_count || 0);
    const totalCount = Number(
      effectiveWorkflowClaimReadiness?.total_count
      || claimReadinessControls.length
      || 0,
    );
    if (!totalCount) return '';
    return `Claim readiness: ${resolvedCount}/${totalCount} resolved`;
  }, [
    claimReadinessControls.length,
    effectiveWorkflowClaimReadiness?.resolved_count,
    effectiveWorkflowClaimReadiness?.total_count,
    hasCanonicalClaimReadiness,
  ]);
  const canonicalClaimBoundary = useMemo(
    () => (hasCanonicalClaimReadiness ? effectiveWorkflowClaimReadiness?.claim_boundary ?? null : null),
    [effectiveWorkflowClaimReadiness?.claim_boundary, hasCanonicalClaimReadiness],
  );
  const canonicalEffectivenessClaimLabel = useMemo(() => {
    if (!hasCanonicalClaimReadiness) return null;
    return buildEffectivenessClaimLabelFromBoundary(canonicalClaimBoundary);
  }, [canonicalClaimBoundary, hasCanonicalClaimReadiness]);
  const canonicalFailedGatesLabel = useMemo(() => {
    if (!hasCanonicalClaimReadiness) return null;
    const blockingLabels = claimReadinessControls
      .filter((control) => {
        if (control.blocking === false) return false;
        const status = String(control.status || '').trim().toLowerCase();
        return status !== 'resolved' && status !== 'not_applicable';
      })
      .map((control) => String(control.label || control.control_id || '').trim())
      .filter(Boolean);
    return blockingLabels.length ? blockingLabels.join(', ') : 'None recorded';
  }, [claimReadinessControls, hasCanonicalClaimReadiness]);

  const refreshClaimReadiness = useCallback(async () => {
    if (!workflowId) return;
    try {
      const response = await reportsApi.getWorkflowClaimReadiness(
        workflowId,
        explicitPersistedReportId || null,
      );
      if (!response?.ok) return;
      const reportScopedWorkflows = Array.isArray(response.validation_workflows)
        ? response.validation_workflows
        : [];
      if (explicitPersistedReportId && reportScopedWorkflows.length === 0) {
        const workflowResponse = await reportsApi.getWorkflowClaimReadiness(workflowId, null);
        if (workflowResponse?.ok) {
          setWorkflowClaimReadinessState(workflowResponse);
          return;
        }
      }
      setWorkflowClaimReadinessState(response);
    } catch {
      return;
    }
  }, [explicitPersistedReportId, workflowId]);

  useEffect(() => {
    const correctiveActions = _reportProjectionWorkflowState?.corrective_actions;
    const activeActions = correctiveActions?.active_actions;
    const completedActions = correctiveActions?.completed_actions;
    const cancelledActions = correctiveActions?.cancelled_actions;
    setWorkflowActionsState({
      active_actions: Array.isArray(activeActions) ? activeActions : [],
      completed_actions: Array.isArray(completedActions) ? completedActions : [],
      cancelled_actions: Array.isArray(cancelledActions) ? cancelledActions : [],
      evidence_summary: correctiveActions?.evidence_summary || null,
    });
  }, [_reportProjectionWorkflowState?.corrective_actions]);

  useEffect(() => {
    setWorkflowImpactState(_reportProjectionWorkflowState?.impact_summary || null);
  }, [_reportProjectionWorkflowState?.impact_summary]);

  const refreshWorkflowSummary = useCallback(async () => {
    if (!workflowId) return;
    setWorkflowSectionBusy(true);
    setWorkflowSectionError('');
    try {
      const [actionsResponse, impactResponse] = await Promise.all([
        reportsApi.listWorkflowCorrectiveActions(workflowId),
        reportsApi.getWorkflowImpactSummary(workflowId, {
          reportWindowStart: canonicalProofWindowStart,
          reportWindowEnd: canonicalProofWindowEnd,
        }),
      ]);
      setWorkflowActionsState({
        active_actions: Array.isArray(actionsResponse?.active_actions) ? actionsResponse.active_actions : [],
        completed_actions: Array.isArray(actionsResponse?.completed_actions) ? actionsResponse.completed_actions : [],
        cancelled_actions: Array.isArray(actionsResponse?.cancelled_actions) ? actionsResponse.cancelled_actions : [],
        evidence_summary: actionsResponse?.evidence_summary || null,
      });
      setWorkflowImpactState(impactResponse || null);
      const primaryAction = (
        actionsResponse?.active_actions?.[0]
        || actionsResponse?.completed_actions?.[0]
        || null
      ) as WorkflowCorrectiveAction | null;
      const representativeIncidentId = String(
        linkedSafetyCases[0]?.representative_incident_id
        || handoffParams.representativeIncidentId
        || '',
      ).trim();
      let measurementEnvelope: Record<string, any> = {};
      if (representativeIncidentId) {
        try {
          const incident = await incidentsApi.getIncident(representativeIncidentId);
          measurementEnvelope = (incident as any)?.envelope_metadata?.measurement || {};
        } catch {
          measurementEnvelope = {};
        }
      }
      await syncOpenValidationTasksFromLiveWorkflow({
        workflowId,
        reportId: explicitPersistedReportId || null,
        verificationMethod: primaryAction?.verification_method,
        workflowImpactSummary: impactResponse || null,
        coverageReview: measurementEnvelope.coverage_review,
        zoneControlsConfirmation: measurementEnvelope.zone_controls_confirmation,
      });
      await refreshClaimReadiness();
    } catch (error) {
      setWorkflowSectionError(
        error instanceof Error
          ? error.message
          : 'Workflow corrective actions could not be refreshed right now.',
      );
    } finally {
      setWorkflowSectionBusy(false);
    }
  }, [
    explicitPersistedReportId,
    canonicalProofWindowEnd,
    canonicalProofWindowStart,
    handoffParams.representativeIncidentId,
    linkedSafetyCases,
    refreshClaimReadiness,
    workflowId,
  ]);

  useEffect(() => {
    if (!workflowId) return;
    void refreshWorkflowSummary();
  }, [refreshWorkflowSummary, workflowId]);

  useEffect(() => {
    if (!workflowId) return;
    void refreshClaimReadiness();
  }, [refreshClaimReadiness, workflowId]);

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      void refreshClaimReadiness();
      void refreshWorkflowSummary();
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [refreshClaimReadiness, refreshWorkflowSummary]);

  const handleCreateWorkflowAction = useCallback(async () => {
    if (!workflowId || !String(workflowActionTitleDraft || '').trim()) return;
    setWorkflowSectionBusy(true);
    setWorkflowSectionError('');
    try {
      await reportsApi.createWorkflowCorrectiveAction(workflowId, {
        action_type: 'forklift_pedestrian_separation',
        title: String(workflowActionTitleDraft || '').trim(),
        assignee: String(workflowActionAssigneeDraft || '').trim() || undefined,
        due_date: toIsoFromLocalInput(workflowActionDueDateDraft) || undefined,
      });
      setWorkflowActionTitleDraft('');
      setWorkflowActionAssigneeDraft('');
      setWorkflowActionDueDateDraft('');
      await refreshWorkflowSummary();
    } catch (error) {
      setWorkflowSectionError(
        error instanceof Error
          ? error.message
          : 'The corrective action could not be created right now.',
      );
      setWorkflowSectionBusy(false);
    }
  }, [refreshWorkflowSummary, workflowActionAssigneeDraft, workflowActionDueDateDraft, workflowActionTitleDraft, workflowId]);

  const handleUpdateWorkflowActionStatus = useCallback(async (actionId: string, status: 'completed' | 'validated') => {
    if (!actionId) return;
    setWorkflowSectionBusy(true);
    setWorkflowSectionError('');
    try {
      await reportsApi.patchWorkflowCorrectiveAction(actionId, { status });
      await refreshWorkflowSummary();
    } catch (error) {
      setWorkflowSectionError(
        error instanceof Error
          ? error.message
          : 'The corrective action status could not be updated right now.',
      );
      setWorkflowSectionBusy(false);
    }
  }, [refreshWorkflowSummary]);

  const handleAddWorkflowEvidenceNote = useCallback(async (actionId: string) => {
    const description = String(workflowEvidenceDrafts[actionId] || '').trim();
    if (!actionId || !description) return;
    setWorkflowSectionBusy(true);
    setWorkflowSectionError('');
    try {
      await reportsApi.addWorkflowCorrectiveActionEvidence(actionId, {
        evidence_type: 'note',
        description,
      });
      setWorkflowEvidenceDrafts((current) => ({ ...current, [actionId]: '' }));
      await refreshWorkflowSummary();
    } catch (error) {
      setWorkflowSectionError(
        error instanceof Error
          ? error.message
          : 'The validation note could not be saved right now.',
      );
      setWorkflowSectionBusy(false);
    }
  }, [refreshWorkflowSummary, workflowEvidenceDrafts]);

  useEffect(() => {
    if (!proofFocusTarget || !hasScope || !showSafetyReportContent) return;
    const focusMap: Record<string, { surface: ProofSurfaceKey; focus: () => void }> = {
      corrective_action: { surface: 'package', focus: focusCorrectiveActionStatusSection },
      impact_measurement: { surface: 'package', focus: focusImpactMeasurementStatusSection },
      report_package: { surface: 'package', focus: focusReportPackageSection },
      final_signoff: { surface: 'package', focus: focusFinalSignoffSection },
    };
    const target = focusMap[proofFocusTarget];
    if (!target) return;
    if (activeProofSurface !== target.surface) {
      selectProofSurface(target.surface);
    }
    const timer = window.setTimeout(() => {
      target.focus();
    }, 100);
    return () => window.clearTimeout(timer);
  }, [
    activeProofSurface,
    focusCorrectiveActionStatusSection,
    focusFinalSignoffSection,
    focusImpactMeasurementStatusSection,
    focusReportPackageSection,
    hasScope,
    proofFocusTarget,
    selectProofSurface,
    showSafetyReportContent,
  ]);

  const dashboardBackPath = useMemo(() => {
    const query = searchParams.toString();
    return query ? `/acceptance/reports?${query}` : '/acceptance/reports';
  }, [searchParams]);

  const reportPackageStatusLabel = useMemo(() => {
    if (proofPageState === 'GENERATING' || artifactZipStatus === 'GENERATING' || artifactPdfStatus === 'GENERATING') {
      return 'Generating';
    }
    if (proofPageState === 'ERROR' || artifactZipStatus === 'FAILED' || artifactPdfStatus === 'FAILED') {
      return 'Build failed';
    }
    if (artifactZipStatus === 'READY' || artifactPdfStatus === 'READY' || proofPackArtifactExists) {
      return artifactZipStatus === 'READY' || artifactPdfStatus === 'READY' ? 'Ready' : 'Built';
    }
    return 'Not generated';
  }, [artifactPdfStatus, artifactZipStatus, proofPackArtifactExists, proofPageState]);

  const openScopeControls = useCallback(() => {
    setReportFiltersExpanded(true);
  }, []);

  const openSafetyCasesHome = useCallback(() => {
    navigate(safetyCasesHomePath);
  }, [navigate, safetyCasesHomePath]);

  const openInheritedReview = useCallback(() => {
    const linkedCase = linkedSafetyCases[0];
    if (handoffParams.isUpdatedScan && linkedCase) {
      navigate(buildSafetyCaseReviewNewActivityPath(linkedCase, {
        siteId: String(canonicalProofSiteId || scopedSiteId || '').trim(),
        orgId: scopedOrgId,
      }));
      return;
    }
    if (canOpenSelectedSafetyEvent && selectedIncidentReviewHref) {
      navigate(selectedIncidentReviewHref);
      return;
    }
    navigate(incidentReviewReturnHref);
  }, [
    canOpenSelectedSafetyEvent,
    canonicalProofSiteId,
    handoffParams.isUpdatedScan,
    incidentReviewReturnHref,
    linkedSafetyCases,
    navigate,
    scopedOrgId,
    scopedSiteId,
    selectedIncidentReviewHref,
  ]);

  const openVerifyScanObservations = useCallback(() => {
    const draftId = String(persistedDraftReportId || '').trim();
    if (!draftId) {
      openInheritedReview();
      return;
    }
    navigate(buildScanVerifyPath({
      reportId: draftId,
      orgId: scopedOrgId,
      siteId: String(canonicalProofSiteId || scopedSiteId || '').trim() || null,
      safetyCaseId: String(
        handoffParams.safetyCaseId
        || searchParams.get('safety_case_id')
        || linkedSafetyCases[0]?.safety_case_id
        || linkedSafetyCases[0]?.finding_id
        || '',
      ).trim() || null,
    }));
  }, [
    canonicalProofSiteId,
    handoffParams.safetyCaseId,
    linkedSafetyCases,
    navigate,
    openInheritedReview,
    persistedDraftReportId,
    scopedOrgId,
    scopedSiteId,
    searchParams,
  ]);

  const openEvidenceReview = useCallback(() => {
    openInheritedReview();
  }, [openInheritedReview]);

  const openSafetyCaseLinkDisambiguation = useCallback(() => {
    if (pendingLinkCases.length > 1) {
      setLinkDisambiguationOpen(true);
      return;
    }
    openSafetyCasesHome();
  }, [openSafetyCasesHome, pendingLinkCases.length]);

  const handleSelectLinkedSafetyCase = useCallback((item: SafetyCaseRecord) => {
    setLinkedSafetyCases([item]);
    setPendingLinkCases([]);
    setLinkDisambiguationOpen(false);
  }, []);

  const _openReviewForAction = useCallback(() => {
    navigate(incidentReviewReturnHref);
  }, [incidentReviewReturnHref, navigate]);
  const openLinkedSafetyCase = useCallback((item: SafetyCaseRecord) => {
    const caseId = String(item.safety_case_id || item.finding_id || '').trim();
    if (!caseId) {
      navigate(safetyCasesHomePath);
      return;
    }
    navigate(buildLiveSafetyCasePath({
      caseId,
      orgId: scopedOrgId,
      siteId: canonicalProofSiteId || scopedSiteId,
      workflowId: String(item.workflow_id || workflowId || '').trim() || undefined,
      incidentId: String(item.representative_incident_id || searchParams.get('incident_id') || '').trim() || undefined,
    }));
  }, [canonicalProofSiteId, navigate, safetyCasesHomePath, scopedOrgId, scopedSiteId, searchParams, workflowId]);
  const focusReportLanguagePreview = useCallback(() => {
    const section = reportLanguagePreviewRef.current;
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => {
        section.focus({ preventScroll: true });
      }, 300);
    }
    setReportLanguagePreviewHighlighted(true);
    window.setTimeout(() => {
      setReportLanguagePreviewHighlighted(false);
    }, 1200);
  }, []);
  const refreshLinkedSafetyCase = useCallback(async () => {
    const linkedCase = linkedSafetyCases[0];
    const caseId = String(linkedCase?.safety_case_id || linkedCase?.finding_id || '').trim();
    if (!caseId) return;
    try {
      const opened = await reportsApi.openSafetyCase(caseId);
      const record = opened.safety_case || opened.finding;
      if (record) {
        setLinkedSafetyCases([record]);
      }
    } catch {
      return;
    }
  }, [linkedSafetyCases]);

  const handleInlineCaseAttestationSaved = useCallback(async () => {
    await refreshLinkedSafetyCase();
    await refreshClaimReadiness();
    await refreshWorkflowSummary();
  }, [refreshClaimReadiness, refreshLinkedSafetyCase, refreshWorkflowSummary]);

  const linkedCaseAttestationImpactSummary = useMemo(
    () => (workflowImpactState ? (workflowImpactState as unknown as Record<string, unknown>) : null),
    [workflowImpactState],
  );

  const linkedCaseFollowupMeasurement = useMemo(() => {
    const linkedCase = linkedSafetyCases[0];
    if (!linkedCase) return null;
    return (
      ((linkedCase as { followup_measurement?: Record<string, unknown> }).followup_measurement)
      || linkedCase.status_snapshot?.followup_measurement
      || null
    ) as Record<string, unknown> | null;
  }, [linkedSafetyCases]);

  const inlineCaseAttestationEligible = Boolean(
    hasScope
    && hasLinkedSafetyCases
    && !isCategoryReport
    && !finalizedPersistedReport
    && linkedSafetyCases[0],
  );

  const openClaimReadinessControl = useCallback((control: ClaimReadinessControlSummary) => {
    const controlId = String(control.control_id || '').trim().toLowerCase();
    const linkedCase = linkedSafetyCases[0];
    const siteId = String(canonicalProofSiteId || scopedSiteId || '').trim();
    const reviewFocus = resolveClaimReadinessBlockerReviewFocus(controlId);
    if (reviewFocus && siteId) {
      navigate(buildClaimReadinessBlockerReviewPath({
        siteId,
        workflowId: String(linkedCase?.workflow_id || workflowId || '').trim() || undefined,
        incidentId: String(
          linkedCase?.representative_incident_id
          || searchParams.get('incident_id')
          || '',
        ).trim() || undefined,
        zoneId: String(linkedCase?.zone_id || handoffParams.zoneId || '').trim() || undefined,
        cameraId: String(linkedCase?.camera_id || handoffParams.cameraId || '').trim() || undefined,
        deviceId: String(linkedCase?.device_id || '').trim() || undefined,
        riskType: String(linkedCase?.risk_type || handoffParams.riskType || '').trim() || undefined,
        reviewFocus,
      }));
      return;
    }
    const safetyCaseFocus = resolveClaimReadinessSafetyCaseFocus(controlId);
    if (safetyCaseFocus && inlineCaseAttestationEligible) {
      safetyCaseAttestationPanelRef.current?.focusBlocker(controlId);
      return;
    }
    const caseId = String(linkedCase?.safety_case_id || linkedCase?.finding_id || '').trim();
    if (caseId) {
      navigate(buildLiveSafetyCasePath({
        caseId,
        orgId: scopedOrgId,
        siteId: siteId || undefined,
        workflowId: String(linkedCase?.workflow_id || workflowId || '').trim() || undefined,
        incidentId: String(linkedCase?.representative_incident_id || '').trim() || undefined,
        claimReadinessFocus: safetyCaseFocus || controlId,
      }));
      return;
    }
    openSafetyCasesHome();
  }, [
    canonicalProofSiteId,
    handoffParams.cameraId,
    handoffParams.riskType,
    handoffParams.zoneId,
    linkedSafetyCases,
    navigate,
    openSafetyCasesHome,
    scopedOrgId,
    scopedSiteId,
    searchParams,
    inlineCaseAttestationEligible,
    workflowId,
  ]);

  const claimReadinessBlockers = useMemo<_ReadinessBlocker[]>(() => {
    if (finalizedPersistedReport || !hasCanonicalClaimReadiness) return [];
    return claimReadinessControls
      .filter((control) => {
        if (control.blocking === false) return false;
        const status = String(control.status || '').trim().toLowerCase();
        return status && status !== 'resolved' && status !== 'not_applicable';
      })
      .map((control, index) => {
        const controlId = String(control.control_id || `claim-readiness-${index}`).trim();
        return {
          id: `claim-readiness-${controlId}`,
          code: controlId || 'claim_readiness_control',
          workflowStepLabel: _CLAIM_READINESS_WORKFLOW_STEP_LABELS[controlId] || 'Claim Readiness',
          label: _claimReadinessControlStatusLabel(control),
          nextActionText: _claimReadinessControlNextStep(control),
          ctaLabel: claimReadinessBlockerActionLabel(controlId, { inline: inlineCaseAttestationEligible }),
          onClick: () => openClaimReadinessControl(control),
        };
      });
  }, [
    claimReadinessControls,
    finalizedPersistedReport,
    hasCanonicalClaimReadiness,
    openClaimReadinessControl,
    inlineCaseAttestationEligible,
  ]);
  const showInlineCaseAttestation = Boolean(
    inlineCaseAttestationEligible
    && claimReadinessBlockers.some(
      (blocker) => resolveClaimReadinessSafetyCaseFocus(String(blocker.code || '').trim()) !== null,
    ),
  );

  const backendFinalizationBlockers = useMemo<_ReadinessBlocker[]>(() => {
    const raw = proofPackPreviewData?.persisted_report?.finalization_blockers;
    if (!Array.isArray(raw)) return [];
    const existingCodes = new Set<string>();
    const focusStatusDetails = () => {
      window.setTimeout(() => {
        document.querySelector('[data-testid="safety-report-detail-sections"]')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
    };
    const frozenPending = parseOptionalCount(
      operatorLifecycleSnapshot.review?.unreviewed ?? operatorLifecycleSnapshot.review?.pending,
    );
    const resolveBackendBlockerAction = (code: string) => {
      switch (code) {
        case 'observation_disposition_pending': {
          const pending = frozenPending ?? parsePendingCountFromBlockers(
            proofPackPreviewData?.persisted_report?.finalization_blockers || [],
          );
          return {
            ctaLabel: pending != null && pending > 0
              ? `Review required episodes — ${pending} pending`
              : 'Review required episodes',
            onClick: openVerifyScanObservations,
          };
        }
        case 'evidence_review_pending':
        case 'representative_evidence_missing':
        case 'corrective_action_not_complete':
        case 'impact_measurement_pending':
          return {
            ctaLabel: 'Open Review',
            onClick: openInheritedReview,
          };
        case 'report_language_preview_required':
          return {
            ctaLabel: 'Open Wording Preview',
            onClick: focusReportLanguagePreview,
          };
        default:
          return {
            ctaLabel: 'Review Status Details',
            onClick: focusStatusDetails,
          };
      }
    };
    return raw
      .map((item: { code?: string | null; message?: string | null }, index: number) => {
        const code = String(item?.code || '').trim();
        const message = String(item?.message || '').trim();
        if (!code && !message) return null;
        if (code === 'report_language_preview_required') return null;
        if (code === 'observation_disposition_pending' && frozenPending === 0) return null;
        if (code && existingCodes.has(code)) return null;
        if (code) existingCodes.add(code);
        const action = resolveBackendBlockerAction(code);
        return {
          id: `backend-finalization-${code || index}`,
          code: code || 'report_finalize_blocked',
          workflowStepLabel:
            code === 'observation_disposition_pending' ? 'Review' : 'Safety Scan sign-off',
          label: message || code.replace(/_/g, ' '),
          nextActionText:
            code === 'observation_disposition_pending'
              ? 'Review the required episodes in this scan window, then return to sign. This is not a review of every image.'
              : message || 'Resolve the remaining server sign-off check and try again.',
          ctaLabel: action.ctaLabel,
          onClick: action.onClick,
        } satisfies _ReadinessBlocker;
      })
      .filter((blocker): blocker is _ReadinessBlocker => Boolean(blocker));
  }, [
    focusReportLanguagePreview,
    openInheritedReview,
    openVerifyScanObservations,
    operatorLifecycleSnapshot.review,
    proofPackPreviewData?.persisted_report?.finalization_blockers,
  ]);

  const reportReadinessBlockers = useMemo<_ReadinessBlocker[]>(() => {
    if (finalizedPersistedReport) return [];
    const blockers: _ReadinessBlocker[] = [];
    // Measurement Assurance is a sign-off gate only — never a package/download blocker.
    if (!hasLinkedSafetyCases) {
      blockers.push({
        id: 'linked-safety-cases',
        code: 'linked_safety_case_missing',
        workflowStepLabel: 'Linked Safety Cases',
        label: 'No Safety Case is linked to this Draft Safety Scan yet.',
        nextActionText: 'Select at least one Safety Case before building or signing the scan.',
        ctaLabel: 'Select Safety Case',
        onClick: openSafetyCaseLinkDisambiguation,
      });
    }
    if (usesInheritedReadiness) {
      if (inheritedReadiness.representativeEvidence.state === 'needs_selection') {
        blockers.push({
          id: 'representative-evidence',
          code: 'representative_evidence_missing',
          workflowStepLabel: 'Representative Evidence',
          label: 'Representative evidence still needs to be selected in Review.',
          nextActionText: 'Open Review to select representative evidence for this Safety Case.',
          ctaLabel: 'Open Review',
          onClick: openInheritedReview,
        });
      } else if (inheritedReadiness.representativeEvidence.state === 'needs_review') {
        blockers.push({
          id: 'evidence-review',
          code: 'evidence_review_pending',
          workflowStepLabel: 'Representative Evidence',
          label: 'Representative evidence review is pending.',
          nextActionText: handoffParams.isUpdatedScan
            ? 'Review new activity after the latest Signed Safety Scan.'
            : 'Review representative evidence in the Safety Case workflow.',
          ctaLabel: 'Open Review',
          onClick: openInheritedReview,
        });
      }
    } else {
      if (!effectiveRepresentativeEvidenceSelected) {
        blockers.push({
          id: 'representative-evidence',
          code: 'representative_evidence_missing',
          workflowStepLabel: 'Representative Evidence',
          label: 'Representative evidence is pending.',
          nextActionText: 'Select representative evidence so this Draft Safety Scan can be reviewed and signed.',
          ctaLabel: 'Select Representative Evidence',
          onClick: openScopeControls,
        });
      }
      if (!effectiveReviewComplete) {
        blockers.push({
          id: 'evidence-review',
          code: 'evidence_review_pending',
          workflowStepLabel: 'Representative Evidence',
          label: 'Representative evidence review is pending.',
          nextActionText: 'Review representative evidence and decide whether this Risk Pattern should become or update a Safety Case.',
          ctaLabel: 'Open Review',
          onClick: openEvidenceReview,
        });
      }
    }
    if (!reportScopeFieldsComplete) {
      blockers.push({
        id: 'report-scope',
        code: 'report_scope_incomplete',
        workflowStepLabel: 'Scan Scope',
        label: handoffParams.isUpdatedScan
          ? 'Updated Safety Scan scope is incomplete.'
          : 'Scan scope is incomplete.',
        nextActionText: handoffParams.isUpdatedScan
          ? 'Confirm the updated scan scope (new activity after the latest Signed Safety Scan) before continuing.'
          : 'Confirm the scan scope before continuing with representative evidence review or package generation.',
        ctaLabel: handoffParams.isUpdatedScan ? 'Confirm Updated Safety Scan Scope' : 'Change Scan Scope',
        onClick: openScopeControls,
      });
    }
    if (hasCanonicalClaimReadiness) {
      blockers.push(...claimReadinessBlockers);
    } else {
      if (!effectiveActionOwnerAssigned) {
        blockers.push({
          id: 'action-owner',
          code: 'action_owner_missing',
          workflowStepLabel: 'Response Readiness',
          label: 'Response owner is missing.',
          nextActionText: 'Assign an action owner so the Safety Case response can be tracked to closure.',
          ctaLabel: 'Assign Action Owner',
          onClick: openSafetyCasesHome,
        });
      }
      if (!effectiveCorrectiveActionComplete) {
        blockers.push({
          id: 'corrective-action',
          code: 'corrective_action_not_complete',
          workflowStepLabel: 'Response Readiness',
          label: 'Corrective action has not been completed.',
          nextActionText: 'Record the response owner, corrective action, or explicit reason no corrective action is included.',
          ctaLabel: 'Record Corrective Action',
          onClick: openSafetyCasesHome,
        });
      }
      if (!effectiveOutcomeRecorded) {
        blockers.push({
          id: 'impact-measurement',
          code: 'impact_measurement_pending',
          workflowStepLabel: 'Measurement Readiness',
          label: 'Measurement is pending.',
          nextActionText: 'Record measurement evidence or mark that effectiveness is not claimed before sign-off.',
          ctaLabel: 'Record Measurement',
          onClick: openSafetyCasesHome,
        });
      }
    }
    if (hasReportWorkplaceCondition && !String(effectiveReportWorkplaceCondition.condition_type || '').trim()) {
      blockers.push({
        id: 'condition-type',
        code: 'condition_type_required',
        workflowStepLabel: 'Workplace Condition',
        label: 'Workplace condition type is missing.',
        nextActionText: 'Open the linked Safety Case and classify the recurring workplace condition before sign-off.',
        ctaLabel: linkedSafetyCases[0] ? 'Open Safety Case' : 'Open Safety Cases',
        onClick: linkedSafetyCases[0]
          ? () => openLinkedSafetyCase(linkedSafetyCases[0])
          : openSafetyCasesHome,
      });
    }
    if (
      hasReportWorkplaceCondition
      && String(effectiveReportWorkplaceCondition.condition_type || '').trim()
      && !reportLanguagePreviewConfirmedCurrent
    ) {
      blockers.push({
        id: 'report-language-preview',
        code: 'report_language_preview_required',
        workflowStepLabel: 'Report Language',
        label: 'Final report wording has not been reviewed and confirmed.',
        nextActionText: 'Review the Report Language Preview and confirm the wording before sign-off.',
        ctaLabel: 'Review Report Language',
        onClick: focusReportLanguagePreview,
      });
    }
    if (!proofPackExists && workflowLinkedDraftModeEligible && blockers.length === 0 && proofPageState === 'ERROR') {
      blockers.push({
        id: 'zip-generation',
        code: 'report_zip_missing',
        workflowStepLabel: 'Scan Package',
        label: reportError || 'Scan package could not be built.',
        nextActionText: 'Retry generating the Scan Package so the report can be delivered.',
        ctaLabel: 'Retry Scan Package',
        onClick: primaryIncidentEvidencePackageAction,
      });
    } else if (!proofPackExists && !workflowLinkedDraftModeEligible) {
      const blockedByEarlierSteps = blockers.length > 0;
      blockers.push({
        id: 'zip-generation',
        code: 'report_zip_missing',
        workflowStepLabel: 'Scan Package',
        label: 'Scan package has not been built.',
        nextActionText: 'Build the Scan Package after the blocking readiness items above are resolved.',
        ctaLabel: 'Build Scan Package',
        disabled: blockedByEarlierSteps,
        onClick: primaryIncidentEvidencePackageAction,
      });
    }
    const seenCodes = new Set(blockers.map((blocker) => String(blocker.code || '').trim()).filter(Boolean));
    backendFinalizationBlockers.forEach((blocker) => {
      const code = String(blocker.code || '').trim();
      if (code && seenCodes.has(code)) return;
      if (code) seenCodes.add(code);
      blockers.push(blocker);
    });
    return blockers;
  }, [
    backendFinalizationBlockers,
    effectiveActionOwnerAssigned,
    effectiveCorrectiveActionComplete,
    effectiveOutcomeRecorded,
    effectiveRepresentativeEvidenceSelected,
    effectiveReviewComplete,
    finalizedPersistedReport,
    claimReadinessBlockers,
    handoffParams.isUpdatedScan,
    hasReportWorkplaceCondition,
    hasCanonicalClaimReadiness,
    hasLinkedSafetyCases,
    inheritedReadiness,
    linkedSafetyCases,
    openEvidenceReview,
    openLinkedSafetyCase,
    openInheritedReview,
    openSafetyCaseLinkDisambiguation,
    openSafetyCasesHome,
    openScopeControls,
    primaryIncidentEvidencePackageAction,
    proofPageState,
    proofPackExists,
    reportError,
    reportLanguagePreviewConfirmedCurrent,
    focusReportLanguagePreview,
    reportScopeFieldsComplete,
    usesInheritedReadiness,
    effectiveReportWorkplaceCondition.condition_type,
    workflowLinkedDraftModeEligible,
  ]);
  const workflowReadinessCleared = useMemo(
    () => finalizedPersistedReport || reportReadinessBlockers.length === 0,
    [finalizedPersistedReport, reportReadinessBlockers.length],
  );
  const reportReadyForSignOff = useMemo(
    () => finalizedPersistedReport || (
      workflowReadinessCleared
      && packageBuilt
      && measurementGateAdmitted === true
      && fieldVerificationAllowed === true
    ),
    [fieldVerificationAllowed, finalizedPersistedReport, measurementGateAdmitted, packageBuilt, workflowReadinessCleared],
  );
  const canSignSafetyScan = reportReadyForSignOff;
  const safetyScanSignOffSummary = useMemo(() => {
    if (finalizedPersistedReport) {
      return 'This Safety Scan is signed. The record is frozen; new Safety Case activity is tracked separately.';
    }
    if (workflowReadinessCleared && !proofPackExists) {
      return proofPackGenerationInProgress
        ? 'The workflow is complete. The scan package is being built now.'
        : 'The workflow is complete. Generate the scan package when you are ready.';
    }
    if (reportReadyForSignOff) {
      return `${SIGN_SAFETY_SCAN_COPY.helper} Download the package first if you want to review it.`;
    }
    return `Complete ${reportReadinessBlockers.length} remaining step${reportReadinessBlockers.length === 1 ? '' : 's'} below, then sign this Safety Scan.`;
  }, [finalizedPersistedReport, proofPackExists, proofPackGenerationInProgress, reportReadinessBlockers.length, reportReadyForSignOff, workflowReadinessCleared]);

  const reportStatusBannerBody = useMemo(() => {
    if (finalizedPersistedReport) {
      return 'This signed-off Safety Scan is being shown from the persisted artifact. Follow-up Safety Case activity is tracked separately and does not change this frozen scan.';
    }
    if (!hasLinkedSafetyCases) {
      return 'No Safety Case is linked to this Draft Safety Scan yet. Select at least one Safety Case before building or signing the scan.';
    }
    if (workflowReadinessCleared && !proofPackExists) {
      return proofPackGenerationInProgress
        ? 'This Draft Safety Scan is complete from a workflow perspective. The Scan Package is being generated now.'
        : 'This Draft Safety Scan is complete from a workflow perspective. The Scan Package is not generated yet.';
    }
    if (reportReadyForSignOff) {
      return 'This Safety Scan packages selected Safety Cases for sign-off and sharing. Representative evidence, response readiness, measurement readiness, package readiness, and sign-off are aligned.';
    }
    if (proofPackExists) {
      return `Scan package is built. Complete the ${reportReadinessBlockers.length} remaining sign-off step${reportReadinessBlockers.length === 1 ? '' : 's'} below to finalize this Safety Scan.`;
    }
    return 'This Draft Safety Scan is not ready for sign-off. Resolve the blockers below so the scan can be packaged, reviewed, and signed.';
  }, [finalizedPersistedReport, hasLinkedSafetyCases, proofPackExists, proofPackGenerationInProgress, reportReadinessBlockers.length, reportReadyForSignOff, workflowReadinessCleared]);

  const reportStatusBannerChips = useMemo(() => {
    if (finalizedPersistedReport) {
      return Array.from(new Set(['Signed Safety Scan', `Scan Package: ${reportPackageStatusLabel}`]));
    }
    const chips: string[] = [];
    if (claimReadinessSummaryLabel) chips.push(claimReadinessSummaryLabel);
    if (!hasLinkedSafetyCases) chips.push('Linked Safety Case required');
    if (reportReadyForSignOff) chips.push('Ready for sign-off');
    else if (workflowReadinessCleared) chips.push('Preparing report');
    if (!effectiveReviewComplete) chips.push(reportNarrativeScopeReady ? 'Representative evidence pending' : 'Needs review');
    if (!reportScopeFieldsComplete) chips.push('Scan scope incomplete');
    if (!representativeEvidenceSelected) chips.push('Representative evidence missing');
    if (!hasCanonicalClaimReadiness) {
      if (!actionOwnerAssigned) chips.push('Response owner missing');
      if (!outcomeRecorded) chips.push('Measurement pending');
    } else if (claimReadinessBlockers.length > 0) {
      chips.push('Claim readiness blocked');
    }
    if (hasReportWorkplaceCondition && !String(effectiveReportWorkplaceCondition.condition_type || '').trim()) {
      chips.push('Condition type required');
    }
    if (hasReportWorkplaceCondition && !reportLanguagePreviewConfirmedCurrent && reportLanguagePreview.sections.length === 0) {
      chips.push('Language preview required');
    }
    chips.push(`Scan Package: ${reportPackageStatusLabel}`);
    return Array.from(new Set(chips));
  }, [
    actionOwnerAssigned,
    claimReadinessBlockers.length,
    claimReadinessSummaryLabel,
    effectiveReportWorkplaceCondition.condition_type,
    finalizedPersistedReport,
    hasCanonicalClaimReadiness,
    hasLinkedSafetyCases,
    hasReportWorkplaceCondition,
    outcomeRecorded,
    reportNarrativeScopeReady,
    reportPackageStatusLabel,
    reportLanguagePreviewConfirmedCurrent,
    reportLanguagePreview.sections.length,
    reportScopeFieldsComplete,
    reportReadyForSignOff,
    representativeEvidenceSelected,
    effectiveReviewComplete,
    workflowReadinessCleared,
  ]);
  const effectiveSafetyReportReadinessState = useMemo(() => {
    if (isCategoryReport) return safetyReportReadinessState;
    if (finalizedPersistedReport) return 'ready';
    return reportReadyForSignOff ? 'ready' : 'blocked';
  }, [finalizedPersistedReport, isCategoryReport, reportReadyForSignOff, safetyReportReadinessState]);

  const reportReadinessActionLabel = useMemo(() => {
    if (finalizedPersistedReport) return 'Safety Scan signed';
    if (safetyScanFinalizing) return 'Signing Safety Scan...';
    return 'Sign Safety Scan';
  }, [finalizedPersistedReport, safetyScanFinalizing]);

  const reportReadinessActionDisabled = finalizedPersistedReport
    || safetyScanFinalizing
    || proofPageState === 'GENERATING'
    || !hasLinkedSafetyCases
    || !persistedDraftReportId
    || effectiveSafetyReportReadinessState !== 'ready';

  const reportReadinessActionHelper = useMemo(() => {
    if (finalizedPersistedReport) {
      return 'This Safety Scan is already signed off. New Safety Case activity does not change the persisted scan artifact.';
    }
    if (!hasLinkedSafetyCases) {
      return 'Select at least one linked Safety Case before building or signing this Draft Safety Scan.';
    }
    if (proofPageState === 'GENERATING') {
      return 'The Scan Package is still being generated for the selected scan scope.';
    }
    if (workflowReadinessCleared && !proofPackExists) {
      return proofPackGenerationInProgress
        ? 'The workflow is complete. The Scan Package is being generated now.'
        : 'The workflow is complete. Generate the Scan Package when you are ready.';
    }
    if (effectiveSafetyReportReadinessState !== 'ready') {
      return 'This Draft Safety Scan is not ready for sign-off. Resolve the blockers below so the scan can be packaged, reviewed, and signed.';
    }
    return SIGN_SAFETY_SCAN_COPY.helper;
  }, [effectiveSafetyReportReadinessState, finalizedPersistedReport, hasLinkedSafetyCases, proofPackExists, proofPackGenerationInProgress, proofPageState, workflowReadinessCleared]);

  useEffect(() => {
    if (!reportFiltersScopedToCamera) return;
    const zoneDraft = String(proofScopeZoneDraft || '').trim();
    const zoneValid = !zoneDraft || scopedZoneOptionsForReport.some((option) => option.value === zoneDraft);
    if (zoneValid) return;
    const fromCase = String(activeRepresentativeCase?.zoneId || '').trim();
    const nextZone = (fromCase && scopedZoneOptionsForReport.some((option) => option.value === fromCase))
      ? fromCase
      : (scopedZoneOptionsForReport[0]?.value || '');
    if (nextZone !== proofScopeZoneDraft) {
      setProofScopeZoneDraft(nextZone);
    }
  }, [
    activeRepresentativeCase?.zoneId,
    proofScopeZoneDraft,
    reportFiltersScopedToCamera,
    scopedZoneOptionsForReport,
  ]);

  useEffect(() => {
    if (!reportFiltersScopedToCamera) return;
    const policyDraft = String(proofScopePolicyTypeDraft || '').trim();
    const resolved = resolveProofScopePolicySelectValue(
      policyDraft,
      scopedPolicyTypeOptionsForReport,
      _allPolicies,
    );
    if (resolved && resolved !== policyDraft) {
      setProofScopePolicyTypeDraft(resolved);
      return;
    }
    const policyValid = !policyDraft
      || scopedPolicyTypeOptionsForReport.some((option) => option.value === resolved);
    if (policyValid) return;
    const fromCase = String(activeRepresentativeCase?.policyType || '').trim();
    const nextPolicy = (fromCase && scopedPolicyTypeOptionsForReport.some((option) => option.value === fromCase))
      ? fromCase
      : (scopedPolicyTypeOptionsForReport[0]?.value || '');
    if (nextPolicy && nextPolicy !== proofScopePolicyTypeDraft) {
      setProofScopePolicyTypeDraft(nextPolicy);
    }
  }, [
    _allPolicies,
    activeRepresentativeCase?.policyType,
    proofScopePolicyTypeDraft,
    reportFiltersScopedToCamera,
    scopedPolicyTypeOptionsForReport,
  ]);

  useEffect(() => {
    if (!activeRepresentativeCase) return;
    const nextValue = resolveCameraFilterSelectValue(
      reviewRecordCameraFilter,
      reviewRecordCameraOptionsResolved,
      activeRepresentativeCase.cameraLabel,
    );
    const cameraId = String(activeRepresentativeCase.cameraId || '').trim();
    const resolved = nextValue || cameraId || String(activeRepresentativeCase.cameraLabel || '').trim();
    if (resolved && resolved !== reviewRecordCameraFilter) {
      setReviewRecordCameraFilter(resolved);
    }
    const zoneId = String(activeRepresentativeCase.zoneId || '').trim();
    if (zoneId && !String(proofScopeZoneDraft || '').trim()) {
      setProofScopeZoneDraft(zoneId);
    }
    const policyType = normalizeProofScopePolicyType(
      String(activeRepresentativeCase.policyType || '').trim(),
      _allPolicies,
    );
    if (policyType && !String(proofScopePolicyTypeDraft || '').trim()) {
      setProofScopePolicyTypeDraft(policyType);
    }
  }, [
    _allPolicies,
    activeRepresentativeCase,
    proofScopePolicyTypeDraft,
    proofScopeZoneDraft,
    reviewRecordCameraFilter,
    reviewRecordCameraOptionsResolved,
  ]);

  const appliedReportScopeType = useMemo(
    () => parseSafetyReportTypeFromSearchParams(searchParams),
    [searchParams],
  );

  const proofScopeDirty = useMemo(
    () =>
      String(proofScopeZoneDraft || '').trim() !== String(reportZoneId || canonicalProofZoneId || '').trim()
      || String(proofScopePolicyTypeDraft || '').trim() !== String(reportPolicyType || canonicalProofIncidentType || '').trim()
      || normalizeScopeWindowValue(proofScopeFromDraft) !== normalizeScopeWindowValue(reportFrom || canonicalProofWindowStart || '')
      || normalizeScopeWindowValue(proofScopeToDraft) !== normalizeScopeWindowValue(reportTo || canonicalProofWindowEnd || '')
      || String(proofScopeCaseDraft || '').trim() !== currentProofScopeCaseId
      || selectedReviewIncidentIds.map((value) => String(value || '').trim()).filter(Boolean).join(',') !== currentSelectedIncidentIdsKey
      || safetyReportTypeMode !== appliedReportScopeType.reportTypeMode
      || (safetyReportTypeMode === 'hazard_class' && hazardClassKey !== appliedReportScopeType.hazardClassKey),
    [
      appliedReportScopeType.hazardClassKey,
      appliedReportScopeType.reportTypeMode,
      hazardClassKey,
      canonicalProofIncidentType,
      canonicalProofWindowEnd,
      canonicalProofWindowStart,
      canonicalProofZoneId,
      currentSelectedIncidentIdsKey,
      currentProofScopeCaseId,
      proofScopeCaseDraft,
      proofScopeFromDraft,
      proofScopePolicyTypeDraft,
      proofScopeToDraft,
      proofScopeZoneDraft,
      reportFrom,
      reportPolicyType,
      reportTo,
      reportZoneId,
      safetyReportTypeMode,
      selectedReviewIncidentIds,
    ],
  );

  const _trustFacts = useMemo(
    () => [
      { label: 'Evidence strength', value: evidenceConfidenceSummary || 'Unavailable' },
      { label: 'System / reliability posture', value: trustPostureLabel || 'Needs review' },
      { label: 'Residual gaps', value: evidenceActions[0]?.label || 'Reliability metrics still in progress' },
    ],
    [evidenceActions, evidenceConfidenceSummary, trustPostureLabel],
  );
  const selectedHazardClassOption = useMemo(
    () => (isCategoryReport ? resolveHazardClassOption(hazardClassKey) : null),
    [hazardClassKey, isCategoryReport],
  );
  const workflowMeasurementTrust = useMemo(
    () => (workflowImpactState?.measurement_trust && typeof workflowImpactState.measurement_trust === 'object'
      ? workflowImpactState.measurement_trust
      : null),
    [workflowImpactState],
  );
  const effectivenessClaimAllowed = useMemo(() => {
    if (hasCanonicalClaimReadiness) {
      return isEffectivenessClaimAllowedFromBoundary(canonicalClaimBoundary);
    }
    return false;
  }, [
    canonicalClaimBoundary,
    hasCanonicalClaimReadiness,
  ]);
  const measurementTrustLevelLabel = useMemo(() => {
    if (isCategoryReport) return 'Category Summary Only';
    const rawLevel = String(workflowMeasurementTrust?.level || '').trim();
    if (rawLevel) return _titleCase(rawLevel.replace(/_/g, ' '));
    return trustPostureLabel || 'Needs review';
  }, [isCategoryReport, trustPostureLabel, workflowMeasurementTrust?.level]);
  const measurementTrustSummary = useMemo(() => {
    if (isCategoryReport) {
      return String(
        selectedHazardClassOption?.claimBoundary
        || customerMeasurementBoundarySummary
        || 'Category summaries do not claim category-wide corrective-action effectiveness or risk elimination.',
      ).trim();
    }
    return String(
      workflowMeasurementTrust?.reason
      || workflowMeasurementTrust?.claim_boundary
      || customerMeasurementBoundarySummary
      || customerUnprovenSummary
      || 'Measurement trust has not been recorded yet for this report.',
    ).trim();
  }, [
    customerMeasurementBoundarySummary,
    customerUnprovenSummary,
    isCategoryReport,
    selectedHazardClassOption?.claimBoundary,
    workflowMeasurementTrust?.claim_boundary,
    workflowMeasurementTrust?.reason,
  ]);
  const measurementTrustFacts = useMemo(
    () => [
      { label: 'Trust level', value: measurementTrustLevelLabel || 'Not recorded' },
      {
        label: 'Effectiveness claim',
        value: isCategoryReport
          ? 'Not allowed category-wide'
          : (hasCanonicalClaimReadiness
            ? (canonicalEffectivenessClaimLabel || 'Not allowed')
            : 'Not allowed'),
      },
      {
        label: 'Claim boundary',
        value: (hasCanonicalClaimReadiness && String(canonicalClaimBoundary?.wording || '').trim())
          ? String(canonicalClaimBoundary?.wording || '').trim()
          : (selectedHazardClassOption?.claimBoundary
            || workflowMeasurementTrust?.claim_boundary
            || customerMeasurementBoundarySummary
            || (isCategoryReport
              ? 'Category summaries do not claim corrective-action effectiveness.'
              : 'Corrective-action effectiveness is not claimed.')),
      },
      {
        label: 'Failed gates',
        value: isCategoryReport
          ? 'Category summaries inherit workflow evidence boundaries and do not create a separate trust gate.'
          : (hasCanonicalClaimReadiness
            ? (canonicalFailedGatesLabel || 'None recorded')
            : (() => {
              const failedGates = workflowMeasurementTrust?.failed_gates;
              return Array.isArray(failedGates) && failedGates.length > 0
                ? failedGates
                  .map((gate) => String((gate as any)?.code || gate || '').trim())
                  .filter(Boolean)
                  .join(', ')
                : 'None recorded';
            })()),
      },
    ],
    [
      canonicalClaimBoundary?.wording,
      canonicalEffectivenessClaimLabel,
      canonicalFailedGatesLabel,
      customerMeasurementBoundarySummary,
      hasCanonicalClaimReadiness,
      isCategoryReport,
      measurementTrustLevelLabel,
      selectedHazardClassOption?.claimBoundary,
      workflowMeasurementTrust?.claim_boundary,
      workflowMeasurementTrust?.failed_gates,
    ],
  );
  const workflowPrimaryAction = useMemo(
    () => workflowActionsState.active_actions[0] || workflowActionsState.completed_actions[0] || null,
    [workflowActionsState.active_actions, workflowActionsState.completed_actions],
  );
  const workflowImpactSummaryLine = useMemo(() => {
    if (workflowImpactState?.status === 'waived') {
      return workflowImpactState.waiver_summary || 'Impact measurement was explicitly waived.';
    }
    const comparison = buildSafetyScanProductPresentation({
      comparison: { impact: workflowImpactState },
    }).conclusion;
    if (comparison.outcome === 'NOT COMPARABLE' || comparison.outcome === 'INCONCLUSIVE') {
      return comparison.explanation;
    }
    return customerMeasurementSummary || comparison.explanation;
  }, [customerMeasurementSummary, workflowImpactState]);

  const _correctiveActionFacts = useMemo(
    () => [
      {
        label: 'Action owner',
        value: usesInheritedReadiness
          ? (displayActionOwnerAssigned ? displayCustomerOwnerLabel : 'Action owner missing')
          : (String(workflowPrimaryAction?.assignee || '').trim() || (actionOwnerAssigned ? customerOwnerLabel : 'Action owner missing')),
      },
      {
        label: 'Corrective action',
        value: String(workflowPrimaryAction?.title || '').trim()
          || (correctiveActionComplete ? (safetyActionsForUi[0]?.label || 'Recorded') : 'Not completed yet'),
      },
      {
        label: 'Workflow status',
        value: String(workflowPrimaryAction?.status || '').trim()
          ? _titleCase(String(workflowPrimaryAction?.status || '').replace(/_/g, ' '))
          : formatTimestamp(supportPackageGeneratedAt) || operationalFollowThroughModel.summaryLine || 'No corrective workflow recorded yet',
      },
      {
        label: 'Evidence notes',
        value: workflowActionsState.evidence_summary?.total_count != null
          ? `${workflowActionsState.evidence_summary.total_count} recorded`
          : 'No validation evidence recorded yet',
      },
    ],
    [
      actionOwnerAssigned,
      correctiveActionComplete,
      customerOwnerLabel,
      displayActionOwnerAssigned,
      displayCustomerOwnerLabel,
      operationalFollowThroughModel.summaryLine,
      safetyActionsForUi,
      supportPackageGeneratedAt,
      usesInheritedReadiness,
      workflowActionsState.evidence_summary,
      workflowPrimaryAction,
    ],
  );

  const _outcomeFacts = useMemo(
    () => [
      {
        label: 'Baseline',
        value: workflowImpactState?.status === 'waived'
          ? 'Waived'
          : workflowImpactState?.measurement_status === 'sufficient_data'
          ? `${workflowImpactState.baseline_exposure_count == null ? 'Not available' : workflowImpactState.baseline_exposure_count} (${workflowImpactState.baseline_exposure_rate == null ? 'Not available' : `${workflowImpactState.baseline_exposure_rate}/day`})`
          : currentResolutionProofCase.baselineCount != null ? String(currentResolutionProofCase.baselineCount) : 'Not measured yet',
      },
      {
        label: 'Follow-up',
        value: workflowImpactState?.status === 'waived'
          ? 'Waived'
          : workflowImpactState?.measurement_status === 'sufficient_data'
          ? `${workflowImpactState.post_action_exposure_count == null ? 'Not available' : workflowImpactState.post_action_exposure_count} (${workflowImpactState.post_action_exposure_rate == null ? 'Not available' : `${workflowImpactState.post_action_exposure_rate}/day`})`
          : currentResolutionProofCase.followUpCount != null ? String(currentResolutionProofCase.followUpCount) : 'Not measured yet',
      },
      {
        label: 'Pattern reduction',
        value: workflowImpactState?.status === 'waived'
          ? (workflowImpactState.waiver_reason || 'Waived')
          : 'Observed follow-up counts are available. Effectiveness is gated separately.',
      },
    ],
    [currentResolutionProofCase, workflowImpactState],
  );
  const impactRoiSummary = useMemo(() => {
    if (workflowImpactState?.status === 'waived') {
      return workflowImpactState.waiver_summary || 'Impact measurement was explicitly waived for this workflow.';
    }
    const comparison = buildSafetyScanProductPresentation({
      comparison: { impact: workflowImpactState },
    }).conclusion;
    if (comparison.showPercent && comparison.percentReduction != null) {
      return comparison.explanation;
    }
    return comparison.explanation || 'Not enough post-action workflow data is available yet to classify corrective action impact.';
  }, [workflowImpactState]);
  const impactRoiFacts = useMemo(
    () => [
      {
        label: 'Exposure change',
        value: workflowImpactState?.measurement_status === 'sufficient_data'
          ? 'Observed'
          : workflowImpactState?.status === 'waived'
          ? 'Waived'
          : 'Insufficient data',
      },
      {
        label: 'Classification',
        value: workflowImpactState?.roi_classification
          ? _titleCase(String(workflowImpactState.roi_classification).replace(/_/g, ' '))
          : workflowImpactState?.status === 'waived'
          ? 'Waived'
          : 'Insufficient data',
      },
      {
        label: 'Confidence',
        value: workflowImpactState?.confidence_level
          ? _titleCase(String(workflowImpactState.confidence_level).replace(/_/g, ' '))
          : workflowImpactState?.measurement_status === 'sufficient_data'
          ? 'Directional'
          : 'Not yet available',
      },
      {
        label: 'Financial ROI',
        value: workflowImpactState?.financial_roi_summary
          || 'Financial ROI not calculated because corrective-action cost and avoided-incident assumptions were not provided.',
      },
      {
        label: 'Next step',
        value: workflowImpactState?.recommended_next_step
          ? _titleCase(String(workflowImpactState.recommended_next_step).replace(/_/g, ' '))
          : 'Collect more data',
      },
    ],
    [workflowImpactState],
  );

  const executiveSummaryText = useMemo(() => {
    if (!reportNarrativeScopeReady) {
      return 'Executive summary unavailable until scan scope and representative evidence are selected.';
    }
    return customerSummarySentence || customerWhatHappened;
  }, [customerSummarySentence, customerWhatHappened, reportNarrativeScopeReady]);

  const findingText = useMemo(() => {
    if (!reportScopeFieldsComplete) {
      return 'Safety Scan finding unavailable until scan scope is complete.';
    }
    return customerRiskPatternSummary;
  }, [customerRiskPatternSummary, reportScopeFieldsComplete]);

  const evidenceStrengthLabel = useMemo(
    () => customerSecondaryStatusFacts.find((fact) => /evidence strength/i.test(String(fact.label || '')))?.value || '',
    [customerSecondaryStatusFacts],
  );

  const evidenceReadinessLabel = useMemo(() => {
    if (!representativeEvidenceSelected) return 'Representative evidence not selected.';
    if (!reviewComplete) return 'Evidence readiness: Not evaluated.';
    if (/limited|low|review required/i.test(String(evidenceStrengthLabel || ''))) {
      return 'Evidence readiness: Needs more review.';
    }
    return 'Evidence readiness: Confirmed.';
  }, [evidenceStrengthLabel, representativeEvidenceSelected, reviewComplete]);

  const evidenceReadinessDetail = useMemo(() => {
    if (!representativeEvidenceSelected) {
      return 'Select an evidence record before evidence readiness can be evaluated.';
    }
    if (!reviewComplete) {
      return 'Representative evidence must be reviewed before evidence readiness can be confirmed.';
    }
    if (/limited|low|review required/i.test(String(evidenceStrengthLabel || ''))) {
      return `${evidenceStrengthLabel || 'Current evidence strength is limited.'} Additional review is still needed before this evidence can support Safety Scan sign-off.`;
    }
    return 'Representative evidence has been reviewed and can support this Draft Safety Scan for sign-off.';
  }, [evidenceStrengthLabel, representativeEvidenceSelected, reviewComplete]);

  const reportPackageFacts = useMemo(
    () => [
      { label: 'Package status', value: reportPackageStatusLabel },
      { label: 'Generated', value: formatTimestamp(supportPackageGeneratedAt) || 'Not built' },
      { label: 'Package contents', value: 'PDF Safety Scan, representative evidence, technical appendix, metadata JSON, audit trail' },
      {
        label: isCategoryReport ? 'Export readiness' : 'Safety Scan sign-off',
        value: isCategoryReport
          ? (reportPackageActionEnabled ? 'Ready to export' : 'Scope incomplete')
          : finalizedPersistedReport ? 'Completed' : reportReadyForSignOff ? 'Ready for Sign-off' : workflowReadinessCleared ? 'Preparing package' : 'Blocked',
      },
    ],
    [finalizedPersistedReport, isCategoryReport, reportPackageActionEnabled, reportPackageStatusLabel, reportReadyForSignOff, supportPackageGeneratedAt, workflowReadinessCleared],
  );
  const reportLanguageFacts = useMemo(
    () => [
      { label: 'Condition type', value: reportConditionTypeLabel },
      {
        label: 'Preview status',
        value: reportLanguagePreviewConfirmedCurrent ? 'Confirmed' : 'Needs confirmation',
      },
      {
        label: 'Last confirmed',
        value: formatTimestamp(String(reportLanguagePreviewSnapshot?.confirmed_at || '').trim()) || 'Not confirmed',
      },
    ],
    [
      reportConditionTypeLabel,
      reportLanguagePreviewConfirmedCurrent,
      reportLanguagePreviewSnapshot?.confirmed_at,
    ],
  );

  const categoryCoverageFacts = useMemo(
    () => [
      {
        label: 'Report mode',
        value: selectedHazardClassOption?.displayName
          ? `${selectedHazardClassOption.displayName} category summary`
          : 'Risk category summary',
      },
      {
        label: 'Review coverage',
        value: customerReviewStatusSummary || 'Review coverage is still being compiled.',
      },
      {
        label: 'Scope',
        value: reportBuildingScopeSummary || 'Selected category scope',
      },
      {
        label: 'Hotspot summary',
        value: customerRiskPatternSummary || customerWhatHappened || 'Hotspot summary is not available yet.',
      },
    ],
    [
      customerReviewStatusSummary,
      customerRiskPatternSummary,
      customerWhatHappened,
      reportBuildingScopeSummary,
      selectedHazardClassOption?.displayName,
    ],
  );

  const categoryActionCoverageFacts = useMemo(
    () => [
      {
        label: 'Ownership coverage',
        value: customerOwnershipSummary || 'Ownership coverage is still being resolved.',
      },
      {
        label: 'Corrective action coverage',
        value: customerCorrectiveActionSummary || 'Corrective action coverage is not available yet.',
      },
      {
        label: 'Action follow-up',
        value: customerActionSnapshotSummary || 'Action follow-up coverage has not been recorded yet.',
      },
      {
        label: 'Escalation',
        value: customerEscalationSummary || 'No escalations are recorded in this summary.',
      },
    ],
    [
      customerActionSnapshotSummary,
      customerCorrectiveActionSummary,
      customerEscalationSummary,
      customerOwnershipSummary,
    ],
  );

  const categoryImpactFacts = useMemo(
    () => [
      {
        label: 'Impact coverage',
        value: customerMeasurementSummary || 'Impact coverage is still pending for some included workflows.',
      },
      {
        label: 'Claim boundary',
        value: selectedHazardClassOption?.claimBoundary
          || customerMeasurementBoundarySummary
          || 'Category-wide effectiveness is not claimed.',
      },
      {
        label: 'Unproven claims',
        value: customerUnprovenSummary || 'This category summary does not prove accident prevention, risk elimination, or category-wide corrective-action effectiveness.',
      },
      {
        label: 'Measurement trust',
        value: measurementTrustLevelLabel || 'Category Summary Only',
      },
    ],
    [
      customerMeasurementBoundarySummary,
      customerMeasurementSummary,
      customerUnprovenSummary,
      measurementTrustLevelLabel,
      selectedHazardClassOption?.claimBoundary,
    ],
  );

  const reportLimitations = useMemo(
    () => isCategoryReport ? [
      'Category reports are on-demand summaries, not persisted draft/finalize records.',
      'Included workflows can have mixed review, corrective action, and impact states.',
      'This summary does not prove accident prevention, risk elimination, or category-wide corrective-action effectiveness.',
      'Media evidence and identifier-level details remain in Advanced Technical Details.',
      'Export readiness depends on scope completeness, not workflow Safety Scan sign-off.',
    ] : [
      'Pattern observations are not separate confirmed incidents.',
      'Safety Scan sign-off still depends on evidence review and human approval.',
      outcomeRecorded
        ? 'Impact measurement should still be validated against the recorded follow-up window.'
        : 'Impact measurement is pending until corrective action is completed or explicitly waived.',
      'Media evidence and identifier-level details remain in Advanced Technical Details.',
      'This report does not prove risk reduction until follow-up measurement is recorded.',
    ],
    [isCategoryReport, outcomeRecorded],
  );

  const workflowCurrentStageLabel = useMemo(
    () => _WORKFLOW_STEP_LABELS[String(safetyWorkflowProgress?.currentStepId || 'detected')] || 'Observed',
    [safetyWorkflowProgress?.currentStepId],
  );

  const workflowMissingLine = useMemo(() => {
    if (workflowReadinessCleared) return proofPackArtifactExists ? 'No blockers remain.' : 'Workflow blockers are clear. The scan package is not generated yet.';
    return reportReadinessBlockers.map((blocker) => blocker.label.replace(/\.$/, '')).join(' ');
  }, [proofPackArtifactExists, reportReadinessBlockers, workflowReadinessCleared]);

  const reportPackageSummary = useMemo(() => {
    if (!hasLinkedSafetyCases) {
      return 'No Safety Case is linked to this Draft Safety Scan yet. Select at least one Safety Case before building the Scan Package.';
    }
    if (!reportPackageActionEnabled) {
      return 'Build the scan package after linked Safety Cases, representative evidence, and readiness items are complete.';
    }
    if (proofPageState === 'GENERATING') {
      return 'The Scan Package is being generated for the selected scan scope.';
    }
    if (proofPageState === 'ERROR') {
      return reportError || 'The Scan Package failed and should be rebuilt.';
    }
    if (proofPackArtifactExists) {
      return 'The Scan Package is available for the selected scan scope. Package availability does not mean the Safety Scan is ready for sign-off.';
    }
    return workflowReadinessCleared
      ? 'The workflow is complete. The Scan Package is being generated automatically now.'
      : 'Build the Scan Package after linked Safety Cases, representative evidence, and readiness items are complete.';
  }, [hasLinkedSafetyCases, proofPageState, proofPackArtifactExists, reportError, reportPackageActionEnabled, workflowReadinessCleared]);

  const recommendedNextSteps = useMemo(() => {
    if (!hasLinkedSafetyCases) {
      return [
        'Select at least one Safety Case for this Draft Safety Scan.',
        'Return to Safety Cases to choose the case that should be packaged.',
        'Build the Scan Package only after a linked Safety Case and representative evidence are ready.',
      ];
    }
    if (!reportNarrativeScopeReady) {
      return [
        'Confirm the scan scope and representative evidence.',
        'Update the Safety Scan after changing scope.',
        'Return to Review after the representative evidence is selected.',
        'Build the Scan Package only after the scope is complete.',
      ];
    }
    if (workflowReadinessCleared && !proofPackArtifactExists) {
      return [
        'The workflow is complete.',
        'Generate the Scan Package when you are ready.',
        'Opening this page does not start a build.',
      ];
    }
    const items = reportReadinessBlockers.slice(0, 4).map((blocker) => blocker.nextActionText);
    if (!proofPackArtifactExists) items.push('Build the Scan Package after the blockers above are resolved.');
    return items;
  }, [hasLinkedSafetyCases, proofPackArtifactExists, reportNarrativeScopeReady, reportReadinessBlockers, workflowReadinessCleared]);

  const finalSignoffSummary = useMemo(() => {
    if (finalizedPersistedReport) {
      return 'This Safety Scan has already been signed off and is being shown from the persisted scan artifact.';
    }
    if (!hasLinkedSafetyCases) {
      return 'A Draft Safety Scan cannot be signed without at least one linked Safety Case.';
    }
    if (workflowReadinessCleared && !proofPackArtifactExists) {
      return 'This draft Safety Scan is complete from a workflow perspective. The Scan Package is being generated automatically now.';
    }
    if (reportReadyForSignOff) {
      return 'This draft Safety Scan is ready for final human review and sign-off.';
    }
    return 'Safety Scan sign-off freezes this scan only. Resolve the blockers above before marking this Safety Scan ready.';
  }, [finalizedPersistedReport, hasLinkedSafetyCases, proofPackArtifactExists, reportReadyForSignOff, workflowReadinessCleared]);
  const primaryReadinessBlocker = useMemo(() => reportReadinessBlockers[0] || null, [reportReadinessBlockers]);
  const safetyScanReportContext = useMemo(() => resolveWorkspaceSafetyScanReportContext({
    reportId: persistedDraftReportId,
    persistedReport: proofPackPreviewData?.persisted_report,
    queueWindow: observationFunnelQueue.window,
    urlStart: canonicalProofWindowStart,
    urlEnd: canonicalProofWindowEnd,
    workflowId: handoffParams.workflowId || proofPackPreviewData?.persisted_report?.workflow_id,
    safetyCaseId: handoffParams.safetyCaseId,
    linkedCase: linkedSafetyCases[0] || null,
    zoneId: canonicalProofZoneId,
    cameraId: reviewRecordCameraSelectValue,
    areaLabel: reportScopeHierarchy.areaLabel,
    cameraLabel: reportScopeHierarchy.cameraLabel,
    actions: [...workflowActionsState.active_actions, ...workflowActionsState.completed_actions],
    workflowPrimaryAction: (workflowPrimaryAction || null) as Record<string, unknown> | null,
    lifecycleControlRecorded: inheritedReadiness.correctiveActionRecorded,
  }), [
    canonicalProofWindowEnd, canonicalProofWindowStart, canonicalProofZoneId,
    handoffParams.safetyCaseId, handoffParams.workflowId, inheritedReadiness.correctiveActionRecorded,
    linkedSafetyCases, observationFunnelQueue.window, persistedDraftReportId,
    proofPackPreviewData?.persisted_report, reportScopeHierarchy.areaLabel, reportScopeHierarchy.cameraLabel,
    reviewRecordCameraSelectValue, workflowActionsState.active_actions, workflowActionsState.completed_actions,
    workflowPrimaryAction,
  ]);
  const safetyScanHeader = useMemo(() => buildSafetyScanHeader({
    patternLabel: reportScopeHierarchy.patternLabel,
    areaLabel: safetyScanReportContext.place.areaLabel || reportScopeHierarchy.areaLabel,
    timeWindowLabel: buildSafetyScanWindowLabel(
      safetyScanReportContext.timeWindow.start,
      safetyScanReportContext.timeWindow.end,
      siteTimeZone,
    ) || reportScopeHierarchy.timeWindowLabel,
    signed: Boolean(finalizedPersistedReport),
    operationalComplete: Boolean(workflowReadinessCleared && proofPackArtifactExists),
    reviewPending: !finalizedPersistedReport && !reviewComplete,
    inProgress: Boolean(!finalizedPersistedReport && (proofPageState === 'GENERATING' || evidenceBundleCreating)),
  }), [
    evidenceBundleCreating, finalizedPersistedReport, proofPackArtifactExists, proofPageState,
    reportScopeHierarchy.areaLabel, reportScopeHierarchy.patternLabel, reportScopeHierarchy.timeWindowLabel,
    reviewComplete, safetyScanReportContext.place.areaLabel, safetyScanReportContext.timeWindow.end,
    safetyScanReportContext.timeWindow.start, siteTimeZone, workflowReadinessCleared,
  ]);
  const heroTitle = useMemo(() => (
    isCategoryReport
      ? `${selectedHazardClassOption?.displayName || 'Risk Category'} Summary`
      : safetyScanHeader.title
  ), [isCategoryReport, selectedHazardClassOption?.displayName, safetyScanHeader.title]);
  const heroReportTypeLine = useMemo(() => (
    isCategoryReport
      ? 'Risk Category Summary · On-demand category summary for recurring safety-risk patterns'
      : safetyScanHeader.subtitle
  ), [isCategoryReport, safetyScanHeader.subtitle]);
  const heroScopeLine = useMemo(() => (
    isCategoryReport
      ? `${reportScopeHierarchy.areaLabel} · ${reportScopeHierarchy.timeWindowLabel}`
      : safetyScanHeader.secondaryCopy
  ), [isCategoryReport, reportScopeHierarchy.areaLabel, reportScopeHierarchy.timeWindowLabel, safetyScanHeader.secondaryCopy]);
  const summaryCardFacts = useMemo(
    () => isCategoryReport ? [] : [
      { label: 'Site', value: canonicalProofSiteId || scopedSiteId || 'Not recorded' },
      { label: 'Time window', value: reportScopeHierarchy.timeWindowLabel || 'Not recorded' },
      { label: 'Included Safety Cases', value: String(linkedSafetyCases.length) },
      { label: 'Primary blocker', value: primaryReadinessBlocker?.label || (proofPackArtifactExists ? 'No blockers remain.' : 'No workflow blockers remain.') },
      { label: 'Next step', value: primaryReadinessBlocker?.nextActionText || (proofPackArtifactExists ? 'Review the package and complete Safety Scan sign-off.' : 'Wait for the Scan Package to finish building.') },
    ],
    [canonicalProofSiteId, isCategoryReport, linkedSafetyCases.length, primaryReadinessBlocker, proofPackArtifactExists, reportScopeHierarchy.timeWindowLabel, scopedSiteId],
  );
  const heroStatusLabel = useMemo(() => {
    if (isCategoryReport) return 'On-demand summary';
    if (finalizedPersistedReport) return 'Signed off';
    if (!hasLinkedSafetyCases) return 'Blocked';
    if (reportReadyForSignOff) return 'Ready for sign-off';
    if (workflowReadinessCleared && proofPackArtifactExists) return 'Operationally complete';
    if (workflowReadinessCleared) return 'Preparing report';
    return 'Needs attention';
  }, [finalizedPersistedReport, hasLinkedSafetyCases, isCategoryReport, proofPackArtifactExists, reportReadyForSignOff, workflowReadinessCleared]);
  const heroPrimaryBlocker = useMemo(() => {
    if (finalizedPersistedReport) {
      return 'This page is showing the Signed Safety Scan artifact, not the Safety Case workspace.';
    }
    if (primaryReadinessBlocker) return primaryReadinessBlocker.label;
    return isCategoryReport
      ? 'This category summary is exportable and does not use the persisted draft/finalize lifecycle.'
      : proofPackArtifactExists ? 'No blockers remain.' : 'No workflow blockers remain. The package is not generated yet.';
  }, [finalizedPersistedReport, isCategoryReport, primaryReadinessBlocker, proofPackArtifactExists]);
  const heroNextStep = useMemo(() => {
    if (finalizedPersistedReport) {
      return 'Review the Signed Safety Scan or inspect newer Safety Case activity separately.';
    }
    if (primaryReadinessBlocker) {
      if (proofPackArtifactExists && reportReadinessBlockers.length > 0) {
        return `Scan package is built. ${reportReadinessBlockers.length} sign-off step${reportReadinessBlockers.length === 1 ? '' : 's'} remain: ${primaryReadinessBlocker.nextActionText}`;
      }
      return primaryReadinessBlocker.nextActionText;
    }
    return isCategoryReport
      ? 'Review category coverage and export the summary package when ready.'
      : proofPackArtifactExists ? 'Review the package and complete Safety Scan sign-off.' : 'Wait for the Scan Package to finish building.';
  }, [finalizedPersistedReport, isCategoryReport, primaryReadinessBlocker, proofPackArtifactExists, reportReadinessBlockers.length]);
  const awaitingCaseLinkResolution = Boolean(
    String(handoffParams.safetyCaseId || searchParams.get('safety_case_id') || explicitPersistedReportId || '').trim(),
  );
  const showDraftSetupOnly = requiresLinkedSafetyCases && !hasLinkedSafetyCases && !(linkedSafetyCasesLoading && awaitingCaseLinkResolution);
  const showLinkedCaseLoading = requiresLinkedSafetyCases && linkedSafetyCasesLoading && !hasLinkedSafetyCases && awaitingCaseLinkResolution;
  const showLinkedDraftWorkspace = hasScope && !showDraftSetupOnly && !showLinkedCaseLoading && (hasLinkedSafetyCases || finalizedPersistedReport);
  const safetyScanProductModel = useSafetyScanProductStoryModel({
    linkedCase: linkedSafetyCases[0],
    observationFunnelProgress,
    finalizedFunnel: (proofPackPreviewData?.persisted_report?.finalized_payload as { observation_funnel?: Record<string, unknown> } | undefined)?.observation_funnel || null,
    workflowPrimaryAction,
    patternLabel: reportScopeHierarchy.patternLabel,
    incidentType: canonicalProofIncidentType,
    siteLabel: canonicalProofSiteId,
    zoneId: canonicalProofZoneId,
    areaLabel: reportScopeHierarchy.areaLabel,
    cameraId: reviewRecordCameraSelectValue,
    cameraLabel: reportScopeHierarchy.cameraLabel,
    windowStart: canonicalProofWindowStart,
    windowEnd: canonicalProofWindowEnd,
    timeZone: siteTimeZone,
    signed: Boolean(finalizedPersistedReport),
    workflowImpactState,
    canonicalClaimBoundary,
    canSignSafetyScan,
    hasCanonicalClaimReadiness,
    reportReadyForSignOff,
    claimReadinessControls,
    readinessBlockers: reportReadinessBlockers.map((blocker) => ({
      key: blocker.code || blocker.id,
      controlId: blocker.code || blocker.id,
      label: blocker.label,
      complete: false,
      blocking: true,
    })),
    reviewComplete: effectiveReviewComplete,
    controlRecorded: Boolean(
      safetyScanReportContext.control.recorded || workflowPrimaryAction || correctiveActionComplete,
    ),
    displayCustomerOwnerLabel,
    observationDispositionPending: parseOptionalCount(
      operatorLifecycleSnapshot.review?.unreviewed ?? operatorLifecycleSnapshot.review?.pending,
    ) != null
      ? parseOptionalCount(
        operatorLifecycleSnapshot.review?.unreviewed ?? operatorLifecycleSnapshot.review?.pending,
      )! > 0
      : Array.isArray(proofPackPreviewData?.persisted_report?.finalization_blockers)
        && (proofPackPreviewData?.persisted_report?.finalization_blockers || []).some(
          (item: { code?: string | null }) => String(item?.code || '').trim() === 'observation_disposition_pending',
        ),
    pendingVerifyCountFromBlockers: parsePendingCountFromBlockers(
      proofPackPreviewData?.persisted_report?.finalization_blockers,
    ),
    frozenReview: operatorLifecycleSnapshot.review,
    reportContext: safetyScanReportContext,
  });
  const showSafetyScanWorkflowPage = showLinkedDraftWorkspace && !isCategoryReport;
  const scanMeasurementContext = resolveSafetyScanMeasurementContext({
    persisted: proofPackPreviewData?.persisted_report as Parameters<typeof resolveSafetyScanMeasurementContext>[0]['persisted'],
    siteIdFromSearch: searchParams.get('site_id'),
    signed: Boolean(finalizedPersistedReport),
    controlAction: workflowPrimaryAction as Parameters<typeof resolveSafetyScanMeasurementContext>[0]['controlAction'],
  });
  const safetyScanSignedNav = useMemo(() => {
    if (!persistedDraftReportId) return null;
    const persisted = proofPackPreviewData?.persisted_report as {
      revision?: unknown;
      report_revision?: unknown;
      current_revision?: unknown;
      revisions?: Array<{ revision?: unknown }> | null;
      workflow_anchor?: {
        scope?: {
          site_id?: string | null;
          device_id?: string | null;
          camera_id?: string | null;
          zones?: unknown;
          risk_type?: string | null;
        } | null;
        time_window?: { start?: string | null; end?: string | null; from?: string | null; to?: string | null } | null;
        workflow_id?: string | null;
        representative_incident_id?: string | null;
      } | null;
      workflow_id?: string | null;
    } | undefined;
    const workflowAnchor = persisted?.workflow_anchor || null;
    const scope = workflowAnchor?.scope || null;
    const timeWindow = workflowAnchor?.time_window || null;
    const scopeZones = scope?.zones;
    const zones = Array.isArray(scopeZones) ? scopeZones : [];
    const anchorIncidentId = String(
      workflowAnchor?.representative_incident_id
      || searchParams.get('incident_id')
      || searchParams.get('case_id')
      || '',
    ).trim();
    return resolvePostFinalizeNavigation({
      reportId: persistedDraftReportId,
      revision: resolvePostFinalizeRevision(persisted),
      searchParams,
      artifactArgs: {
        reportId: persistedDraftReportId,
        orgId: searchParams.get('org_id'),
        siteId: String(scope?.site_id || searchParams.get('site_id') || '').trim() || undefined,
        workflowId: String(
          persisted?.workflow_id
          || workflowAnchor?.workflow_id
          || searchParams.get('workflow_id')
          || '',
        ).trim() || undefined,
        deviceId: String(scope?.device_id || searchParams.get('device_id') || '').trim() || undefined,
        zoneId: String(zones[0] || searchParams.get('zone_id') || '').trim() || undefined,
        cameraId: String(scope?.camera_id || searchParams.get('camera_id') || '').trim() || undefined,
        reportFrom: String(timeWindow?.start || timeWindow?.from || searchParams.get('report_from') || '').trim() || undefined,
        reportTo: String(timeWindow?.end || timeWindow?.to || searchParams.get('report_to') || '').trim() || undefined,
        caseId: anchorIncidentId || undefined,
        incidentId: anchorIncidentId || undefined,
        policyType: String(scope?.risk_type || searchParams.get('policy_type') || '').trim() || undefined,
        policyName: String(searchParams.get('policy_name') || '').trim() || undefined,
      },
    });
  }, [persistedDraftReportId, proofPackPreviewData?.persisted_report, searchParams]);
  const safetyScanSignedAtLabel = useMemo(() => {
    if (!finalizedPersistedReport) return '';
    const persisted = proofPackPreviewData?.persisted_report as { finalized_at?: string | null } | undefined;
    return formatSiteDateTime(String(persisted?.finalized_at || '').trim() || null, siteTimeZone);
  }, [finalizedPersistedReport, proofPackPreviewData?.persisted_report, siteTimeZone]);
  const comparisonReferenceView = useMemo(
    () => presentMeasurementReferences(measurementReferencesRaw, { historyHref: dashboardBackPath }),
    [dashboardBackPath, measurementReferencesRaw],
  );
  const safetyScanWorkflowModel = useMemo(() => buildSafetyScanWorkflowViewModel({
    presentation: safetyScanProductModel,
    signed: Boolean(finalizedPersistedReport),
    canSign: Boolean(canSignSafetyScan),
    signing: safetyScanFinalizing,
    signedRecordHref: finalizedPersistedReport && safetyScanSignedNav?.usedExactRevision
      ? safetyScanSignedNav.path
      : null,
    historyHref: dashboardBackPath,
    downloadPdfLabel: 'Download Customer Safety Scan PDF',
    downloadZipLabel: 'Download Scan Package',
    artifactPdfStatus,
    artifactZipStatus,
    previewReportId: canSignSafetyScan && !finalizedPersistedReport ? persistedDraftReportId : null,
    signedAtLabel: safetyScanSignedAtLabel,
    comparisonReference: {
      primaryLabel: comparisonReferenceView.primary.label,
      primaryIdentity: comparisonReferenceView.primary.identity,
      primaryWindow: comparisonReferenceView.primary.windowLabel,
      secondaryLabel: comparisonReferenceView.secondary?.label,
      secondaryIdentity: comparisonReferenceView.secondary?.identity,
      currentScanIdentity: comparisonReferenceView.currentScan.identity,
      pairLabel: comparisonReferenceView.comparablePairLabel,
      controlInForceLabel: comparisonReferenceView.controlInForceLabel,
      initialBaselineEligible: comparisonReferenceView.initialBaselineEligible,
      controlReferenceEligible: comparisonReferenceView.controlReferenceEligible,
      preControlBaselineUnavailable: comparisonReferenceView.preControlBaselineUnavailable,
      historicalControlEffectiveness: comparisonReferenceView.historicalControlEffectiveness,
      currentControlReferenceIdentity: comparisonReferenceView.currentControlReference.identity,
      initialBaselineIdentity: comparisonReferenceView.initialBaseline.identity,
      historyHref: dashboardBackPath,
    },
  }), [
    artifactPdfStatus,
    artifactZipStatus,
    canSignSafetyScan,
    comparisonReferenceView,
    dashboardBackPath,
    finalizedPersistedReport,
    persistedDraftReportId,
    safetyScanFinalizing,
    safetyScanProductModel,
    safetyScanSignedAtLabel,
    safetyScanSignedNav,
  ]);
  const showNotGeneratedPackageMode = showLinkedDraftWorkspace
    && workflowLinkedDraftModeEligible
    && !isCategoryReport
    && !finalizedPersistedReport
    && workflowReadinessCleared
    && !packageBuilt
    && !proofPackGenerationInProgress;
  const showPreparingDeliveryMode = showLinkedDraftWorkspace
    && workflowLinkedDraftModeEligible
    && !isCategoryReport
    && !finalizedPersistedReport
    && workflowReadinessCleared
    && !packageBuilt
    && proofPackGenerationInProgress;
  const showAwaitingPackageMode = showPreparingDeliveryMode || showNotGeneratedPackageMode;
  const showReadyDeliveryMode = showLinkedDraftWorkspace
    && workflowLinkedDraftModeEligible
    && !isCategoryReport
    && !finalizedPersistedReport
    && workflowReadinessCleared
    && packageBuilt;
  const showSingleBlockerMode = showLinkedDraftWorkspace
    && workflowLinkedDraftModeEligible
    && !isCategoryReport
    && !finalizedPersistedReport
    && reportReadinessBlockers.length > 0;
  const showFocusedReportLanguagePreview = showSingleBlockerMode && primaryReadinessBlocker?.id === 'report-language-preview';
  const deliveryModeFacts = useMemo<_ReportDetailFact[]>(
    () => (showReadyDeliveryMode || showAwaitingPackageMode || showSingleBlockerMode) ? [
      { label: 'Site', value: canonicalProofSiteId || scopedSiteId || 'Not recorded' },
      { label: 'Area', value: reportScopeHierarchy.areaLabel || 'Not recorded' },
      { label: 'Time window', value: reportScopeHierarchy.timeWindowLabel || 'Not recorded' },
      { label: 'Included Safety Cases', value: String(linkedSafetyCases.length) },
      {
        label: 'Package',
        value: reportPackageStatusLabel || (packageBuilt ? 'Built' : 'Not built'),
      },
    ] : [],
    [
      canonicalProofSiteId,
      linkedSafetyCases.length,
      packageBuilt,
      reportPackageStatusLabel,
      reportScopeHierarchy.areaLabel,
      reportScopeHierarchy.timeWindowLabel,
      scopedSiteId,
      showAwaitingPackageMode,
      showSingleBlockerMode,
      showReadyDeliveryMode,
    ],
  );
  const currentWorkflowBlockerAction = useMemo(() => {
    if (!primaryReadinessBlocker) return null;
    const prefersSecondary = Boolean(
      primaryReadinessBlocker.secondaryOnClick
      && !primaryReadinessBlocker.secondaryDisabled,
    );
    return {
      label: prefersSecondary
        ? primaryReadinessBlocker.secondaryCtaLabel || primaryReadinessBlocker.ctaLabel
        : primaryReadinessBlocker.ctaLabel,
      onClick: prefersSecondary
        ? primaryReadinessBlocker.secondaryOnClick || primaryReadinessBlocker.onClick
        : primaryReadinessBlocker.onClick,
      disabled: prefersSecondary
        ? Boolean(primaryReadinessBlocker.secondaryDisabled)
        : Boolean(primaryReadinessBlocker.disabled),
      secondaryAction: prefersSecondary
        ? (
          <Button
            variant="outlined"
            onClick={primaryReadinessBlocker.onClick}
            disabled={Boolean(primaryReadinessBlocker.disabled)}
          >
            {primaryReadinessBlocker.ctaLabel}
          </Button>
        )
        : (
          primaryReadinessBlocker.secondaryCtaLabel && primaryReadinessBlocker.secondaryOnClick ? (
            <Button
              variant="outlined"
              onClick={primaryReadinessBlocker.secondaryOnClick}
              disabled={Boolean(primaryReadinessBlocker.secondaryDisabled)}
            >
              {primaryReadinessBlocker.secondaryCtaLabel}
            </Button>
          ) : null
        ),
    };
  }, [primaryReadinessBlocker]);
  const workflowSafetyScanPdfDownloadLabel = persistedDraftReportId && workflowLinkedDraftModeEligible
    ? (supportsDualCustomerPdfFaces ? 'Download Customer Safety Scan' : 'Download Safety Control Review PDF')
    : 'Download Scan Report';
  const workflowSafetyScanPackageDownloadLabel = persistedDraftReportId && workflowLinkedDraftModeEligible
    ? 'Download Full Evidence Package'
    : 'Download Scan Report';
  const packageDownloadAction = useMemo(() => (
    canDownloadSafetyReportZip ? (
      <Button
        variant="outlined"
        onClick={primaryIncidentEvidencePackageAction}
        disabled={proofPackDownloading}
        data-testid="download-safety-scan-pdf"
      >
        {proofPackDownloading ? 'Downloading…' : workflowSafetyScanPdfDownloadLabel}
      </Button>
    ) : null
  ), [
    canDownloadSafetyReportZip,
    primaryIncidentEvidencePackageAction,
    proofPackDownloading,
    workflowSafetyScanPdfDownloadLabel,
  ]);
  const detailedSignedArtifactPdfDownloadAction = useMemo(() => (
    persistedDraftReportId
    && workflowLinkedDraftModeEligible
    && supportsDualCustomerPdfFaces
    && canDownloadSafetyReportZip ? (
      <Button
        variant="outlined"
        onClick={detailedSignedArtifactPdfAction}
        disabled={proofPackDownloading}
        data-testid="download-detailed-signed-pdf"
      >
        {proofPackDownloading ? 'Downloading…' : 'Download Technical Report'}
      </Button>
    ) : null
  ), [
    canDownloadSafetyReportZip,
    detailedSignedArtifactPdfAction,
    persistedDraftReportId,
    proofPackDownloading,
    supportsDualCustomerPdfFaces,
    workflowLinkedDraftModeEligible,
  ]);
  const fullPackageDownloadAction = useMemo(() => (
    persistedDraftReportId && workflowLinkedDraftModeEligible && canDownloadSafetyReportZip ? (
      <Button
        variant="outlined"
        onClick={secondaryIncidentEvidencePackageAction}
        disabled={proofPackDownloading}
        data-testid="download-safety-scan-package"
      >
        {proofPackDownloading ? 'Downloading…' : workflowSafetyScanPackageDownloadLabel}
      </Button>
    ) : null
  ), [
    canDownloadSafetyReportZip,
    persistedDraftReportId,
    proofPackDownloading,
    secondaryIncidentEvidencePackageAction,
    workflowLinkedDraftModeEligible,
    workflowSafetyScanPackageDownloadLabel,
  ]);
  const showFocusedInlineAttestation = useMemo(
    () => Boolean(
      showInlineCaseAttestation
      && primaryReadinessBlocker
      && resolveClaimReadinessSafetyCaseFocus(primaryReadinessBlocker.code) !== null,
    ),
    [primaryReadinessBlocker, showInlineCaseAttestation],
  );
  const importedWorkflowStepLabels = useMemo(
    () => (usesInheritedReadiness
      ? ['Evidence Review', 'Response Readiness', 'Measurement Readiness', 'Outcome', 'Safety Scan sign-off']
      : ['Representative Evidence', 'Response Readiness', 'Measurement Readiness', 'Scan Package', 'Safety Scan Sign-off']),
    [usesInheritedReadiness],
  );
  const workflowProgressSteps = useMemo<Array<{ label: string; status: _WorkflowStepStatus }>>(
    () => finalizedPersistedReport ? [
      { label: importedWorkflowStepLabels[0], status: 'Complete' },
      { label: importedWorkflowStepLabels[1], status: 'Complete' },
      { label: importedWorkflowStepLabels[2], status: 'Complete' },
      { label: importedWorkflowStepLabels[3], status: 'Complete' },
      { label: importedWorkflowStepLabels[4], status: 'Complete' },
    ] : [
      {
        label: importedWorkflowStepLabels[0],
        status: effectiveReviewComplete ? 'Complete' : effectiveRepresentativeEvidenceSelected ? 'Pending' : 'Blocked',
      },
      {
        label: importedWorkflowStepLabels[1],
        status: hasCanonicalClaimReadiness
          ? _claimReadinessStageStatus(
            claimReadinessControlsById,
            ['verification_method', 'field_confirmation_of_zone_controls'],
          )
          : effectiveCorrectiveActionComplete ? 'Complete' : effectiveActionOwnerAssigned ? 'Pending' : 'Blocked',
      },
      {
        label: importedWorkflowStepLabels[2],
        status: hasCanonicalClaimReadiness
          ? _claimReadinessStageStatus(
            claimReadinessControlsById,
            ['observation_coverage_reliability', 'matched_window_followup_validation'],
          )
          : effectiveOutcomeRecorded ? 'Complete' : effectiveCorrectiveActionComplete ? 'Pending' : 'Not started',
      },
      {
        label: importedWorkflowStepLabels[3],
        status: proofPackArtifactExists ? 'Complete' : workflowReadinessCleared ? 'Pending' : 'Blocked',
      },
      {
        label: importedWorkflowStepLabels[4],
        status: finalizedPersistedReport
          ? 'Complete'
          : reportReadyForSignOff
            ? 'Ready'
            : !hasLinkedSafetyCases || reportReadinessBlockers.length > 0
              ? 'Blocked'
              : proofPackArtifactExists
                ? 'Pending'
                : 'Not started',
      },
    ],
    [
      effectiveActionOwnerAssigned,
      effectiveCorrectiveActionComplete,
      effectiveOutcomeRecorded,
      effectiveRepresentativeEvidenceSelected,
      effectiveReviewComplete,
      finalizedPersistedReport,
      claimReadinessControlsById,
      hasCanonicalClaimReadiness,
      hasLinkedSafetyCases,
      importedWorkflowStepLabels,
      proofPackArtifactExists,
      reportReadinessBlockers.length,
      reportReadyForSignOff,
      workflowReadinessCleared,
    ],
  );
  const currentStatusFacts = useMemo(
    () => isCategoryReport ? [
      { label: 'Coverage review', value: customerReviewStatusSummary || 'Review coverage pending' },
      { label: 'Response tracking', value: customerCorrectiveActionSummary || 'Response coverage pending' },
      { label: 'Impact coverage', value: customerMeasurementSummary || 'Impact coverage pending' },
      { label: 'Report status', value: 'On-demand summary' },
    ] : finalizedPersistedReport ? [
      { label: 'Representative evidence', value: 'Complete' },
      { label: 'Response readiness', value: 'Completed' },
      { label: 'Measurement readiness', value: 'Completed' },
      { label: 'Safety Scan sign-off', value: 'Completed' },
    ] : hasCanonicalClaimReadiness ? [
      { label: 'Representative evidence', value: effectiveReviewComplete ? 'Complete' : 'Pending' },
      {
        label: 'Response readiness',
        value: `${['verification_method', 'field_confirmation_of_zone_controls']
          .filter((controlId) => {
            const status = String(claimReadinessControlsById.get(controlId)?.status || '').trim().toLowerCase();
            return status === 'resolved' || status === 'not_applicable';
          }).length}/2 resolved`,
      },
      {
        label: 'Measurement readiness',
        value: `${['observation_coverage_reliability', 'matched_window_followup_validation']
          .filter((controlId) => {
            const status = String(claimReadinessControlsById.get(controlId)?.status || '').trim().toLowerCase();
            return status === 'resolved' || status === 'not_applicable';
          }).length}/2 resolved`,
      },
      { label: 'Safety Scan sign-off', value: reportReadyForSignOff ? 'Ready' : workflowReadinessCleared ? 'Preparing package' : 'Blocked' },
    ] : usesInheritedReadiness ? [
      { label: 'Representative evidence', value: effectiveReviewComplete ? 'Complete' : 'Pending' },
      { label: 'Response readiness', value: effectiveActionOwnerAssigned ? 'Owner assigned' : 'Owner missing' },
      { label: 'Measurement readiness', value: effectiveOutcomeRecorded ? 'Completed' : 'Pending' },
      { label: 'Safety Scan sign-off', value: reportReadyForSignOff ? 'Ready' : workflowReadinessCleared ? 'Preparing package' : 'Blocked' },
    ] : [
      { label: 'Representative evidence', value: reviewComplete ? 'Complete' : 'Pending' },
      { label: 'Response readiness', value: correctiveActionComplete ? 'Completed' : actionOwnerAssigned ? 'In progress' : 'Not started' },
      { label: 'Measurement readiness', value: outcomeRecorded ? 'Completed' : 'Pending' },
      { label: 'Safety Scan sign-off', value: reportReadyForSignOff ? 'Ready' : workflowReadinessCleared ? 'Preparing package' : 'Blocked' },
    ],
    [
      actionOwnerAssigned,
      correctiveActionComplete,
      customerCorrectiveActionSummary,
      customerMeasurementSummary,
      customerReviewStatusSummary,
      effectiveActionOwnerAssigned,
      effectiveOutcomeRecorded,
      effectiveReviewComplete,
      finalizedPersistedReport,
      isCategoryReport,
      claimReadinessControlsById,
      hasCanonicalClaimReadiness,
      outcomeRecorded,
      reportReadyForSignOff,
      reviewComplete,
      usesInheritedReadiness,
      workflowReadinessCleared,
    ],
  );
  const impactOverviewFacts = useMemo(
    () => isCategoryReport ? [
      { label: 'Impact coverage', value: customerMeasurementSummary || 'Impact coverage pending' },
      { label: 'Effectiveness claim', value: 'Effectiveness not claimed' },
      { label: 'Next step', value: 'Review coverage and export the summary package when ready' },
    ] : [
      { label: 'Impact status', value: effectiveOutcomeRecorded
        ? (usesInheritedReadiness ? inheritedReadiness.measurementLabel : (workflowImpactState?.status === 'waived' ? 'Waived' : 'Recorded'))
        : (workflowImpactState?.measurement_status === 'sufficient_data' ? 'Measured' : 'Impact not measured yet') },
      {
        label: 'Effectiveness claim',
        value: hasCanonicalClaimReadiness
          ? (canonicalEffectivenessClaimLabel && canonicalEffectivenessClaimLabel !== 'Not allowed'
            ? canonicalEffectivenessClaimLabel
            : 'Not claimed in current boundary')
          : 'Effectiveness not claimed',
      },
      { label: 'Financial ROI', value: workflowImpactState?.financial_roi_summary || 'Not calculated' },
      { label: 'Next step', value: effectiveOutcomeRecorded
        ? 'No further measurement action required for sign-off unless claim boundary changes.'
        : (workflowImpactState?.recommended_next_step
          ? _titleCase(String(workflowImpactState.recommended_next_step).replace(/_/g, ' '))
          : 'Collect post-action measurement') },
    ],
    [
      canonicalEffectivenessClaimLabel,
      customerMeasurementSummary,
      effectiveOutcomeRecorded,
      hasCanonicalClaimReadiness,
      inheritedReadiness.measurementLabel,
      isCategoryReport,
      usesInheritedReadiness,
      workflowImpactState?.financial_roi_summary,
      workflowImpactState?.measurement_status,
      workflowImpactState?.recommended_next_step,
      workflowImpactState?.status,
    ],
  );
  const measurementReadinessSummary = useMemo(() => {
    if (effectiveOutcomeRecorded) {
      const label = usesInheritedReadiness
        ? inheritedReadiness.measurementLabel
        : (workflowImpactState?.status === 'waived'
          ? 'Waived for this workflow'
          : workflowImpactState?.measurement_status === 'sufficient_data'
            ? 'Measured from live workflow impact'
            : 'Recorded without claiming effectiveness');
      return `Impact measurement is ${label.toLowerCase()} for the linked Safety Case. This scan only claims what the claim boundary allows.`;
    }
    return 'Post-action measurement is not available yet. This scan should not claim effectiveness unless measurement evidence exists.';
  }, [
    effectiveOutcomeRecorded,
    inheritedReadiness.measurementLabel,
    usesInheritedReadiness,
    workflowImpactState?.measurement_status,
    workflowImpactState?.status,
  ]);
  const responseOverviewFacts = useMemo(
    () => isCategoryReport ? categoryActionCoverageFacts : [
      {
        label: 'Owner',
        value: usesInheritedReadiness
          ? (effectiveActionOwnerAssigned ? displayCustomerOwnerLabel : 'Owner missing')
          : (String(workflowPrimaryAction?.assignee || '').trim() || (actionOwnerAssigned ? customerOwnerLabel : 'Owner missing')),
      },
      {
        label: 'Response tracking',
        value: buildWorkflowCorrectiveActionTrackingLabel(workflowPrimaryAction)
          || customerCorrectiveActionSummary
          || 'Response tracking has not started yet.',
      },
      {
        label: 'External reference',
        value: workflowPrimaryAction?.title || 'No work order or external reference recorded',
      },
      {
        label: 'Validation evidence',
        value: workflowActionsState.evidence_summary?.total_count != null
          ? `${workflowActionsState.evidence_summary.total_count} note(s) recorded`
          : 'No validation evidence recorded yet',
      },
    ],
    [
      actionOwnerAssigned,
      categoryActionCoverageFacts,
      customerCorrectiveActionSummary,
      customerOwnerLabel,
      displayCustomerOwnerLabel,
      effectiveActionOwnerAssigned,
      isCategoryReport,
      usesInheritedReadiness,
      workflowActionsState.evidence_summary,
      workflowPrimaryAction,
    ],
  );
  const claimsReadinessFacts = useMemo(
    () => isCategoryReport ? [
      {
        label: 'Readiness state',
        value: 'On-demand summary',
      },
      {
        label: 'Claims boundary',
        value: selectedHazardClassOption?.claimBoundary
          || customerMeasurementBoundarySummary
          || 'Category reports summarize scope and coverage, but do not claim category-wide effectiveness.',
      },
      {
        label: 'Export vs sign-off',
        value: proofPackExists
          ? `${reportPackageStatusLabel}. Category packages are export artifacts, not finalized workflow-backed reports.`
          : 'The category report package has not been generated yet.',
      },
      {
        label: 'Recommended action',
        value: reportPackageActionEnabled
          ? 'Build or download the category package after reviewing coverage and claim boundaries.'
          : 'Complete scan scope selection before exporting the category summary package.',
      },
    ] : [
      {
        label: 'Readiness state',
        value: finalizedPersistedReport ? 'Completed' : reportReadyForSignOff ? 'Ready for Sign-off' : workflowReadinessCleared ? 'Preparing package' : 'Blocked',
      },
      {
        label: 'Claims boundary',
        value: (hasCanonicalClaimReadiness && reportReadyForSignOff && !effectivenessClaimAllowed)
          ? 'This scan documents observed conditions and corrective response. Measured outcome improvement is not claimed.'
          : ((hasCanonicalClaimReadiness && String(canonicalClaimBoundary?.wording || '').trim())
            ? String(canonicalClaimBoundary?.wording || '').trim()
            : (customerMeasurementBoundarySummary
              || customerUnprovenSummary
              || 'Claims remain limited until measurement trust is explicitly recorded.')),
      },
      {
        label: 'Package vs sign-off',
        value: proofPackExists
          ? `${reportPackageStatusLabel}. Package availability does not mean the report is finalized.`
          : 'The Scan Package has not been generated yet.',
      },
      {
        label: 'Recommended action',
        value: reportReadinessActionHelper,
      },
    ],
    [
      canonicalClaimBoundary?.wording,
      customerMeasurementBoundarySummary,
      customerUnprovenSummary,
      effectivenessClaimAllowed,
      finalizedPersistedReport,
      hasCanonicalClaimReadiness,
      isCategoryReport,
      proofPackExists,
      reportPackageActionEnabled,
      reportPackageStatusLabel,
      reportReadinessActionHelper,
      reportReadyForSignOff,
      workflowReadinessCleared,
      selectedHazardClassOption?.claimBoundary,
    ],
  );
  const detailStateCards = useMemo(
    (): Array<{
      title: string;
      value: string;
      helper: string;
      tone: 'success' | 'warning' | 'error' | 'info' | 'default';
    }> => [
      {
        title: 'Workflow State',
        value: isCategoryReport ? 'Multi-workflow summary' : workflowCurrentStageLabel,
        helper: isCategoryReport
          ? 'This report mode summarizes multiple recurring-risk workflows in one hazard category.'
          : workflowMissingLine,
        tone: isCategoryReport ? 'info' : workflowReadinessCleared ? 'success' : 'warning',
      },
      {
        title: 'Report Artifact State',
        value: isCategoryReport
          ? (reportNarrativeScopeReady ? 'Summary loaded' : 'Summary scope incomplete')
          : finalizedPersistedReport ? 'Finalized report' : reportNarrativeScopeReady ? 'Draft loaded' : 'Draft incomplete',
        helper: reportStatusBannerBody,
        tone: finalizedPersistedReport || reportNarrativeScopeReady ? 'success' : 'warning',
      },
      {
        title: 'Package / Export State',
        value: reportPackageStatusLabel,
        helper: reportPackageSummary,
        tone: proofPageState === 'ERROR' ? 'error' : proofPackExists ? 'success' : 'warning',
      },
      {
        title: 'Finalization State',
        value: isCategoryReport
          ? 'On-demand summary'
          : finalizedPersistedReport
            ? 'Completed'
            : reportReadyForSignOff
              ? 'Ready for Sign-off'
              : workflowReadinessCleared
                ? 'Preparing package'
              : 'Blocked',
        helper: isCategoryReport
          ? 'Category reports are exportable summaries and do not use the persisted draft/finalize lifecycle in this MVP.'
          : finalizedPersistedReport
            ? 'This finalized report remains signed off even if newer workflow activity is now pending.'
            : finalSignoffSummary,
        tone: isCategoryReport ? 'info' : finalizedPersistedReport || workflowReadinessCleared ? 'success' : 'warning',
      },
      {
        title: 'Measurement Trust',
        value: measurementTrustLevelLabel || 'Needs review',
        helper: measurementTrustSummary,
        tone: isCategoryReport ? 'warning' : effectivenessClaimAllowed ? 'success' : _trustStateColor,
      },
    ],
    [
      finalSignoffSummary,
      finalizedPersistedReport,
      isCategoryReport,
      measurementTrustLevelLabel,
      measurementTrustSummary,
      proofPackExists,
      proofPageState,
      reportNarrativeScopeReady,
      reportPackageStatusLabel,
      reportPackageSummary,
      reportStatusBannerBody,
      reportReadyForSignOff,
      workflowReadinessCleared,
      workflowCurrentStageLabel,
      effectivenessClaimAllowed,
      workflowMissingLine,
      _trustStateColor,
    ],
  );

  return (
    <Box sx={{ width: '100%', maxWidth: 'none', px: { xs: 2, sm: 3 }, pb: 3 }}>
      <Stack spacing={2}>
        <Box>
          <Button variant="text" sx={{ px: 0, minWidth: 0 }} onClick={() => navigate(dashboardBackPath)}>
            Back to Safety Control
          </Button>
        </Box>

        <ProofWorkspaceHeader
          canReadIncidents={capabilityProfile.canReadIncidents}
          canReadPolicies={capabilityProfile.canReadPolicies}
          configurationHandoffHref={configurationHandoffHref}
          onOpenIncidents={() => navigate(incidentReviewReturnHref)}
          onOpenConfiguration={() => navigate(configurationHandoffHref)}
          summaryLine={customerSummarySentence}
          overline={safetyScanHeader.overline}
          stateLabel={finalizedPersistedReport ? SIGNED_SAFETY_SCAN_LABEL : DRAFT_SAFETY_SCAN_LABEL}
          reportTitle={
            isCategoryReport
              ? heroTitle
              : (showSafetyScanWorkflowPage
                ? safetyScanProductModel.problem.conditionLabel
                : safetyScanHeader.title)
          }
          reportSubtitle={isCategoryReport ? heroReportTypeLine : safetyScanHeader.subtitle}
          secondaryCopy={isCategoryReport ? heroScopeLine : safetyScanHeader.secondaryCopy}
        />
        {showSafetyScanWorkflowPage ? null : repeatSafetyScanPath ? (
          <Box>
            <Button
              variant="outlined"
              onClick={() => navigate(repeatSafetyScanPath)}
            >
              Schedule this Safety Scan
            </Button>
          </Box>
        ) : null}

        {showSafetyScanWorkflowPage ? null : (
        <>
        <PlatformWorkflowStrip
          currentStep={
            packageBuilt || evidenceBundleCreating || evidenceBundleReady || evidenceBundleFailed || evidenceBundleNeedsRefresh
              ? 'scan'
              : 'measure'
          }
          secondaryStep={
            packageBuilt || evidenceBundleCreating || evidenceBundleReady || evidenceBundleFailed || evidenceBundleNeedsRefresh
              ? 'measure'
              : 'scan'
          }
          compact
          onStepClick={(step) => {
            if (step === 'measure') {
              focusMeasurementAssuranceSection();
              return;
            }
            if (step === 'scan') {
              selectProofSurface('package');
              prepareProofPackAction();
              window.setTimeout(() => {
                const target = evidenceBundleSectionRef.current
                  || document.querySelector('[data-testid="safety-report-detail-sections"]')
                  || document.querySelector('[data-testid="proof-summary-surface"]');
                if (target && 'scrollIntoView' in target) {
                  (target as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 0);
            }
          }}
        />

        {packageBuilt ? null : (
          <Alert
            severity="info"
            data-testid="measure-workflow-step-banner"
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {evidenceBundleCreating || evidenceBundleReady || evidenceBundleFailed || evidenceBundleNeedsRefresh
                ? 'Scan Package'
                : 'You are on Measure → Scan'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.35 }}>
              {evidenceBundleCreating || evidenceBundleReady || evidenceBundleFailed || evidenceBundleNeedsRefresh
                ? 'Scan Package status is in the card below. Click Scan in the strip to jump there.'
                : 'Admit detection-performance evaluation before sign-off. Downloads work as soon as the Scan Package is built.'}
            </Typography>
          </Alert>
        )}
        </>
        )}

        {_reportNotice ? <Alert severity="info">{_reportNotice}</Alert> : null}
        {reportError ? <Alert severity="error">{reportError}</Alert> : null}
        {!hasScope ? <Alert severity="warning">Set org and site scope to load report detail.</Alert> : null}

        {showSafetyScanWorkflowPage ? (
          <SafetyScanWorkflowPage
            model={safetyScanWorkflowModel}
            onReviewEpisodes={openVerifyScanObservations}
            onSign={() => { void finalizeSafetyScanAction(); }}
            onOpenSignedRecord={() => {
              if (safetyScanSignedNav?.path) navigate(safetyScanSignedNav.path);
            }}
            onDownloadSignedRecord={persistedDraftReportId ? () => {
              void downloadPersistedSafetyScanPdf(persistedDraftReportId, { face: 'customer_compact' });
            } : undefined}
            signDisabled={reportReadinessActionDisabled}
            measurementSource={scanMeasurementContext.source}
            controlRelation={scanMeasurementContext.controlRelation}
          />
        ) : showLinkedDraftWorkspace && !isCategoryReport ? (
          <SafetyScanProductStory
            model={safetyScanProductModel}
            onVerify={openVerifyScanObservations}
            onOpenCase={() => {
              const linked = linkedSafetyCases[0];
              if (linked) openLinkedSafetyCase(linked);
            }}
            onSign={() => { void finalizeSafetyScanAction(); }}
            signDisabled={reportReadinessActionDisabled}
            signing={safetyScanFinalizing}
          />
        ) : null}

        {hasScope && isSafetyReportBuilding(proofPageState) && !showSafetyReportContent ? (
          <Alert severity="info" data-testid="safety-report-building-banner">
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {reportBuildingBannerText}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.35 }}>
              {`Using selected scan scope: ${reportBuildingScopeSummary || 'Selected scan scope'}`}
            </Typography>
          </Alert>
        ) : null}

        {/* Measure stays in Advanced on the workflow page. */}
        {showSafetyScanWorkflowPage ? null : hasScope && !finalizedPersistedReport && !isCategoryReport && persistedDraftReportId && !showDraftSetupOnly && !packageBuilt && measurementGateAdmitted === false ? (
          <Box ref={measurementAssuranceSectionRef} sx={{ mb: 2 }}>
            <MeasurementAssurancePanel
              reportId={persistedDraftReportId}
              variant="presignoff"
              onAdmissionChange={(state) => {
                setMeasurementGateAdmitted(state.admitted);
                setFieldVerificationAllowed(state.fieldVerificationAllowed);
                setFieldVerificationLabel(state.fieldVerificationLabel);
              }}
            />
          </Box>
        ) : null}

        {hasScope && showLinkedCaseLoading ? <DraftSafetyScanLinkLoadingPanel /> : null}

        {hasScope && showDraftSetupOnly ? (
          <Paper
            variant="outlined"
            sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3 }}
            data-testid="draft-safety-scan-setup-state"
          >
            <Stack spacing={1.5}>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                Draft Safety Scan
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                No Safety Case is linked to this Draft Safety Scan yet.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                A Safety Scan packages one or more Safety Cases for sign-off and sharing.
                Select a Safety Case scope first, then complete Measurement Assurance and build the package.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                <Button variant="contained" onClick={openSafetyCaseLinkDisambiguation}>
                  Select Safety Case
                </Button>
                <Button variant="outlined" onClick={() => navigate(dashboardBackPath)}>
                  Back to Safety Control
                </Button>
                <Button variant="outlined" onClick={openSafetyCasesHome}>
                  Back to Safety Cases
                </Button>
              </Stack>
            </Stack>
          </Paper>
        ) : null}

        <OptionalCollapsedAdvanced enabled={showSafetyScanWorkflowPage}>
        {showSafetyScanWorkflowPage ? (
          <>
            <Alert severity="info" sx={{ mb: 1.5 }} data-testid="safety-scan-download-helper">
              {safetyScanWorkflowModel.downloads.helper}
            </Alert>
            {hasScope && !finalizedPersistedReport && persistedDraftReportId && !packageBuilt ? (
              <Box ref={measurementAssuranceSectionRef} sx={{ mb: 2 }}>
                <MeasurementAssurancePanel
                  reportId={persistedDraftReportId}
                  variant="presignoff"
                  onAdmissionChange={(state) => {
                    setMeasurementGateAdmitted(state.admitted);
                    setFieldVerificationAllowed(state.fieldVerificationAllowed);
                    setFieldVerificationLabel(state.fieldVerificationLabel);
                  }}
                />
              </Box>
            ) : null}
            {repeatSafetyScanPath ? (
              <Box sx={{ mb: 1.5 }}>
                <Button variant="outlined" onClick={() => navigate(repeatSafetyScanPath)}>
                  Schedule this Safety Scan
                </Button>
              </Box>
            ) : null}
            <SafetyScanProductStory
              model={safetyScanProductModel}
              hideActions
            />
          </>
        ) : null}
        {showLinkedDraftWorkspace ? (
          <ReadinessStatusPanel
            title={showReadyDeliveryMode
              ? 'Scan package ready'
              : showNotGeneratedPackageMode
                ? 'Scan package not generated'
              : showPreparingDeliveryMode
                ? 'Preparing scan package'
                : showSingleBlockerMode
                  ? (showFocusedReportLanguagePreview && packageBuilt
                    ? 'Scan package ready'
                    : 'Finish this step to generate the scan report')
                  : heroTitle}
            reportTypeLine={showReadyDeliveryMode
              ? (canSignSafetyScan
                ? 'Download the package, or sign off to freeze a signed record.'
                : 'Download the built package now. Admit Measurement Assurance only when you are ready to sign.')
              : showNotGeneratedPackageMode
                ? 'The workflow is complete. Generate the Scan Package when you are ready.'
              : showPreparingDeliveryMode
                ? 'The workflow is complete. The package is building now because you requested it.'
                : showSingleBlockerMode
                  ? (packageBuilt
                    ? 'The package is built. Download now, then finish the remaining workflow step if you need to sign.'
                    : 'The report is not ready yet. Fix the current blocker below; the rest of the workflow stays collapsed as audit context.')
              : heroReportTypeLine}
            scopeLine={showReadyDeliveryMode || showAwaitingPackageMode || showSingleBlockerMode
              ? `${reportScopeHierarchy.areaLabel} · ${reportScopeHierarchy.timeWindowLabel}`
              : heroScopeLine}
            summaryIntro={showReadyDeliveryMode || showAwaitingPackageMode || showSingleBlockerMode
              ? 'Workflow details stay available below as collapsed audit context.'
              : undefined}
            facts={showReadyDeliveryMode || showAwaitingPackageMode || showSingleBlockerMode ? deliveryModeFacts : summaryCardFacts}
            statusLabel={showReadyDeliveryMode ? (canSignSafetyScan ? 'Ready' : 'Package built') : showNotGeneratedPackageMode ? 'Not generated' : showPreparingDeliveryMode ? 'Generating' : heroStatusLabel}
            primaryBlocker={showReadyDeliveryMode
              ? (canSignSafetyScan
                ? 'No blockers remain.'
                : (fieldVerificationAllowed === false
                  ? (fieldVerificationLabel || 'Control confirmation on site required')
                  : 'Package ready to download. Sign-off needs a recorded detection check.'))
              : showNotGeneratedPackageMode
                ? 'No workflow blockers remain.'
              : showPreparingDeliveryMode
                ? 'No workflow blockers remain.'
                : showSingleBlockerMode
                  ? primaryReadinessBlocker?.label || 'A workflow blocker remains.'
                  : heroPrimaryBlocker}
            primaryBlockerLabel={showReadyDeliveryMode
              ? (canSignSafetyScan ? 'Report status' : 'Downloads available')
              : showNotGeneratedPackageMode
                ? 'Package status'
              : showPreparingDeliveryMode
                ? 'Workflow status'
                : showSingleBlockerMode
                  ? 'Current blocker'
                  : undefined}
            nextStep={showReadyDeliveryMode
              ? (canSignSafetyScan
                ? 'Download the Safety Scan first. Sign-off is a secondary action and is not the same as operational complete.'
                : 'Download the Safety Scan now. Open Measurement Assurance below when you are ready to sign.')
              : showNotGeneratedPackageMode
                ? 'Click Build Scan Package to generate ZIP and PDF artifacts. Opening this page does not start a build.'
              : showPreparingDeliveryMode
                ? 'Wait for the package to finish building. This page will switch to Scan package ready automatically.'
                : showSingleBlockerMode
                  ? (packageBuilt
                    ? 'Download the Safety Scan PDF now, then resolve the remaining step if you need to sign.'
                    : primaryReadinessBlocker?.nextActionText || 'Resolve the current blocker below.')
              : heroNextStep}
            nextStepLabel={showReadyDeliveryMode
              ? 'Next step'
              : showNotGeneratedPackageMode
                ? 'Next action'
              : showPreparingDeliveryMode
                ? 'Next status'
                : showSingleBlockerMode
                  ? 'Next action'
                  : undefined}
            primaryActionLabel={showReadyDeliveryMode
              ? workflowSafetyScanPdfDownloadLabel
              : showNotGeneratedPackageMode
                ? 'Build Scan Package'
              : showPreparingDeliveryMode
                ? 'Building Scan Report...'
                : showSingleBlockerMode
                  ? (packageBuilt && canDownloadSafetyReportZip
                    ? workflowSafetyScanPdfDownloadLabel
                    : currentWorkflowBlockerAction?.label || 'Resolve blocker')
              : primaryReadinessBlocker?.ctaLabel || (isCategoryReport ? _incidentEvidencePackOutputPrimaryLabel : reportReadinessActionLabel)}
            primaryActionDisabled={
              showReadyDeliveryMode
                ? (proofPackDownloading || !canDownloadSafetyReportZip)
                : showNotGeneratedPackageMode
                  ? !canOperateOnProofPackage
                : showPreparingDeliveryMode
                  ? true
                  : showSingleBlockerMode
                    ? (packageBuilt && canDownloadSafetyReportZip
                      ? proofPackDownloading
                      : Boolean(currentWorkflowBlockerAction?.disabled))
                : Boolean(primaryReadinessBlocker?.disabled)
              || (isCategoryReport ? !reportPackageActionEnabled || proofPackGenerationInProgress || proofPackDownloading : false)
              || (!primaryReadinessBlocker && !isCategoryReport && reportReadinessActionDisabled)
            }
            onPrimaryAction={showReadyDeliveryMode
              ? primaryIncidentEvidencePackageAction
              : showNotGeneratedPackageMode
                ? () => { void runEvidenceBundleAction(); }
              : showPreparingDeliveryMode
                ? () => {}
                : showSingleBlockerMode
                  ? (packageBuilt && canDownloadSafetyReportZip
                    ? primaryIncidentEvidencePackageAction
                    : currentWorkflowBlockerAction?.onClick || (() => {}))
              : primaryReadinessBlocker?.onClick || (isCategoryReport ? primaryIncidentEvidencePackageAction : () => { void finalizeSafetyScanAction(); })}
            secondaryActions={(
              showReadyDeliveryMode ? (
                <>
                  {!canSignSafetyScan ? (
                    <Button
                      variant="outlined"
                      onClick={focusMeasurementAssuranceSection}
                      data-testid="admit-evaluation-for-signoff"
                    >
                      Admit evaluation to sign
                    </Button>
                  ) : isCategoryReport ? (
                    <Button
                      variant="outlined"
                      disabled={reportReadinessActionDisabled}
                      onClick={() => { void finalizeSafetyScanAction(); }}
                    >
                      {safetyScanFinalizing ? 'Signing Safety Scan...' : 'Sign Safety Scan'}
                    </Button>
                  ) : null}
                  {detailedSignedArtifactPdfDownloadAction}
                  {fullPackageDownloadAction}
                </>
              ) : showSingleBlockerMode ? (
                <>
                  {packageBuilt && canDownloadSafetyReportZip ? (
                    <>
                      {detailedSignedArtifactPdfDownloadAction}
                      {fullPackageDownloadAction}
                      {currentWorkflowBlockerAction?.secondaryAction || null}
                    </>
                  ) : (
                    <>
                      {packageDownloadAction}
                      {detailedSignedArtifactPdfDownloadAction}
                      {fullPackageDownloadAction}
                      {currentWorkflowBlockerAction?.secondaryAction || null}
                    </>
                  )}
                </>
              ) : !isCategoryReport && !hasLinkedSafetyCases ? (
                <>
                  <Button variant="outlined" onClick={openSafetyCasesHome}>
                    Select Safety Case
                  </Button>
                  <Button variant="outlined" onClick={() => navigate(dashboardBackPath)}>
                    Back to Safety Control
                  </Button>
                  <Button variant="outlined" onClick={openSafetyCasesHome}>
                    Back to Safety Cases
                  </Button>
                </>
              ) : (
                <>
                  {!isCategoryReport ? (
                    <Button variant="outlined" onClick={openEvidenceReview}>
                      Open Review
                    </Button>
                  ) : null}
                  <Button
                    variant="outlined"
                    onClick={primaryIncidentEvidencePackageAction}
                    disabled={proofPackGenerationInProgress || proofPackDownloading || !reportPackageActionEnabled}
                  >
                    {_incidentEvidencePackOutputPrimaryLabel}
                  </Button>
                </>
              )
            )}
            hideActions={showFocusedReportLanguagePreview}
            essentialActions={showFocusedReportLanguagePreview ? packageDownloadAction : undefined}
            chips={showReadyDeliveryMode
              ? [
                canSignSafetyScan ? 'Ready for sign-off' : 'Downloads available',
                `Scan Package: ${reportPackageStatusLabel}`,
              ]
              : showNotGeneratedPackageMode
                ? ['Workflow complete', 'Scan Package: Not generated']
              : showPreparingDeliveryMode
                ? ['Workflow complete', `Scan Package: ${reportPackageStatusLabel}`]
              : reportStatusBannerChips.slice(0, 4)}
          />
        ) : null}

        {hasScope ? (
          <SafetyCaseLinkDisambiguationDialog
            open={linkDisambiguationOpen}
            cases={pendingLinkCases}
            onClose={() => setLinkDisambiguationOpen(false)}
            onSelect={handleSelectLinkedSafetyCase}
          />
        ) : null}

        {showReadyDeliveryMode || showAwaitingPackageMode || showSingleBlockerMode ? (
          <>
            {/* After package is built: Measure is sign-off only — keep it below downloads, collapsed. */}
            {hasScope && !finalizedPersistedReport && !isCategoryReport && persistedDraftReportId && packageBuilt ? (
              <Box ref={measurementAssuranceSectionRef} sx={{ mb: 2 }} data-testid="measurement-assurance-signoff-section">
                <DisclosureSection
                  title="Detection check for this Scan"
                  subtitle={detectionCheckWrapperSubtitle(measurementGateAdmitted === true, canSignSafetyScan)}
                  defaultExpanded={measurementGateAdmitted !== true}
                >
                  <MeasurementAssurancePanel
                    reportId={persistedDraftReportId}
                    variant="presignoff"
                    onAdmissionChange={(state) => {
                      setMeasurementGateAdmitted(state.admitted);
                      setFieldVerificationAllowed(state.fieldVerificationAllowed);
                      setFieldVerificationLabel(state.fieldVerificationLabel);
                    }}
                  />
                  {fieldVerificationAllowed === false ? (
                    <Alert severity="warning" sx={{ mt: 1.5 }} data-testid="field-verification-signoff-blocker">
                      {fieldVerificationLabel || 'Control confirmation on site required'}
                    </Alert>
                  ) : null}
                </DisclosureSection>
              </Box>
            ) : null}
            {showSingleBlockerMode && primaryReadinessBlocker ? (
              <ReadinessBlockersPanel
                blockers={[primaryReadinessBlocker]}
                title="Current blocker"
                emptyStateMessage="No blocker remains."
                hideActions={showFocusedReportLanguagePreview}
              />
            ) : null}
          <DisclosureSection
            title="Workflow details"
            subtitle={showReadyDeliveryMode
              ? 'Collapsed audit context from the completed review, Safety Case, and package state.'
              : showNotGeneratedPackageMode
                ? 'Collapsed audit context. Generate the package when you are ready.'
              : showPreparingDeliveryMode
                ? 'Collapsed audit context while the completed workflow is being packaged.'
                : 'Collapsed audit context for the remaining blocked workflow.'}
          >
            <Stack spacing={2}>
              <SafetyScanLinkedCaseContextBlock
                linkedCase={linkedSafetyCases[0] || null}
                handoff={handoffParams}
              />
              <CompactWorkflowPanel
                title={usesInheritedReadiness ? 'Imported Workflow State' : 'Progress'}
                steps={workflowProgressSteps}
              />
              <Box
                data-testid="delivery-mode-workflow-details"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
                  gap: 2,
                }}
              >
                <ReportDetailSection
                  title={usesInheritedReadiness ? 'Evidence Review' : 'Representative Evidence'}
                  summary={usesInheritedReadiness
                    ? 'Read-only evidence review state imported from Review and the linked Safety Case.'
                    : 'Representative evidence selected for this completed Safety Scan workflow.'}
                  facts={currentStatusFacts}
                />
                <ReportDetailSection
                  title="Measurement Readiness"
                  summary={measurementReadinessSummary}
                  facts={impactOverviewFacts}
                />
                <ReportDetailSection
                  title={isCategoryReport ? 'Response Tracking' : 'Response Readiness'}
                  summary="Recorded response owner, corrective action, and readiness state imported from the linked workflow."
                  facts={responseOverviewFacts}
                />
                <ReportDetailSection
                  title="Scan Package"
                  summary={reportPackageSummary}
                  facts={reportPackageFacts}
                />
              </Box>
            </Stack>
          </DisclosureSection>
          </>
        ) : hasScope && !showDraftSetupOnly ? (
          <>
            <SafetyScanLinkedCaseContextBlock
              linkedCase={linkedSafetyCases[0] || null}
              handoff={handoffParams}
            />
            <LinkedSafetyCasesPanel
              linkedSafetyCases={linkedSafetyCases}
              loading={linkedSafetyCasesLoading}
              onSelectSafetyCase={openSafetyCaseLinkDisambiguation}
              onBackToSafetyCases={openSafetyCasesHome}
              onOpenSafetyCase={openLinkedSafetyCase}
            />
          </>
        ) : null}

        {showLinkedDraftWorkspace && !isCategoryReport && !showReadyDeliveryMode && !showAwaitingPackageMode && !showSingleBlockerMode ? (
          <CompactWorkflowPanel
            title={usesInheritedReadiness ? 'Imported Workflow State' : 'Progress'}
            steps={workflowProgressSteps}
          />
        ) : null}

        {showLinkedDraftWorkspace && !showReadyDeliveryMode && !showAwaitingPackageMode && !showSingleBlockerMode ? (
          <Box
            data-testid="safety-report-detail-sections"
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            <ReportDetailSection
              title={usesInheritedReadiness ? 'Evidence Review' : 'Representative Evidence'}
              summary={usesInheritedReadiness
                ? 'Read-only evidence review state imported from Review and the linked Safety Case.'
                : 'Representative evidence must be selected before this Safety Scan can be reviewed and signed.'}
              facts={currentStatusFacts}
            >
              {!isCategoryReport ? (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                  <Button variant="outlined" onClick={openScopeControls}>
                    Select Representative Evidence
                  </Button>
                  <Button variant="outlined" onClick={openEvidenceReview}>
                    Open Review
                  </Button>
                </Stack>
              ) : null}
            </ReportDetailSection>
            <ReportDetailSection
              title="Measurement Readiness"
              summary={measurementReadinessSummary}
              facts={impactOverviewFacts}
            />
            <ReportDetailSection
              title={isCategoryReport ? 'Response Tracking' : 'Response Readiness'}
              summary="Record the response owner, corrective action, or explicit reason no corrective action is included."
              facts={responseOverviewFacts}
            >
              {!isCategoryReport ? (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                  <Button variant="outlined" onClick={() => linkedSafetyCases[0] ? openLinkedSafetyCase(linkedSafetyCases[0]) : openSafetyCasesHome()}>
                    Open Safety Case
                  </Button>
                  <Button variant="outlined" onClick={openSafetyCasesHome}>
                    Assign Action Owner
                  </Button>
                  <Button variant="outlined" onClick={openSafetyCasesHome}>
                    Record Corrective Action
                  </Button>
                </Stack>
              ) : null}
            </ReportDetailSection>
            <ReportDetailSection
              title="Scan Package"
              summary={reportPackageSummary}
              facts={reportPackageFacts}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                <Button
                  variant="contained"
                  onClick={primaryIncidentEvidencePackageAction}
                  disabled={proofPackGenerationInProgress || proofPackDownloading || !reportPackageActionEnabled}
                >
                  {_incidentEvidencePackOutputPrimaryLabel}
                </Button>
                {repeatSafetyScanPath ? (
                  <Button
                    variant="outlined"
                    onClick={() => navigate(repeatSafetyScanPath)}
                  >
                    Schedule this Safety Scan
                  </Button>
                ) : null}
                <Button
                  variant="outlined"
                  aria-label="Advanced Technical Details"
                  onClick={() => setTechnicalDetailsExpanded(true)}
                >
                  Open Advanced Technical Details
                </Button>
              </Stack>
            </ReportDetailSection>
          </Box>
        ) : null}

        {showLinkedDraftWorkspace && !showReadyDeliveryMode && !showAwaitingPackageMode && !isCategoryReport && hasLinkedSafetyCases && hasReportWorkplaceCondition && (!showSingleBlockerMode || showFocusedReportLanguagePreview) ? (
          <ReportDetailSection
            title="Report Language Preview"
            summary="Report language is generated automatically from the linked workplace condition and frozen into the scan at sign-off."
            facts={reportLanguageFacts}
            sectionRef={reportLanguagePreviewRef}
            highlighted={reportLanguagePreviewHighlighted}
          >
            <Stack spacing={1.25}>
              {reportLanguagePreview.warning ? (
                <Alert severity="warning">
                  {reportLanguagePreview.warning}
                </Alert>
              ) : null}
              <Typography variant="body2" color="text.secondary">
                Workplace condition type: {reportConditionTypeLabel}
              </Typography>
              {reportLanguagePreview.sections.length > 0 ? (
                <Stack spacing={1}>
                  {reportLanguagePreview.sections.map((section) => (
                    <Box key={section.label}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        {section.label}
                      </Typography>
                      <Typography variant="body2">{section.text}</Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Report language will appear here once the linked Safety Case has workplace-condition fields.
                </Typography>
              )}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                <Button
                  variant="outlined"
                  onClick={() => linkedSafetyCases[0] ? openLinkedSafetyCase(linkedSafetyCases[0]) : openSafetyCasesHome()}
                >
                  Open Safety Case
                </Button>
              </Stack>
            </Stack>
          </ReportDetailSection>
        ) : null}

        {showLinkedDraftWorkspace && !showReadyDeliveryMode && !showAwaitingPackageMode && !showSingleBlockerMode && isCategoryReport ? (
          <ReportDetailSection
            title="Signed result"
            summary={safetyScanSignOffSummary}
            facts={claimsReadinessFacts}
          >
            <Button variant="contained" disabled={reportReadinessActionDisabled} onClick={() => { void finalizeSafetyScanAction(); }}>
              {reportReadinessActionLabel}
            </Button>
          </ReportDetailSection>
        ) : null}

        {showLinkedDraftWorkspace && hasLinkedSafetyCases && !showSingleBlockerMode && !showAwaitingPackageMode && (finalizedPersistedReport || reportReadinessBlockers.length > 0) ? (
          <DisclosureSection
            title={finalizedPersistedReport ? 'New activity since latest Signed Safety Scan' : 'Why this needs attention'}
            subtitle={
              finalizedPersistedReport
                ? 'This Signed Safety Scan stays finalized. Any newer Safety Case activity is tracked separately.'
                : primaryReadinessBlocker?.label || 'No blockers remain.'
            }
            defaultExpanded={!finalizedPersistedReport && reportReadinessBlockers.length > 0}
          >
            <ReadinessBlockersPanel
              blockers={reportReadinessBlockers}
              title={finalizedPersistedReport ? 'New activity since latest Signed Safety Scan' : 'Why this needs attention'}
              emptyStateMessage={
                finalizedPersistedReport
                  ? 'No artifact blockers remain. This persisted report is already signed off.'
                  : 'No blockers remain. This Draft Safety Scan is ready for sign-off.'
              }
            />
          </DisclosureSection>
        ) : null}

        {(showInlineCaseAttestation && !showSingleBlockerMode) || showFocusedInlineAttestation ? (
          <DisclosureSection
            title="Safety Case attestation"
            subtitle="Complete closure summary and related attestations here — no need to open the Safety Case page."
            defaultExpanded={showFocusedInlineAttestation || claimReadinessBlockers.some(
              (blocker) => resolveClaimReadinessSafetyCaseFocus(blocker.code) !== null,
            )}
          >
            <SafetyCaseAttestationPanel
              ref={safetyCaseAttestationPanelRef}
              safetyCaseId={String(
                linkedSafetyCases[0]?.safety_case_id
                || linkedSafetyCases[0]?.finding_id
                || '',
              ).trim()}
              item={linkedSafetyCases[0] as SafetyCaseRecord}
              readOnly={!canManageSafetyCases}
              workflowImpactSummary={linkedCaseAttestationImpactSummary}
              followupMeasurement={linkedCaseFollowupMeasurement}
              onSaved={() => { void handleInlineCaseAttestationSaved(); }}
            />
          </DisclosureSection>
        ) : null}

        {showLinkedDraftWorkspace ? (
          <DisclosureSection
            title="Status details"
            subtitle="Workflow, report, package, sign-off, and claim state"
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(5, minmax(0, 1fr))' },
                gap: 2,
              }}
            >
              {detailStateCards.map((card) => (
                <DetailStateCard
                  key={card.title}
                  title={card.title}
                  value={card.value}
                  helper={card.helper}
                  tone={card.tone}
                />
              ))}
            </Box>
          </DisclosureSection>
        ) : null}

        {showLinkedDraftWorkspace ? (
          <>
            {handoffParams.isUpdatedScan && !reportReadyForSignOff ? (
              <UpdatedSafetyScanScopeConfirmationPanel
                handoff={handoffParams}
                linkedCase={linkedSafetyCases[0] || null}
                updatedScopeStartLabel={updatedScanScopeConfirmationLabels.updatedScopeStartLabel}
                updatedScopeEndLabel={updatedScanScopeConfirmationLabels.updatedScopeEndLabel}
                zoneLabel={updatedScanScopeConfirmationLabels.zoneLabel}
                cameraLabel={updatedScanScopeConfirmationLabels.cameraLabel}
                riskPatternLabel={updatedScanScopeConfirmationLabels.riskPatternLabel}
                observationCount={updatedScanObservationCount}
                observationCountLoading={reviewScopeIncidentsLoading}
                onConfirm={confirmUpdatedSafetyScanScope}
                onAdjustScope={openScopeControls}
                confirmDisabled={!hasMinimumReportPackageScope}
              />
            ) : null}
            <DisclosureSection
            title={handoffParams.isUpdatedScan
              ? 'Adjust Updated Safety Scan Scope'
              : hasLinkedSafetyCases
                ? 'Advanced · Scan scope'
                : 'Scan Scope Controls'}
            subtitle={
              handoffParams.isUpdatedScan
                ? 'Refine the new-activity window, zone, camera, or risk pattern before building'
                : reportScopeHierarchy.reportingOnLine
            }
            defaultExpanded={!hasLinkedSafetyCases && !handoffParams.isUpdatedScan}
          >
            <ProofScopePanel
              reviewRecordPolicyFilter={reviewRecordPolicyFilter}
              onReviewRecordPolicyFilterChange={setReviewRecordPolicyFilter}
              reviewRecordPolicyOptions={reviewRecordPolicyOptions}
              reviewRecordCameraFilter={reviewRecordCameraFilter}
              onReviewRecordCameraFilterChange={handleReviewRecordCameraFilterChange}
              reviewRecordCameraOptions={reviewRecordCameraOptions}
              filteredSelectableReviewIncidents={filteredSelectableReviewIncidents}
              activeRepresentativeCase={activeRepresentativeCase}
              selectedReviewIncidentIds={selectedReviewIncidentIds}
              onSelectedReviewIncidentIdsChange={(ids) => {
                const next = ids.map((value) => String(value || '').trim()).filter(Boolean).slice(0, 1);
                setSelectedReviewIncidentIds(next);
              }}
              reviewScopeIncidentsLoading={reviewScopeIncidentsLoading}
              selectedIncidentScopeIsValid={selectedIncidentScopeDerivation.isValid}
              selectedIncidentScopeInvalidReason={selectedIncidentScopeDerivation.invalidReason}
              proofScopePolicyTypeDraft={proofScopePolicyTypeDraft}
              proofScopePolicySelectValue={proofScopePolicySelectValue}
              onProofScopePolicyTypeDraftChange={setProofScopePolicyTypeDraft}
              policyTypeOptions={scopedPolicyTypeOptionsForReport.length > 0
                ? scopedPolicyTypeOptionsForReport
                : _policyTypeOptions}
              reportFiltersScopedToCamera={reportFiltersScopedToCamera}
              proofScopeZoneDraft={proofScopeZoneDraft}
              onProofScopeZoneDraftChange={setProofScopeZoneDraft}
              zoneOptions={scopedZoneOptionsForReport.length > 0
                ? scopedZoneOptionsForReport
                : _zoneOptions}
              proofScopeFromDraft={proofScopeFromDraft}
              onProofScopeFromDraftChange={handleProofScopeFromDraftChange}
              proofScopeToDraft={proofScopeToDraft}
              onProofScopeToDraftChange={handleProofScopeToDraftChange}
              currentSelectionSummary={currentSelectionSummary}
              reportScopeHierarchy={reportScopeHierarchy}
              scopeCarriedFromReview={scopeCarriedFromReview}
              reportScopeDisplayLines={reportScopeDisplayLines}
              reportScopeResolutionHelper={reportScopeResolutionHelper}
              reviewRecordCameraSelectValue={reviewRecordCameraSelectValue}
              reviewRecordCameraOptionsResolved={reviewRecordCameraOptionsResolved}
              filtersExpanded={reportFiltersExpanded}
              onToggleFiltersExpanded={() => setReportFiltersExpanded((value) => !value)}
              onApplyProofScopeSelection={() => {
                setReportFiltersExpanded(false);
                applyProofScopeSelection();
              }}
              proofScopeDirty={proofScopeDirty}
              disableUpdateReport={
                !proofScopeDirty
                || (
                  safetyReportTypeMode === 'pattern'
                  && selectedReviewIncidentIds.length > 0
                  && !selectedIncidentScopeDerivation.isValid
                )
              }
              onResetFilters={() => {
                const nextSelectedIds = currentSelectedIncidentIdsKey
                  ? currentSelectedIncidentIdsKey.split(',').map((value) => String(value || '').trim()).filter(Boolean)
                  : [];
                setSelectedReviewIncidentIds(nextSelectedIds);
                setProofScopeZoneDraft(String(reportZoneId || canonicalProofZoneId || '').trim());
                setProofScopePolicyTypeDraft(String(reportPolicyType || canonicalProofIncidentType || '').trim());
                proofScopeWindowDraftEditedRef.current = false;
                setProofScopeFromDraft(appliedProofScopeFromLocal);
                setProofScopeToDraft(appliedProofScopeToLocal);
                setProofScopeCaseDraft(currentProofScopeCaseId);
              }}
              onOpenSelectedIncident={() => {
                if (selectedIncidentReviewHref) navigate(selectedIncidentReviewHref);
              }}
              selectedIncidentReviewHref={canOpenSelectedSafetyEvent ? selectedIncidentReviewHref : ''}
              onBackToReview={() => navigate(incidentReviewReturnHref)}
              safetyReportTypeMode={safetyReportTypeMode}
              onSafetyReportTypeModeChange={setSafetyReportTypeMode}
              hazardClassKey={hazardClassKey}
              onHazardClassKeyChange={setHazardClassKey}
            />
          </DisclosureSection>
          </>
        ) : null}

        {showLinkedDraftWorkspace && showSafetyReportContent && !showReadyDeliveryMode && !showAwaitingPackageMode && !showSingleBlockerMode ? (
          <DisclosureSection
            title="Evidence details"
            subtitle="Executive summary, representative evidence, and supporting narrative"
          >
            <ProofSummarySurface
              section="details"
              sectionRef={(node: HTMLDivElement | null) => {
                evidenceBundleSectionRef.current = node;
                reliabilityReviewSectionRef.current = node;
                caseSummarySectionRef.current = node;
              }}
              customerSummarySentence={customerSummarySentence}
              investigationAlignment={investigationAlignment}
              claimsRoiAlignment={claimsRoiAlignment}
              safetyControlReview={safetyControlReview}
              customerTopStatusPanel={customerTopStatusPanel}
              customerSecondaryStatusFacts={customerSecondaryStatusFacts}
              customerWhatHappened={customerWhatHappened}
              customerRiskPatternSummary={customerRiskPatternSummary}
              customerRiskPatternFacts={customerRiskPatternFacts}
              customerEvidencePoints={customerEvidencePoints}
              customerReviewStatusSummary={customerReviewStatusSummary}
              customerReviewChecklist={customerReviewChecklist}
              customerReviewOutcomeSummary={customerReviewOutcomeSummary}
              customerReviewTrendSummary={customerReviewTrendSummary}
              customerReviewBoundarySummary={customerReviewBoundarySummary}
              customerReviewOutcomeRows={customerReviewOutcomeRows as any}
              customerOwnershipSummary={customerOwnershipSummary}
              customerOwnershipDetailFacts={customerOwnershipDetailFacts}
              customerOwnershipRows={customerOwnershipRows as any}
              customerEscalationSummary={customerEscalationSummary}
              customerEscalationRows={customerEscalationRows as any}
              customerCorrectiveActionSummary={customerCorrectiveActionSummary}
              customerCorrectiveActionRows={customerCorrectiveActionRows as any}
              customerMeasurementSummary={customerMeasurementSummary}
              customerMeasurementBoundarySummary={customerMeasurementBoundarySummary}
              customerMeasurementRows={customerMeasurementRows as any}
              customerActionSnapshotSummary={customerActionSnapshotSummary}
              customerActionSnapshotRows={customerActionSnapshotRows as any}
              customerFollowupFacts={customerFollowupFacts}
              customerUnprovenSummary={customerUnprovenSummary}
              reportBuilding={isSafetyReportBuilding(proofPageState)}
              reportBuildingBannerText={reportBuildingBannerText}
              reportBuildingScopeSummary={reportBuildingScopeSummary}
              showReportContent={showSafetyReportContent}
              showPreviousReportBanner={showingPreviousBuiltReport}
              showIncompleteReportBanner={showingPreviousBuiltReport}
              onPrimaryIncidentEvidencePackageAction={primaryIncidentEvidencePackageAction}
              primaryIncidentEvidencePackageLabel={_incidentEvidencePackOutputPrimaryLabel}
              primaryIncidentEvidencePackageHelper={primaryIncidentEvidencePackageHelper}
              evidenceBundleCreating={evidenceBundleCreating}
              evidenceBundleDownloading={proofPackDownloading}
              canOperateOnProofPackage={canOperateOnProofPackage}
              reportReadinessActionLabel={reportReadinessActionLabel}
              reportReadinessActionHelper={reportReadinessActionHelper}
              reportReadinessActionDisabled={reportReadinessActionDisabled}
              onReportReadinessAction={() => { void finalizeSafetyScanAction(); }}
              onShowTechnicalDetails={() => setTechnicalDetailsExpanded(true)}
              executiveSummaryText={executiveSummaryText}
              findingText={findingText}
              evidenceReadinessLabel={evidenceReadinessLabel}
              evidenceReadinessDetail={evidenceReadinessDetail}
              showActionButtons={false}
              showAdvancedDetails={false}
            />
          </DisclosureSection>
        ) : null}

        {showLinkedDraftWorkspace && showSafetyReportContent && !showReadyDeliveryMode && !showAwaitingPackageMode && !showSingleBlockerMode ? (
          <>
            <DisclosureSection
              title="Measurement trust details"
              subtitle="Effectiveness claim boundaries and trust gates"
            >
              <ReportDetailSection
                title="Measurement Trust"
                summary={measurementTrustSummary}
                facts={measurementTrustFacts}
              >
                <Alert severity={isCategoryReport ? 'warning' : effectivenessClaimAllowed ? 'success' : 'warning'}>
                  {isCategoryReport
                    ? 'Category summaries can describe coverage and hotspots, but they do not support category-wide corrective-action effectiveness claims.'
                    : effectivenessClaimAllowed
                      ? (hasCanonicalClaimReadiness
                        ? 'Measurement trust supports a claim within the current canonical boundary.'
                        : 'Measurement trust supports a corrective-action effectiveness claim for this workflow.')
                      : (hasCanonicalClaimReadiness
                        ? 'Measurement trust does not currently support a claim within the current canonical boundary.'
                        : 'Measurement trust does not currently support a corrective-action effectiveness claim for this workflow.')}
                </Alert>
              </ReportDetailSection>
            </DisclosureSection>

            <DisclosureSection
              title="Claims & readiness"
              subtitle={isCategoryReport ? 'Claim boundaries and export readiness' : 'Safety Scan sign-off and claim boundaries'}
            >
              <ReportDetailSection
                title={isCategoryReport ? 'Claims & Export Readiness' : 'Claims & Readiness'}
                sectionRef={finalSignoffSectionRef}
                summary={isCategoryReport ? measurementTrustSummary : finalSignoffSummary}
                facts={claimsReadinessFacts}
              >
                {isCategoryReport ? (
                <Button
                  variant="outlined"
                  onClick={primaryIncidentEvidencePackageAction}
                  disabled={!reportPackageActionEnabled || proofPackGenerationInProgress || proofPackDownloading}
                >
                  {_incidentEvidencePackOutputPrimaryLabel}
                </Button>
                ) : null}
              </ReportDetailSection>
            </DisclosureSection>

            {isCategoryReport ? (
              <DisclosureSection
                title="Coverage details"
                subtitle="Coverage, response, and impact details for this category summary"
              >
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                  <ReportDetailSection
                    title="Category Coverage"
                    sectionRef={correctiveActionStatusSectionRef}
                    summary={customerRiskPatternSummary || customerReviewOutcomeSummary}
                    facts={categoryCoverageFacts}
                  >
                    <Alert severity="info">
                      This on-demand category report summarizes multiple workflow-backed recurring-risk reports inside one hazard class.
                    </Alert>
                  </ReportDetailSection>

                  <ReportDetailSection
                    title="Response Tracking Coverage"
                    sectionRef={impactMeasurementStatusSectionRef}
                    summary={customerCorrectiveActionSummary}
                    facts={categoryActionCoverageFacts}
                  />

                  <ReportDetailSection
                    title="Impact Coverage & Claim Boundary"
                    summary={customerMeasurementSummary || customerMeasurementBoundarySummary}
                    facts={categoryImpactFacts}
                  >
                    <Alert severity="warning">
                      This summary does not prove accident prevention, risk elimination, or category-wide corrective-action effectiveness unless explicitly supported by the included workflow evidence.
                    </Alert>
                  </ReportDetailSection>
                </Box>
              </DisclosureSection>
            ) : (
              <>
                <DisclosureSection
                  title="Response tracking details"
                  subtitle="Owners, response history, and validation evidence"
                >
                  <ReportDetailSection
                    title="Corrective Response Details"
                    sectionRef={correctiveActionStatusSectionRef}
                    summary={workflowPrimaryAction
                      ? `${customerCorrectiveActionSummary} Active workflow actions are attached to ${workflowId || 'the current workflow'}.`
                      : actionOwnerAssigned
                        ? customerCorrectiveActionSummary
                        : 'Corrective response cannot move forward until an owner is assigned in Review.'}
                    facts={_correctiveActionFacts}
                  >
                    <Stack spacing={1.25}>
                      {workflowSectionError ? <Alert severity="error">{workflowSectionError}</Alert> : null}
                      <DisclosureSection
                        title="Advanced · Control editor"
                        subtitle="Legacy inline Control editing. Prefer View / update Control on the Safety Case."
                      >
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <TextField
                          size="small"
                          label="New action title"
                          value={workflowActionTitleDraft}
                          onChange={(event) => setWorkflowActionTitleDraft(event.target.value)}
                          fullWidth
                        />
                        <TextField
                          size="small"
                          label="Assignee"
                          value={workflowActionAssigneeDraft}
                          onChange={(event) => setWorkflowActionAssigneeDraft(event.target.value)}
                          sx={{ minWidth: { sm: 180 } }}
                        />
                        <TextField
                          size="small"
                          label="Due date"
                          type="datetime-local"
                          value={workflowActionDueDateDraft}
                          onChange={(event) => setWorkflowActionDueDateDraft(event.target.value)}
                          InputLabelProps={{ shrink: true }}
                          sx={{ minWidth: { sm: 220 } }}
                        />
                        <Button
                          variant="contained"
                          onClick={() => void handleCreateWorkflowAction()}
                          disabled={!workflowId || workflowSectionBusy || !String(workflowActionTitleDraft || '').trim()}
                        >
                          Add action
                        </Button>
                      </Stack>
                      {workflowActionsState.active_actions.length === 0 && workflowActionsState.completed_actions.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No workflow corrective actions are recorded yet for this recurring safety-risk pattern.
                        </Typography>
                      ) : null}
                      {[...workflowActionsState.active_actions, ...workflowActionsState.completed_actions].map((action) => (
                        <Paper
                          key={String(action.action_id || `${action.title || 'workflow-action'}-${action.status || 'planned'}`)}
                          variant="outlined"
                          sx={{ p: 1.5 }}
                        >
                          <Stack spacing={1}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                              <Box>
                                <Typography variant="subtitle2">{action.title || 'Workflow corrective action'}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {[
                                    action.assignee ? `Assignee: ${action.assignee}` : 'Assignee pending',
                                    action.due_date ? `Due: ${formatTimestamp(action.due_date)}` : 'Due date not set',
                                  ].join(' · ')}
                                </Typography>
                              </Box>
                              <Chip
                                size="small"
                                label={_titleCase(String(action.status || 'planned').replace(/_/g, ' '))}
                                color={String(action.status || '').toLowerCase() === 'validated' ? 'success' : 'default'}
                              />
                            </Stack>
                            {action.description ? (
                              <Typography variant="body2" color="text.secondary">
                                {action.description}
                              </Typography>
                            ) : null}
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                              <TextField
                                size="small"
                                label="Add evidence note"
                                value={workflowEvidenceDrafts[String(action.action_id || '')] || ''}
                                onChange={(event) => setWorkflowEvidenceDrafts((current) => ({
                                  ...current,
                                  [String(action.action_id || '')]: event.target.value,
                                }))}
                                fullWidth
                              />
                              <Button
                                variant="outlined"
                                onClick={() => void handleAddWorkflowEvidenceNote(String(action.action_id || ''))}
                                disabled={workflowSectionBusy || !String(workflowEvidenceDrafts[String(action.action_id || '')] || '').trim()}
                              >
                                Add note
                              </Button>
                              {['planned', 'started'].includes(String(action.status || '').toLowerCase()) ? (
                                <Button
                                  variant="outlined"
                                  onClick={() => void handleUpdateWorkflowActionStatus(String(action.action_id || ''), 'completed')}
                                  disabled={workflowSectionBusy}
                                >
                                  Mark completed
                                </Button>
                              ) : null}
                              {String(action.status || '').toLowerCase() === 'completed' ? (
                                <Button
                                  variant="outlined"
                                  onClick={() => void handleUpdateWorkflowActionStatus(String(action.action_id || ''), 'validated')}
                                  disabled={workflowSectionBusy}
                                >
                                  Mark validated
                                </Button>
                              ) : null}
                            </Stack>
                          </Stack>
                        </Paper>
                      ))}
                      </DisclosureSection>
                    </Stack>
                  </ReportDetailSection>
                </DisclosureSection>

                <DisclosureSection
                  title="Impact details"
                  subtitle="Measurement, effectiveness, and ROI detail"
                >
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                    <ReportDetailSection
                      title="Impact Measurement Status"
                      sectionRef={impactMeasurementStatusSectionRef}
                      summary={workflowImpactSummaryLine || (
                        outcomeRecorded
                          ? customerMeasurementSummary
                          : 'Impact measurement is not recorded yet. Safety Scan sign-off should wait until follow-up measurement or an explicit waiver is recorded.'
                      )}
                      facts={_outcomeFacts}
                    >
                      <Stack spacing={1}>
                        {workflowImpactState?.status === 'waived' ? (
                          <Alert severity="warning">
                            {workflowImpactState.waiver_summary || 'Impact measurement was explicitly waived.'}
                          </Alert>
                        ) : workflowImpactState?.measurement_status === 'sufficient_data' ? (
                          <Alert severity={safetyScanProductModel.conclusion.showPercent ? 'success' : 'info'}>
                            {safetyScanProductModel.conclusion.explanation}
                            {safetyScanProductModel.conclusion.showPercent && safetyScanProductModel.conclusion.percentReduction != null
                              ? ` ${Math.abs(safetyScanProductModel.conclusion.percentReduction)}% ${safetyScanProductModel.conclusion.outcome === 'INCREASED' ? 'increase' : 'reduction'}.`
                              : ''}
                          </Alert>
                        ) : (
                          <Alert severity="info">
                            Not enough post-action workflow data is available yet to measure exposure reduction.
                          </Alert>
                        )}
                        {workflowImpactState?.status === 'waived' ? (
                          <Typography variant="body2" color="text.secondary">
                            {`Waived by ${workflowImpactState.waived_by || 'operator'} on ${formatTimestamp(workflowImpactState.waived_at) || 'the recorded date'}${workflowImpactState.waiver_reason ? ` · Reason: ${workflowImpactState.waiver_reason}` : ''}`}
                          </Typography>
                        ) : null}
                        {workflowImpactState?.measurement_status === 'sufficient_data' ? (
                          <Typography variant="body2" color="text.secondary">
                            {`Baseline window: ${formatTimestamp(workflowImpactState.baseline_window?.start) || 'unknown'} to ${formatTimestamp(workflowImpactState.baseline_window?.end) || 'unknown'} · Post-action window: ${formatTimestamp(workflowImpactState.post_action_window?.start) || 'unknown'} to ${formatTimestamp(workflowImpactState.post_action_window?.end) || 'unknown'}`}
                          </Typography>
                        ) : null}
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="outlined"
                            onClick={() => void refreshWorkflowSummary()}
                            disabled={!workflowId || workflowSectionBusy}
                          >
                            Refresh impact summary
                          </Button>
                        </Stack>
                      </Stack>
                    </ReportDetailSection>

                    <ReportDetailSection
                      title="Corrective Response Impact & ROI"
                      summary={impactRoiSummary}
                      facts={impactRoiFacts}
                    >
                      {workflowImpactState?.measurement_status === 'sufficient_data' ? (
                        <Alert severity={safetyScanProductModel.conclusion.outcome === 'INCREASED' ? 'warning' : 'info'}>
                          {`Baseline: ${safetyScanProductModel.followUp.beforeCount == null ? 'Not available' : safetyScanProductModel.followUp.beforeCount} events · Follow-up: ${safetyScanProductModel.followUp.afterCount == null ? 'Not available' : safetyScanProductModel.followUp.afterCount} events`}
                        </Alert>
                      ) : null}
                    </ReportDetailSection>
                  </Box>
                </DisclosureSection>
              </>
            )}

            <DisclosureSection
              title="Limitations"
              subtitle="Assumptions and claim boundaries"
            >
              <ReportDetailSection
                title="Safety Scan Limitations"
                bullets={reportLimitations}
              />
            </DisclosureSection>

            <DisclosureSection
              title="Recommended next steps"
              subtitle="Suggested follow-up actions"
            >
              <ReportDetailSection
                title="Recommended Next Steps"
                bullets={recommendedNextSteps}
              />
            </DisclosureSection>
          </>
        ) : null}
        {hasScope ? (
          <ProofTechnicalAppendix
            expanded={technicalDetailsExpanded}
            onExpandedChange={setTechnicalDetailsExpanded}
            reportProductName={customerReportProductName}
            hasAppliedIncidentSelection={hasAppliedIncidentSelection}
            appliedProofScopeSummary={appliedProofScopeSummary}
            includedIncidentCaseCount={includedIncidentCaseCount}
            caseTotalIncidents={caseTotalIncidents}
            visibleProofScopeFacts={visibleProofScopeFacts}
            proofReviewCorrelationLine={proofReviewCorrelationLine}
            packageExportSummary={packageExportSummary}
            onOpenTechnicalDetails={() => navigate(technicalDetailsPath, { state: { technicalDetails: technicalDetailsModel } })}
          />
        ) : null}
        </OptionalCollapsedAdvanced>
      </Stack>
    </Box>
  );
};

export default AcceptanceProofWorkspace;
